// src/context/QueryBuilderContext.ts
import { createContext } from "react";
var QueryBuilderContext = /* @__PURE__ */ createContext({});

// src/components/ActionElement.tsx
import { jsx } from "react/jsx-runtime";
var ActionElement = (props) => /* @__PURE__ */ jsx("button", { type: "button", "data-testid": props.testID, disabled: props.disabled && !props.disabledTranslation, className: props.className, title: props.disabledTranslation && props.disabled ? props.disabledTranslation.title : props.title, onClick: (e) => props.handleOnClick(e), children: props.disabledTranslation && props.disabled ? props.disabledTranslation.label : props.label });

// src/components/DragHandle.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
import { forwardRef } from "react";
var DragHandle = /* @__PURE__ */ forwardRef((props, dragRef) => /* @__PURE__ */ jsx2("span", { "data-testid": props.testID, ref: dragRef, className: props.className, title: props.title, children: props.label }));

// src/defaults.ts
var defaultPlaceholderName = "~";
var defaultPlaceholderLabel = "------";
var defaultPlaceholderFieldName = defaultPlaceholderName;
var defaultPlaceholderFieldLabel = defaultPlaceholderLabel;
var defaultPlaceholderFieldGroupLabel = defaultPlaceholderLabel;
var defaultPlaceholderOperatorName = defaultPlaceholderName;
var defaultPlaceholderOperatorLabel = defaultPlaceholderLabel;
var defaultPlaceholderOperatorGroupLabel = defaultPlaceholderLabel;
var defaultJoinChar = ",";
var defaultTranslations = { fields: { title: "Fields", placeholderName: defaultPlaceholderFieldName, placeholderLabel: defaultPlaceholderFieldLabel, placeholderGroupLabel: defaultPlaceholderFieldGroupLabel }, operators: { title: "Operators", placeholderName: defaultPlaceholderOperatorName, placeholderLabel: defaultPlaceholderOperatorLabel, placeholderGroupLabel: defaultPlaceholderOperatorGroupLabel }, value: { title: "Value" }, removeRule: { label: "\u2A2F", title: "Remove rule" }, removeGroup: { label: "\u2A2F", title: "Remove group" }, addRule: { label: "+ Rule", title: "Add rule" }, addGroup: { label: "+ Group", title: "Add group" }, combinators: { title: "Combinators" }, notToggle: { label: "Not", title: "Invert this group" }, cloneRule: { label: "\u29C9", title: "Clone rule" }, cloneRuleGroup: { label: "\u29C9", title: "Clone group" }, shiftActionUp: { label: "\u02C4", title: "Shift up" }, shiftActionDown: { label: "\u02C5", title: "Shift down" }, dragHandle: { label: "\u205E\u205E", title: "Drag handle" }, lockRule: { label: "\u{1F513}", title: "Lock rule" }, lockGroup: { label: "\u{1F513}", title: "Lock group" }, lockRuleDisabled: { label: "\u{1F512}", title: "Unlock rule" }, lockGroupDisabled: { label: "\u{1F512}", title: "Unlock group" }, valueSourceSelector: { title: "Value source" } };
var defaultOperators = [{ name: "=", value: "=", label: "=" }, { name: "!=", value: "!=", label: "!=" }, { name: "<", value: "<", label: "<" }, { name: ">", value: ">", label: ">" }, { name: "<=", value: "<=", label: "<=" }, { name: ">=", value: ">=", label: ">=" }, { name: "contains", value: "contains", label: "contains" }, { name: "beginsWith", value: "beginsWith", label: "begins with" }, { name: "endsWith", value: "endsWith", label: "ends with" }, { name: "doesNotContain", value: "doesNotContain", label: "does not contain" }, { name: "doesNotBeginWith", value: "doesNotBeginWith", label: "does not begin with" }, { name: "doesNotEndWith", value: "doesNotEndWith", label: "does not end with" }, { name: "null", value: "null", label: "is null" }, { name: "notNull", value: "notNull", label: "is not null" }, { name: "in", value: "in", label: "in" }, { name: "notIn", value: "notIn", label: "not in" }, { name: "between", value: "between", label: "between" }, { name: "notBetween", value: "notBetween", label: "not between" }];
var defaultOperatorNegationMap = { "=": "!=", "!=": "=", "<": ">=", "<=": ">", ">": "<=", ">=": "<", beginsWith: "doesNotBeginWith", doesNotBeginWith: "beginsWith", endsWith: "doesNotEndWith", doesNotEndWith: "endsWith", contains: "doesNotContain", doesNotContain: "contains", between: "notBetween", notBetween: "between", in: "notIn", notIn: "in", notNull: "null", null: "notNull" };
var defaultCombinators = [{ name: "and", value: "and", label: "AND" }, { name: "or", value: "or", label: "OR" }];
var defaultCombinatorsExtended = [...defaultCombinators, { name: "xor", value: "xor", label: "XOR" }];
var standardClassnames = { queryBuilder: "queryBuilder", ruleGroup: "ruleGroup", header: "ruleGroup-header", body: "ruleGroup-body", combinators: "ruleGroup-combinators", addRule: "ruleGroup-addRule", addGroup: "ruleGroup-addGroup", cloneRule: "rule-cloneRule", cloneGroup: "ruleGroup-cloneGroup", removeGroup: "ruleGroup-remove", notToggle: "ruleGroup-notToggle", rule: "rule", fields: "rule-fields", operators: "rule-operators", value: "rule-value", removeRule: "rule-remove", betweenRules: "betweenRules", valid: "queryBuilder-valid", invalid: "queryBuilder-invalid", shiftActions: "shiftActions", dndDragging: "dndDragging", dndOver: "dndOver", dndCopy: "dndCopy", dndGroup: "dndGroup", dragHandle: "queryBuilder-dragHandle", disabled: "queryBuilder-disabled", lockRule: "rule-lock", lockGroup: "ruleGroup-lock", valueSource: "rule-valueSource", valueListItem: "rule-value-list-item", branches: "queryBuilder-branches" };
var defaultControlClassnames = { queryBuilder: "", ruleGroup: "", header: "", body: "", combinators: "", addRule: "", addGroup: "", cloneRule: "", cloneGroup: "", removeGroup: "", notToggle: "", rule: "", fields: "", operators: "", value: "", removeRule: "", shiftActions: "", dragHandle: "", lockRule: "", lockGroup: "", valueSource: "", actionElement: "", valueSelector: "", betweenRules: "", valid: "", invalid: "", dndDragging: "", dndOver: "", dndGroup: "", dndCopy: "", disabled: "", valueListItem: "", branches: "" };
var groupInvalidReasons = { empty: "empty", invalidCombinator: "invalid combinator", invalidIndependentCombinators: "invalid independent combinators" };
var TestID = { rule: "rule", ruleGroup: "rule-group", inlineCombinator: "inline-combinator", addGroup: "add-group", removeGroup: "remove-group", cloneGroup: "clone-group", cloneRule: "clone-rule", addRule: "add-rule", removeRule: "remove-rule", combinators: "combinators", fields: "fields", operators: "operators", valueEditor: "value-editor", notToggle: "not-toggle", shiftActions: "shift-actions", dragHandle: "drag-handle", lockRule: "lock-rule", lockGroup: "lock-group", valueSourceSelector: "value-source-selector" };
var LogType = { parentPathDisabled: "action aborted: parent path disabled", pathDisabled: "action aborted: path is disabled", queryUpdate: "query updated", onAddRuleFalse: "onAddRule callback returned false", onAddGroupFalse: "onAddGroup callback returned false", onGroupRuleFalse: "onGroupRule callback returned false", onGroupGroupFalse: "onGroupGroup callback returned false", onMoveRuleFalse: "onMoveRule callback returned false", onMoveGroupFalse: "onMoveGroup callback returned false", onRemoveFalse: "onRemove callback returned false", add: "rule or group added", remove: "rule or group removed", update: "rule or group updated", move: "rule or group moved", group: "rule or group grouped with another" };
var rootPath = [];

// src/components/InlineCombinator.tsx
import { jsx as jsx3 } from "react/jsx-runtime";

// src/utils/clsx.ts
function toVal(mix) {
  let k;
  let y;
  let str = "";
  if (typeof mix === "string" || typeof mix === "number") {
    str += mix;
  } else if (typeof mix === "object") {
    if (Array.isArray(mix)) {
      const len = mix.length;
      for (k = 0; k < len; k++) {
        if (mix[k] && (y = toVal(mix[k]))) {
          str && (str += " ");
          str += y;
        }
      }
    } else {
      for (y in mix) {
        if (mix[y]) {
          str && (str += " ");
          str += y;
        }
      }
    }
  }
  return str;
}
function clsx() {
  let i = 0;
  let tmp;
  let x;
  let str = "";
  const len = arguments.length;
  for (; i < len; i++) {
    if ((tmp = i < 0 || arguments.length <= i ? void 0 : arguments[i]) && (x = toVal(tmp))) {
      str && (str += " ");
      str += x;
    }
  }
  return str;
}
var clsx_default = clsx;

// src/components/InlineCombinator.tsx
var InlineCombinator = (allProps) => {
  const { component: CombinatorSelectorComponent, ...props } = allProps;
  const className = clsx(props.schema.suppressStandardClassnames || standardClassnames.betweenRules, props.schema.classNames.betweenRules);
  return /* @__PURE__ */ jsx3("div", { className, "data-testid": TestID.inlineCombinator, children: /* @__PURE__ */ jsx3(CombinatorSelectorComponent, { ...props, testID: TestID.combinators }) });
};

// src/components/NotToggle.tsx
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
var NotToggle = (props) => /* @__PURE__ */ jsxs("label", { "data-testid": props.testID, className: props.className, title: props.title, children: [/* @__PURE__ */ jsx4("input", { type: "checkbox", onChange: (e) => props.handleOnChange(e.target.checked), checked: !!props.checked, disabled: props.disabled }), props.label] });

// src/messages.ts
var messages = { errorInvalidIndependentCombinatorsProp: "QueryBuilder was rendered with a truthy independentCombinators prop. This prop is deprecated and unnecessary. Furthermore, the initial query/defaultQuery prop was of type RuleGroupType instead of type RuleGroupIC. More info: https://react-querybuilder.js.org/docs/components/querybuilder#independent-combinators", errorUnnecessaryIndependentCombinatorsProp: "QueryBuilder was rendered with the deprecated and unnecessary independentCombinators prop. To use independent combinators, make sure the query/defaultQuery prop is of type RuleGroupIC when the component mounts. More info: https://react-querybuilder.js.org/docs/components/querybuilder#independent-combinators", errorDeprecatedRuleGroupProps: "A custom RuleGroup component has rendered a standard RuleGroup component with deprecated props. The combinator, not, and rules props should not be used. Instead, the full group object should be passed as the ruleGroup prop.", errorDeprecatedRuleProps: "A custom RuleGroup component has rendered a standard Rule component with deprecated props. The field, operator, value, and valueSource props should not be used. Instead, the full rule object should be passed as the rule prop.", errorBothQueryDefaultQuery: "QueryBuilder was rendered with both query and defaultQuery props. QueryBuilder must be either controlled or uncontrolled (specify either the query prop, or the defaultQuery prop, but not both). Decide between using a controlled or uncontrolled query builder and remove one of these props. More info: https://reactjs.org/link/controlled-components", errorUncontrolledToControlled: "QueryBuilder is changing from an uncontrolled component to be controlled. This is likely caused by the query changing from undefined to a defined value, which should not happen. Decide between using a controlled or uncontrolled query builder for the lifetime of the component. More info: https://reactjs.org/link/controlled-components", errorControlledToUncontrolled: "QueryBuilder is changing from a controlled component to be uncontrolled. This is likely caused by the query changing from defined to undefined, which should not happen. Decide between using a controlled or uncontrolled query builder for the lifetime of the component. More info: https://reactjs.org/link/controlled-components", errorEnabledDndWithoutReactDnD: "QueryBuilder was rendered with the enableDragAndDrop prop set to true, but either react-dnd was not detected or one of react-dnd-html5-backend or react-dnd-touch-backend was not detected. To enable drag-and-drop functionality, install react-dnd and one of the backend packages and wrap QueryBuilder in QueryBuilderDnD from @react-querybuilder/dnd." };

// src/redux/QueryBuilderStateContext.ts
import * as React from "react";
var QueryBuilderStateContext = /* @__PURE__ */ React.createContext(null);

// src/redux/_internal.ts
import { createDispatchHook, createStoreHook } from "react-redux";

// src/redux/queriesSlice.ts
import { createSlice } from "@reduxjs/toolkit";
var initialState = {};
var queriesSlice = createSlice({ name: "queries", initialState, reducers: { setQueryState: (state, _ref) => {
  let { payload: { qbId, query } } = _ref;
  state[qbId] = query;
} }, selectors: { getQuerySelectorById: (state, qbId) => state[qbId] } });

// src/redux/warningsSlice.ts
import { createSlice as createSlice2 } from "@reduxjs/toolkit";
var initialState2 = { [messages.errorInvalidIndependentCombinatorsProp]: false, [messages.errorUnnecessaryIndependentCombinatorsProp]: false, [messages.errorDeprecatedRuleGroupProps]: false, [messages.errorDeprecatedRuleProps]: false, [messages.errorBothQueryDefaultQuery]: false, [messages.errorUncontrolledToControlled]: false, [messages.errorControlledToUncontrolled]: false, [messages.errorEnabledDndWithoutReactDnD]: false };
var warningsSlice = createSlice2({ name: "warnings", initialState: initialState2, reducers: { rqbWarn: (state, _ref) => {
  let { payload } = _ref;
  if (!state[payload]) {
    console.error(payload);
    state[payload] = true;
  }
} } });

// src/redux/_internal.ts
var _RQB_INTERNAL_dispatchThunk = (_ref) => {
  let { payload, onQueryChange } = _ref;
  return (dispatch) => {
    dispatch(queriesSlice.actions.setQueryState(payload));
    if (typeof onQueryChange === "function") {
      onQueryChange(
        payload.query
        /* ??? */
      );
    }
  };
};
var useRQB_INTERNAL_QueryBuilderDispatch = createDispatchHook(QueryBuilderStateContext);
var useRQB_INTERNAL_QueryBuilderStore = createStoreHook(QueryBuilderStateContext);
var { rqbWarn: _SYNC_rqbWarn } = warningsSlice.actions;
var rqbWarn = (msg) => (dispatch) => {
  setTimeout(() => dispatch(_SYNC_rqbWarn(msg)));
};
var preloadedState = { queries: queriesSlice.getInitialState(), warnings: warningsSlice.getInitialState() };
var storeCommon = { reducer: { queries: queriesSlice.reducer, warnings: warningsSlice.reducer }, preloadedState, middleware: (getDefaultMiddleware) => getDefaultMiddleware({
  // Ignore non-serializable values in setQueryState actions and rule `value`s
  // https://redux-toolkit.js.org/usage/usage-guide#working-with-non-serializable-data
  serializableCheck: { ignoredActions: [queriesSlice.actions.setQueryState.type], ignoredPaths: [/^queries\b.*\.rules\.\d+\.value$/] }
}) };

// src/hooks/useDeprecatedProps.ts
function useDeprecatedProps(type, logWarning, otherParams) {
  const dispatch = useRQB_INTERNAL_QueryBuilderDispatch();
  if (process.env.NODE_ENV !== "production" && logWarning) {
    if (type === "independentCombinators") {
      if (otherParams === "invalid") {
        dispatch(rqbWarn(messages.errorInvalidIndependentCombinatorsProp));
      }
      if (otherParams === "unnecessary") {
        dispatch(rqbWarn(messages.errorUnnecessaryIndependentCombinatorsProp));
      }
    }
    if (type === "rule") {
      dispatch(rqbWarn(messages.errorDeprecatedRuleProps));
    }
    if (type === "ruleGroup") {
      dispatch(rqbWarn(messages.errorDeprecatedRuleGroupProps));
    }
  }
}

// src/hooks/useReactDndWarning.ts
var didWarnEnabledDndWithoutReactDnD = false;
var useReactDndWarning = (enableDragAndDrop, dndRefs) => {
  if (process.env.NODE_ENV !== "production" && !didWarnEnabledDndWithoutReactDnD && enableDragAndDrop && !dndRefs) {
    console.error(messages.errorEnabledDndWithoutReactDnD);
    didWarnEnabledDndWithoutReactDnD = true;
  }
};

// ../../utils/react-compiler/react-compiler-runtime.ts
import * as React2 from "react";
var $empty = Symbol.for("react.memo_cache_sentinel");
function c(size) {
  return React2.useMemo(
    () => {
      const $ = Array.from({ length: size });
      for (let ii = 0; ii < size; ii++) {
        $[ii] = $empty;
      }
      $[$empty] = true;
      return $;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
}

// src/hooks/useStopEventPropagation.ts
import { useCallback } from "react";
var useStopEventPropagation = (method) => {
  const $ = c(2);
  let t0;
  if ($[0] !== method) {
    t0 = (event, context) => {
      event?.preventDefault();
      event?.stopPropagation();
      method(event, context);
    };
    $[0] = method;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};

// src/utils/arrayUtils.ts
var splitBy = function(str) {
  let splitChar = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : defaultJoinChar;
  return typeof str === "string" ? str.split(`\\${splitChar}`).map((c2) => c2.split(splitChar)).reduce((prev, curr, idx) => {
    if (idx === 0) {
      return curr;
    }
    return [...prev.slice(0, -1), `${prev.at(-1)}${splitChar}${curr[0]}`, ...curr.slice(1)];
  }, []) : [];
};
var joinWith = function(strArr) {
  let joinChar = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : defaultJoinChar;
  return strArr.map((str) => `${str ?? ""}`.replaceAll(joinChar[0], `\\${joinChar[0]}`)).join(joinChar);
};
var trimIfString = (val) => typeof val === "string" ? val.trim() : val;
var toArray = function(v) {
  let { retainEmptyStrings } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  return Array.isArray(v) ? v.map((v2) => trimIfString(v2)) : typeof v === "string" ? splitBy(v, defaultJoinChar).filter(retainEmptyStrings ? () => true : (s) => !/^\s*$/.test(s)).map((s) => s.trim()) : typeof v === "number" ? [v] : [];
};
var nullFreeArray = (arr) => arr.every((el) => el === false || (el ?? false) !== false);

// src/utils/misc.ts
import { numericRegex as numericQuantityRegex } from "numeric-quantity";
var numericRegex = new RegExp(numericQuantityRegex.source.replace(/^\^/, String.raw`^\s*`).replace(/\$$/, String.raw`\s*$`));
var isPojo = (obj) => obj === null || typeof obj !== "object" ? false : Object.getPrototypeOf(obj) === Object.prototype;
var nullOrUndefinedOrEmpty = (value) => value === null || value === void 0 || value === "";

// src/utils/isRuleGroup.ts
var isRuleGroup = (rg) => isPojo(rg) && Array.isArray(rg.rules);
var isRuleGroupType = (rg) => isRuleGroup(rg) && typeof rg.combinator === "string";
var isRuleGroupTypeIC = (rg) => isRuleGroup(rg) && rg.combinator === void 0;

// src/utils/convertQuery.ts
import { produce } from "immer";
var combinatorLevels = ["or", "xor", "and"];
var isSameString = (a, b) => typeof a === "string" && a.toLowerCase() === b;
var generateRuleGroupICWithConsistentCombinators = function(rg) {
  let baseCombinatorLevel = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 0;
  const baseCombinator = combinatorLevels[baseCombinatorLevel];
  if (!rg.rules.includes(baseCombinator)) {
    return baseCombinatorLevel < combinatorLevels.length - 2 ? generateRuleGroupICWithConsistentCombinators(rg, baseCombinatorLevel + 1) : rg;
  }
  return produce(rg, (draft) => {
    let cursor = 0;
    while (cursor < draft.rules.length - 2) {
      if (isSameString(draft.rules[cursor + 1], baseCombinator)) {
        cursor += 2;
        continue;
      }
      const nextBaseCombinatorIndex = draft.rules.findIndex((r, i) => i > cursor && typeof r === "string" && r.toLowerCase() === baseCombinator);
      if (nextBaseCombinatorIndex === -1) {
        draft.rules.splice(cursor, draft.rules.length, generateRuleGroupICWithConsistentCombinators(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          { rules: draft.rules.slice(cursor) },
          baseCombinatorLevel + 1
        ));
        break;
      } else {
        draft.rules.splice(cursor, nextBaseCombinatorIndex - cursor, generateRuleGroupICWithConsistentCombinators(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          { rules: draft.rules.slice(cursor, nextBaseCombinatorIndex) },
          baseCombinatorLevel + 1
        ));
      }
    }
  });
};
var convertFromIC = (rg) => {
  if (isRuleGroupType(rg)) {
    return rg;
  }
  const processedRG = generateRuleGroupICWithConsistentCombinators(rg);
  const rulesAsMixedList = processedRG.rules.map((r) => typeof r === "string" || !isRuleGroup(r) ? r : convertFromIC(r));
  const combinator = rulesAsMixedList.length < 2 ? "and" : rulesAsMixedList[1];
  const rules = rulesAsMixedList.filter((r) => typeof r !== "string");
  return { ...processedRG, combinator, rules };
};
var convertToIC = (rg) => {
  if (isRuleGroupTypeIC(rg)) {
    return rg;
  }
  const { combinator, ...queryWithoutCombinator } = rg;
  const rules = [];
  const { length } = rg.rules;
  for (const [idx, r] of rg.rules.entries()) {
    if (isRuleGroup(r)) {
      rules.push(convertToIC(r));
    } else {
      rules.push(r);
    }
    if (combinator && idx < length - 1) {
      rules.push(combinator);
    }
  }
  return { ...queryWithoutCombinator, rules };
};
function convertQuery(query) {
  return isRuleGroupTypeIC(query) ? convertFromIC(query) : convertToIC(query);
}

// src/utils/defaultValidator.ts
var defaultValidator = (query) => {
  const result = {};
  const validateRule = (_rule) => {
  };
  const validateGroup = (rg) => {
    const reasons = [];
    if (rg.rules.length === 0) {
      reasons.push(groupInvalidReasons.empty);
    } else if (!isRuleGroupType(rg)) {
      let invalidICs = false;
      for (let i = 0; i < rg.rules.length && !invalidICs; i++) {
        if (i % 2 === 0 && typeof rg.rules[i] === "string" || i % 2 === 1 && typeof rg.rules[i] !== "string" || i % 2 === 1 && typeof rg.rules[i] === "string" && !defaultCombinators.map((c2) => c2.name).includes(rg.rules[i])) {
          invalidICs = true;
        }
      }
      if (invalidICs) {
        reasons.push(groupInvalidReasons.invalidIndependentCombinators);
      }
    }
    if (isRuleGroupType(rg) && !defaultCombinators.map((c2) => c2.name).includes(rg.combinator) && rg.rules.length > 1) {
      reasons.push(groupInvalidReasons.invalidCombinator);
    }
    if (rg.id) {
      result[rg.id] = reasons.length > 0 ? { valid: false, reasons } : true;
    }
    for (const r of rg.rules) {
      if (typeof r === "string") {
      } else if (isRuleGroup(r)) {
        validateGroup(r);
      } else {
        validateRule(r);
      }
    }
  };
  validateGroup(query);
  return result;
};

// src/utils/optGroupUtils.ts
import { produce as produce2 } from "immer";
var isOptionWithName = (opt) => isPojo(opt) && "name" in opt && typeof opt.name === "string";
var isOptionWithValue = (opt) => isPojo(opt) && "value" in opt && typeof opt.value === "string";
function toFullOption(opt, baseProperties) {
  const recipe = produce2((draft) => {
    const idObj = {};
    let needsUpdating = !!baseProperties;
    if (isOptionWithName(draft) && !isOptionWithValue(draft)) {
      idObj.value = draft.name;
      needsUpdating = true;
    } else if (!isOptionWithName(draft) && isOptionWithValue(draft)) {
      idObj.name = draft.value;
      needsUpdating = true;
    }
    if (needsUpdating) {
      return Object.assign({}, baseProperties, draft, idObj);
    }
  });
  return recipe(opt);
}
function toFullOptionList(optList, baseProperties) {
  if (!Array.isArray(optList)) {
    return [];
  }
  const recipe = produce2((draft) => {
    if (isFlexibleOptionGroupArray(draft)) {
      for (const optGroup of draft) {
        for (const [idx, opt] of optGroup.options.entries()) optGroup.options[idx] = toFullOption(opt, baseProperties);
      }
    } else {
      for (const [idx, opt] of draft.entries()) draft[idx] = toFullOption(opt, baseProperties);
    }
  });
  return recipe(optList);
}
function toFullOptionMap(optMap, baseProperties) {
  return Object.fromEntries(Object.entries(optMap).map((_ref) => {
    let [k, v] = _ref;
    return [k, toFullOption(v, baseProperties)];
  }));
}
var uniqByName = (originalArray) => uniqByIdentifier(originalArray);
var uniqByIdentifier = (originalArray) => {
  const names = /* @__PURE__ */ new Set();
  const newArray = [];
  for (const el of originalArray) {
    if (!names.has(el.value ?? el.name)) {
      names.add(el.value ?? el.name);
      newArray.push(el);
    }
  }
  return originalArray.length === newArray.length ? originalArray : newArray;
};
var isOptionGroupArray = (arr) => Array.isArray(arr) && arr.length > 0 && isPojo(arr[0]) && "options" in arr[0] && Array.isArray(arr[0].options);
var isFlexibleOptionArray = (arr) => {
  let isFOA = false;
  if (Array.isArray(arr)) {
    for (const o of arr) {
      if (isOptionWithName(o) || isOptionWithValue(o)) {
        isFOA = true;
      } else {
        return false;
      }
    }
  }
  return isFOA;
};
var isFullOptionArray = (arr) => {
  let isFOA = false;
  if (Array.isArray(arr)) {
    for (const o of arr) {
      if (isOptionWithName(o) && isOptionWithValue(o)) {
        isFOA = true;
      } else {
        return false;
      }
    }
  }
  return isFOA;
};
var isFlexibleOptionGroupArray = function(arr) {
  let { allowEmpty = false } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  let isFOGA = false;
  if (Array.isArray(arr)) {
    for (const og of arr) {
      if (isPojo(og) && "options" in og && (isFlexibleOptionArray(og.options) || allowEmpty && Array.isArray(og.options) && og.options.length === 0)) {
        isFOGA = true;
      } else {
        return false;
      }
    }
  }
  return isFOGA;
};
var isFullOptionGroupArray = function(arr) {
  let { allowEmpty = false } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  let isFOGA = false;
  if (Array.isArray(arr)) {
    for (const og of arr) {
      if (isPojo(og) && "options" in og && (isFullOptionArray(og.options) || allowEmpty && Array.isArray(og.options) && og.options.length === 0)) {
        isFOGA = true;
      } else {
        return false;
      }
    }
  }
  return isFOGA;
};
var getOption = (arr, name) => (isFlexibleOptionGroupArray(arr, { allowEmpty: true }) ? arr.flatMap((og) => og.options) : arr).find((op) => op.value === name || op.name === name);
var getFirstOption = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) {
    return null;
  } else if (isFlexibleOptionGroupArray(arr, { allowEmpty: true })) {
    for (const og of arr) {
      if (og.options.length > 0) {
        return og.options[0].value ?? og.options[0].name;
      }
    }
    return null;
  }
  return arr[0].value ?? arr[0].name;
};
var toFlatOptionArray = (arr) => uniqByIdentifier(isOptionGroupArray(arr) ? arr.flatMap((og) => og.options) : arr);
var uniqOptGroups = (originalArray) => {
  const labels = /* @__PURE__ */ new Set();
  const names = /* @__PURE__ */ new Set();
  const newArray = [];
  for (const el of originalArray) {
    if (!labels.has(el.label)) {
      labels.add(el.label);
      const optionsForThisGroup = [];
      for (const opt of el.options) {
        if (!names.has(opt.value ?? opt.name)) {
          names.add(opt.value ?? opt.name);
          optionsForThisGroup.push(toFullOption(opt));
        }
      }
      newArray.push({ ...el, options: optionsForThisGroup });
    }
  }
  return newArray;
};
var uniqOptList = (originalArray) => {
  if (isFlexibleOptionGroupArray(originalArray)) {
    return uniqOptGroups(originalArray);
  }
  return uniqByIdentifier(originalArray.map((o) => toFullOption(o)));
};

// src/utils/filterFieldsByComparator.ts
var filterByComparator = (field, operator, fieldToCompare) => {
  const fullField = toFullOption(field);
  const fullFieldToCompare = toFullOption(fieldToCompare);
  if (fullField.value === fullFieldToCompare.value) {
    return false;
  }
  if (typeof fullField.comparator === "string") {
    return fullField[fullField.comparator] === fullFieldToCompare[fullField.comparator];
  }
  return fullField.comparator?.(fullFieldToCompare, operator) ?? /* istanbul ignore next */
  false;
};
var filterFieldsByComparator = (field, fields, operator) => {
  if (!field.comparator) {
    const filterOutSameField = (f) => (f.value ?? /* istanbul ignore next */
    f.name) !== (field.value ?? /* istanbul ignore next */
    field.name);
    if (isFlexibleOptionGroupArray(fields)) {
      return fields.map((og) => ({ ...og, options: og.options.filter((v) => filterOutSameField(v)) }));
    }
    return fields.filter((v) => filterOutSameField(v));
  }
  if (isFlexibleOptionGroupArray(fields)) {
    return fields.map((og) => ({ ...og, options: og.options.filter((f) => filterByComparator(field, operator, f)) })).filter((og) => og.options.length > 0);
  }
  return fields.filter((f) => filterByComparator(field, operator, f));
};

// src/utils/parseNumber.ts
import { numericQuantity } from "numeric-quantity";
var parseNumber = function(val) {
  let { parseNumbers } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  if (!parseNumbers || typeof val === "bigint" || typeof val === "number") {
    return val;
  }
  if (parseNumbers === "native") {
    return parseFloat(val);
  }
  const valAsNum = (
    // TODO: Should these options be configurable?
    numericQuantity(val, { allowTrailingInvalid: parseNumbers === "enhanced", romanNumerals: false, round: false })
  );
  return isNaN(valAsNum) ? val : valAsNum;
};

// src/utils/getParseNumberMethod.ts
var getParseNumberMethod = (_ref) => {
  let { parseNumbers, inputType } = _ref;
  if (typeof parseNumbers === "string") {
    const [method, level] = parseNumbers.split("-");
    if (level === "limited") {
      return inputType === "number" ? method : false;
    }
    return method;
  }
  return parseNumbers ? "strict" : false;
};

// src/utils/formatQuery/utils.ts
var mapSQLOperator = (rqbOperator) => {
  switch (rqbOperator.toLowerCase()) {
    case "null":
      return "is null";
    case "notnull":
      return "is not null";
    case "notin":
      return "not in";
    case "notbetween":
      return "not between";
    case "contains":
    case "beginswith":
    case "endswith":
      return "like";
    case "doesnotcontain":
    case "doesnotbeginwith":
    case "doesnotendwith":
      return "not like";
    default:
      return rqbOperator;
  }
};
var mongoOperators = {
  "=": "$eq",
  "!=": "$ne",
  "<": "$lt",
  "<=": "$lte",
  ">": "$gt",
  ">=": "$gte",
  in: "$in",
  notin: "$nin",
  notIn: "$nin"
  // only here for backwards compatibility
};
var celCombinatorMap = { and: "&&", or: "||" };
var jsonLogicAdditionalOperators = { startsWith: (a, b) => typeof a === "string" && a.startsWith(b), endsWith: (a, b) => typeof a === "string" && a.endsWith(b) };
var numerifyValues = (rg, options) => ({
  ...rg,
  // @ts-expect-error TS doesn't keep track of odd/even indexes here
  rules: rg.rules.map((r) => {
    if (typeof r === "string") {
      return r;
    }
    if (isRuleGroup(r)) {
      return numerifyValues(r, options);
    }
    const fieldData = getOption(options.fields, r.field);
    const parseNumbers = getParseNumberMethod({ parseNumbers: options.parseNumbers, inputType: fieldData?.inputType });
    if (Array.isArray(r.value)) {
      return { ...r, value: r.value.map((v) => parseNumber(v, { parseNumbers })) };
    }
    const valAsArray = toArray(r.value, { retainEmptyStrings: true }).map((v) => parseNumber(v, { parseNumbers }));
    if (valAsArray.every((v) => typeof v === "number")) {
      if (valAsArray.length > 1) {
        return { ...r, value: valAsArray };
      } else if (valAsArray.length === 1) {
        return { ...r, value: valAsArray[0] };
      }
    }
    return r;
  })
});
var isValidValue = (value) => typeof value === "string" && value.length > 0 || typeof value === "number" && !isNaN(value) || typeof value !== "string" && typeof value !== "number";
var shouldRenderAsNumber = (value, parseNumbers) => !!parseNumbers && (typeof value === "number" || typeof value === "bigint" || typeof value === "string" && numericRegex.test(value));
var isValueProcessorLegacy = (valueProcessor) => valueProcessor.length >= 3;
var getQuoteFieldNamesWithArray = function() {
  let quoteFieldNamesWith = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : ["", ""];
  return Array.isArray(quoteFieldNamesWith) ? quoteFieldNamesWith : typeof quoteFieldNamesWith === "string" ? [quoteFieldNamesWith, quoteFieldNamesWith] : quoteFieldNamesWith ?? ["", ""];
};
var getQuotedFieldName = (fieldName, _ref) => {
  let { quoteFieldNamesWith, fieldIdentifierSeparator } = _ref;
  const [qPre, qPost] = getQuoteFieldNamesWithArray(quoteFieldNamesWith);
  return typeof fieldIdentifierSeparator === "string" && fieldIdentifierSeparator.length > 0 ? joinWith(splitBy(fieldName, fieldIdentifierSeparator).map((part) => `${qPre}${part}${qPost}`), fieldIdentifierSeparator) : `${qPre}${fieldName}${qPost}`;
};

// src/utils/formatQuery/defaultRuleProcessorCEL.ts
var shouldNegate = (op) => op.startsWith("not") || op.startsWith("doesnot");
var escapeDoubleQuotes = (v, escapeQuotes) => typeof v !== "string" || !escapeQuotes ? v : v.replaceAll(`"`, `\\"`);
var defaultRuleProcessorCEL = function(_ref) {
  let { field, operator, value, valueSource } = _ref;
  let { escapeQuotes, parseNumbers, preserveValueOrder } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  const valueIsField = valueSource === "field";
  const operatorTL = (operator === "=" ? "==" : operator).toLowerCase();
  const useBareValue = typeof value === "number" || typeof value === "boolean" || typeof value === "bigint" || shouldRenderAsNumber(value, parseNumbers);
  switch (operatorTL) {
    case "<":
    case "<=":
    case "==":
    case "!=":
    case ">":
    case ">=":
      return `${field} ${operatorTL} ${valueIsField || useBareValue ? trimIfString(value) : `"${escapeDoubleQuotes(value, escapeQuotes)}"`}`;
    case "contains":
    case "doesnotcontain": {
      const negate2 = shouldNegate(operatorTL) ? "!" : "";
      return `${negate2}${field}.contains(${valueIsField ? trimIfString(value) : `"${escapeDoubleQuotes(value, escapeQuotes)}"`})`;
    }
    case "beginswith":
    case "doesnotbeginwith": {
      const negate2 = shouldNegate(operatorTL) ? "!" : "";
      return `${negate2}${field}.startsWith(${valueIsField ? trimIfString(value) : `"${escapeDoubleQuotes(value, escapeQuotes)}"`})`;
    }
    case "endswith":
    case "doesnotendwith": {
      const negate2 = shouldNegate(operatorTL) ? "!" : "";
      return `${negate2}${field}.endsWith(${valueIsField ? trimIfString(value) : `"${escapeDoubleQuotes(value, escapeQuotes)}"`})`;
    }
    case "null":
      return `${field} == null`;
    case "notnull":
      return `${field} != null`;
    case "in":
    case "notin": {
      const [prefix, suffix] = shouldNegate(operatorTL) ? ["!(", ")"] : ["", ""];
      const valueAsArray = toArray(value);
      return `${prefix}${field} in [${valueAsArray.map((val) => valueIsField || shouldRenderAsNumber(val, parseNumbers) ? `${trimIfString(val)}` : `"${escapeDoubleQuotes(val, escapeQuotes)}"`).join(", ")}]${suffix}`;
    }
    case "between":
    case "notbetween": {
      const valueAsArray = toArray(value);
      if (valueAsArray.length >= 2 && !nullOrUndefinedOrEmpty(valueAsArray[0]) && !nullOrUndefinedOrEmpty(valueAsArray[1])) {
        const [first, second] = valueAsArray;
        const firstNum = shouldRenderAsNumber(first, true) ? parseNumber(first, { parseNumbers: true }) : NaN;
        const secondNum = shouldRenderAsNumber(second, true) ? parseNumber(second, { parseNumbers: true }) : NaN;
        let firstValue = isNaN(firstNum) ? valueIsField ? `${first}` : `"${escapeDoubleQuotes(first, escapeQuotes)}"` : firstNum;
        let secondValue = isNaN(secondNum) ? valueIsField ? `${second}` : `"${escapeDoubleQuotes(second, escapeQuotes)}"` : secondNum;
        if (!preserveValueOrder && firstValue === firstNum && secondValue === secondNum && secondNum < firstNum) {
          const tempNum = secondNum;
          secondValue = firstNum;
          firstValue = tempNum;
        }
        return operatorTL === "between" ? `(${field} >= ${firstValue} && ${field} <= ${secondValue})` : `(${field} < ${firstValue} || ${field} > ${secondValue})`;
      } else {
        return "";
      }
    }
  }
  return "";
};

// src/utils/formatQuery/defaultRuleProcessorMongoDBQuery.ts
var defaultRuleProcessorMongoDBQuery = function(_ref) {
  let { field, operator, value, valueSource } = _ref;
  let { parseNumbers, preserveValueOrder } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  const valueIsField = valueSource === "field";
  if (operator === "=" && !valueIsField) {
    return { [field]: shouldRenderAsNumber(value, parseNumbers) ? parseNumber(value, { parseNumbers: "strict" }) : value };
  }
  const operatorLC = operator.toLowerCase();
  switch (operatorLC) {
    case "<":
    case "<=":
    case "=":
    case "!=":
    case ">":
    case ">=": {
      const mongoOperator = mongoOperators[operatorLC];
      return valueIsField ? { $expr: { [mongoOperator]: [`$${field}`, `$${value}`] } } : { [field]: { [mongoOperator]: shouldRenderAsNumber(value, parseNumbers) ? parseNumber(value, { parseNumbers: "strict" }) : value } };
    }
    case "contains":
      return valueIsField ? { $where: `this.${field}.includes(this.${value})` } : { [field]: { $regex: value } };
    case "beginswith":
      return valueIsField ? { $where: `this.${field}.startsWith(this.${value})` } : { [field]: { $regex: `^${value}` } };
    case "endswith":
      return valueIsField ? { $where: `this.${field}.endsWith(this.${value})` } : { [field]: { $regex: `${value}$` } };
    case "doesnotcontain":
      return valueIsField ? { $where: `!this.${field}.includes(this.${value})` } : { [field]: { $not: { $regex: value } } };
    case "doesnotbeginwith":
      return valueIsField ? { $where: `!this.${field}.startsWith(this.${value})` } : { [field]: { $not: { $regex: `^${value}` } } };
    case "doesnotendwith":
      return valueIsField ? { $where: `!this.${field}.endsWith(this.${value})` } : { [field]: { $not: { $regex: `${value}$` } } };
    case "null":
      return { [field]: null };
    case "notnull":
      return { [field]: { $ne: null } };
    case "in":
    case "notin": {
      const valueAsArray = toArray(value);
      return valueIsField ? { $where: `${operatorLC === "notin" ? "!" : ""}[${valueAsArray.map((val) => `this.${val}`).join(",")}].includes(this.${field})` } : { [field]: { [mongoOperators[operatorLC]]: valueAsArray.map((val) => shouldRenderAsNumber(val, parseNumbers) ? parseNumber(val, { parseNumbers: "strict" }) : val) } };
    }
    case "between":
    case "notbetween": {
      const valueAsArray = toArray(value);
      if (valueAsArray.length >= 2 && isValidValue(valueAsArray[0]) && isValidValue(valueAsArray[1])) {
        const [first, second] = valueAsArray;
        const firstNum = shouldRenderAsNumber(first, true) ? parseNumber(first, { parseNumbers: "strict" }) : NaN;
        const secondNum = shouldRenderAsNumber(second, true) ? parseNumber(second, { parseNumbers: "strict" }) : NaN;
        let firstValue = valueIsField ? first : isNaN(firstNum) ? first : firstNum;
        let secondValue = valueIsField ? second : isNaN(secondNum) ? second : secondNum;
        if (!preserveValueOrder && firstValue === firstNum && secondValue === secondNum && secondNum < firstNum) {
          const tempNum = secondNum;
          secondValue = firstNum;
          firstValue = tempNum;
        }
        if (operatorLC === "between") {
          return valueIsField ? { $and: [{ $expr: { $gte: [`$${field}`, `$${firstValue}`] } }, { $expr: { $lte: [`$${field}`, `$${secondValue}`] } }] } : { [field]: { $gte: firstValue, $lte: secondValue } };
        } else {
          return valueIsField ? { $or: [{ $expr: { $lt: [`$${field}`, `$${firstValue}`] } }, { $expr: { $gt: [`$${field}`, `$${secondValue}`] } }] } : { $or: [{ [field]: { $lt: firstValue } }, { [field]: { $gt: secondValue } }] };
        }
      } else {
        return "";
      }
    }
  }
  return "";
};

// src/utils/formatQuery/defaultRuleProcessorMongoDB.ts
var defaultRuleProcessorMongoDB = (rule, options) => {
  const queryObj = defaultRuleProcessorMongoDBQuery(rule, options);
  return queryObj ? JSON.stringify(queryObj) : "";
};

// src/utils/formatQuery/defaultRuleProcessorSpEL.ts
var shouldNegate2 = (op) => op.startsWith("not") || op.startsWith("doesnot");
var wrapInNegation = (clause, negate2) => negate2 ? `!(${clause})` : `${clause}`;
var escapeSingleQuotes = (v, escapeQuotes) => typeof v !== "string" || !escapeQuotes ? v : v.replaceAll(`'`, `\\'`);
var defaultRuleProcessorSpEL = function(_ref) {
  let { field, operator, value, valueSource } = _ref;
  let { escapeQuotes, parseNumbers, preserveValueOrder } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  const valueIsField = valueSource === "field";
  const operatorTL = (operator === "=" ? "==" : operator).toLowerCase();
  const useBareValue = typeof value === "number" || typeof value === "boolean" || typeof value === "bigint" || shouldRenderAsNumber(value, parseNumbers);
  switch (operatorTL) {
    case "<":
    case "<=":
    case "==":
    case "!=":
    case ">":
    case ">=":
      return `${field} ${operatorTL} ${valueIsField || useBareValue ? trimIfString(value) : `'${escapeSingleQuotes(value, escapeQuotes)}'`}`;
    case "contains":
    case "doesnotcontain":
      return wrapInNegation(`${field} matches ${valueIsField || useBareValue ? trimIfString(value) : `'${escapeSingleQuotes(value, escapeQuotes)}'`}`, shouldNegate2(operatorTL));
    case "beginswith":
    case "doesnotbeginwith": {
      const valueTL = valueIsField ? `'^'.concat(${trimIfString(value)})` : `'${typeof value === "string" && !value.startsWith("^") || useBareValue ? "^" : ""}${escapeSingleQuotes(value, escapeQuotes)}'`;
      return wrapInNegation(`${field} matches ${valueTL}`, shouldNegate2(operatorTL));
    }
    case "endswith":
    case "doesnotendwith": {
      const valueTL = valueIsField ? `${trimIfString(value)}.concat('$')` : `'${escapeSingleQuotes(value, escapeQuotes)}${typeof value === "string" && !value.endsWith("$") || useBareValue ? "$" : ""}'`;
      return wrapInNegation(`${field} matches ${valueTL}`, shouldNegate2(operatorTL));
    }
    case "null":
      return `${field} == null`;
    case "notnull":
      return `${field} != null`;
    case "in":
    case "notin": {
      const negate2 = shouldNegate2(operatorTL) ? "!" : "";
      const valueAsArray = toArray(value);
      return valueAsArray.length > 0 ? `${negate2}(${valueAsArray.map((val) => `${field} == ${valueIsField || shouldRenderAsNumber(val, parseNumbers) ? `${trimIfString(val)}` : `'${escapeSingleQuotes(val, escapeQuotes)}'`}`).join(" or ")})` : "";
    }
    case "between":
    case "notbetween": {
      const valueAsArray = toArray(value);
      if (valueAsArray.length >= 2 && !nullOrUndefinedOrEmpty(valueAsArray[0]) && !nullOrUndefinedOrEmpty(valueAsArray[1])) {
        const [first, second] = valueAsArray;
        const firstNum = shouldRenderAsNumber(first, true) ? parseNumber(first, { parseNumbers: true }) : NaN;
        const secondNum = shouldRenderAsNumber(second, true) ? parseNumber(second, { parseNumbers: true }) : NaN;
        let firstValue = isNaN(firstNum) ? valueIsField ? `${first}` : `'${escapeSingleQuotes(first, escapeQuotes)}'` : firstNum;
        let secondValue = isNaN(secondNum) ? valueIsField ? `${second}` : `'${escapeSingleQuotes(second, escapeQuotes)}'` : secondNum;
        if (!preserveValueOrder && firstValue === firstNum && secondValue === secondNum && secondNum < firstNum) {
          const tempNum = secondNum;
          secondValue = firstNum;
          firstValue = tempNum;
        }
        return operatorTL === "between" ? `(${field} >= ${firstValue} and ${field} <= ${secondValue})` : `(${field} < ${firstValue} or ${field} > ${secondValue})`;
      } else {
        return "";
      }
    }
  }
  return "";
};

// src/utils/formatQuery/defaultValueProcessorByRule.ts
var escapeStringValueQuotes = (v, quoteChar, escapeQuotes) => escapeQuotes && typeof v === "string" ? v.replaceAll(`${quoteChar}`, `${quoteChar}${quoteChar}`) : v;
var defaultValueProcessorByRule = function(_ref) {
  let { operator, value, valueSource } = _ref;
  let { escapeQuotes, parseNumbers, preserveValueOrder, quoteFieldNamesWith, quoteValuesWith, concatOperator = "||", fieldIdentifierSeparator, wrapValueWith = ["", ""] } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  const valueIsField = valueSource === "field";
  const operatorLowerCase = operator.toLowerCase();
  const quoteChar = quoteValuesWith || "'";
  const quoteValue = (v) => `${wrapValueWith[0]}${quoteChar}${v}${quoteChar}${wrapValueWith[1]}`;
  const escapeValue = (v) => escapeStringValueQuotes(v, quoteChar, escapeQuotes);
  const wrapAndEscape = (v) => quoteValue(escapeValue(v));
  const wrapFieldName = (v) => getQuotedFieldName(v, { quoteFieldNamesWith, fieldIdentifierSeparator });
  const concat = function() {
    for (var _len = arguments.length, values = new Array(_len), _key = 0; _key < _len; _key++) {
      values[_key] = arguments[_key];
    }
    return concatOperator.toUpperCase() === "CONCAT" ? `CONCAT(${values.join(", ")})` : values.join(` ${concatOperator} `);
  };
  switch (operatorLowerCase) {
    case "null":
    case "notnull": {
      return "";
    }
    case "in":
    case "notin": {
      const valueAsArray = toArray(value);
      if (valueAsArray.length > 0) {
        return `(${valueAsArray.map((v) => valueIsField ? wrapFieldName(v) : shouldRenderAsNumber(v, parseNumbers) ? `${trimIfString(v)}` : `${wrapAndEscape(v)}`).join(", ")})`;
      }
      return "";
    }
    case "between":
    case "notbetween": {
      const valueAsArray = toArray(value, { retainEmptyStrings: true });
      if (valueAsArray.length >= 2 && isValidValue(valueAsArray[0]) && isValidValue(valueAsArray[1])) {
        const [first, second] = valueAsArray;
        const firstNum = shouldRenderAsNumber(first, parseNumbers) ? parseNumber(first, { parseNumbers: "strict" }) : NaN;
        const secondNum = shouldRenderAsNumber(second, parseNumbers) ? parseNumber(second, { parseNumbers: "strict" }) : NaN;
        const firstValue = isNaN(firstNum) ? valueIsField ? `${first}` : first : firstNum;
        const secondValue = isNaN(secondNum) ? valueIsField ? `${second}` : second : secondNum;
        const valsOneAndTwoOnly = [firstValue, secondValue];
        if (!preserveValueOrder && firstValue === firstNum && secondValue === secondNum && secondNum < firstNum) {
          valsOneAndTwoOnly[0] = secondNum;
          valsOneAndTwoOnly[1] = firstNum;
        }
        return (valueIsField ? valsOneAndTwoOnly.map((v) => wrapFieldName(v)) : valsOneAndTwoOnly.every((v) => shouldRenderAsNumber(v, parseNumbers)) ? valsOneAndTwoOnly.map((v) => parseNumber(v, { parseNumbers: "strict" })) : valsOneAndTwoOnly.map((v) => wrapAndEscape(v))).join(` and `);
      }
      return "";
    }
    case "contains":
    case "doesnotcontain":
      return valueIsField ? concat(quoteValue("%"), wrapFieldName(value), quoteValue("%")) : quoteValue(`%${escapeValue(value)}%`);
    case "beginswith":
    case "doesnotbeginwith":
      return valueIsField ? concat(wrapFieldName(value), quoteValue("%")) : quoteValue(`${escapeValue(value)}%`);
    case "endswith":
    case "doesnotendwith":
      return valueIsField ? concat(quoteValue("%"), wrapFieldName(value)) : quoteValue(`%${escapeValue(value)}`);
  }
  if (typeof value === "boolean") {
    return value ? "TRUE" : "FALSE";
  }
  return valueIsField ? wrapFieldName(value) : shouldRenderAsNumber(value, parseNumbers) ? `${trimIfString(value)}` : `${wrapAndEscape(value)}`;
};

// src/utils/formatQuery/defaultRuleProcessorElasticSearch.ts
var rangeOperatorMap = { "<": "lt", "<=": "lte", ">": "gt", ">=": "gte" };
var negateIfNotOp = (op, elasticSearchRule) => op.startsWith("not") || op.startsWith("doesnot") ? { bool: { must_not: elasticSearchRule } } : elasticSearchRule;
var escapeSQ = (s) => s?.replace(/('|\\)/g, `\\$1`);
var textFunctionMap = { beginswith: "startsWith", doesnotbeginwith: "startsWith", doesnotcontain: "contains", doesnotendwith: "endsWith", endswith: "endsWith" };
var getTextScript = (f, o, v) => {
  const script = `doc['${f}'].value.${textFunctionMap[o] ?? o}(doc['${v}'].value)`;
  return o.startsWith("d") ? `!${script}` : script;
};
var valueRenderer = (v, parseNumbers) => typeof v === "boolean" ? v : shouldRenderAsNumber(v, parseNumbers) ? parseNumber(v, { parseNumbers }) : v;
var defaultRuleProcessorElasticSearch = function(_ref) {
  let { field, operator, value, valueSource } = _ref;
  let { parseNumbers, preserveValueOrder } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  const operatorLC = operator.toLowerCase();
  if (valueSource === "field") {
    if (toArray(value).some((v) => typeof v !== "string")) return false;
    const fieldForScript = escapeSQ(field);
    switch (operatorLC) {
      case "=":
      case "!=":
      case ">":
      case ">=":
      case "<":
      case "<=": {
        const operatorForScript = operatorLC === "=" ? "==" : operatorLC;
        const valueForScript = escapeSQ(value);
        return valueForScript ? { bool: { filter: { script: { script: `doc['${fieldForScript}'].value ${operatorForScript} doc['${valueForScript}'].value` } } } } : false;
      }
      case "in":
      case "notin": {
        const valueAsArray = toArray(value);
        if (valueAsArray.length > 0) {
          const arr = valueAsArray.map((v) => ({ bool: { filter: { script: { script: `doc['${fieldForScript}'].value == doc['${v}'].value` } } } }));
          return { bool: operatorLC === "in" ? { should: arr } : { must_not: arr } };
        }
        return false;
      }
      case "between":
      case "notbetween": {
        const valueAsArray = toArray(value);
        if (valueAsArray.length >= 2 && valueAsArray[0] && valueAsArray[1]) {
          const script = `doc['${fieldForScript}'].value >= doc['${valueAsArray[0]}'].value && doc['${fieldForScript}'].value <= doc['${valueAsArray[1]}'].value`;
          return { bool: { filter: { script: { script: operatorLC === "notbetween" ? `!(${script})` : script } } } };
        }
        return false;
      }
      case "contains":
      case "doesnotcontain":
      case "beginswith":
      case "doesnotbeginwith":
      case "endswith":
      case "doesnotendwith": {
        const valueForScript = escapeSQ(value);
        if (!valueForScript) return false;
        const script = getTextScript(fieldForScript, operatorLC, valueForScript);
        return { bool: { filter: { script: { script } } } };
      }
    }
  }
  switch (operatorLC) {
    case "<":
    case "<=":
    case ">":
    case ">=":
      return { range: { [field]: { [rangeOperatorMap[operatorLC]]: valueRenderer(value, parseNumbers) } } };
    case "=":
      return { term: { [field]: valueRenderer(value, parseNumbers) } };
    case "!=":
      return { bool: { must_not: { term: { [field]: valueRenderer(value, parseNumbers) } } } };
    case "null":
      return { bool: { must_not: { exists: { field } } } };
    case "notnull":
      return { exists: { field } };
    case "in":
    case "notin": {
      const valueAsArray = toArray(value).map((v) => valueRenderer(v, parseNumbers));
      if (valueAsArray.length > 0) {
        const arr = valueAsArray.map((v) => ({ term: { [field]: valueRenderer(v, parseNumbers) } }));
        return { bool: operatorLC === "in" ? { should: arr } : { must_not: arr } };
      }
      return false;
    }
    case "between":
    case "notbetween": {
      const valueAsArray = toArray(value);
      if (valueAsArray.length >= 2 && isValidValue(valueAsArray[0]) && isValidValue(valueAsArray[1])) {
        let [first, second] = valueAsArray;
        if (shouldRenderAsNumber(first, true) && shouldRenderAsNumber(second, true)) {
          const firstNum = parseNumber(first, { parseNumbers: true });
          const secondNum = parseNumber(second, { parseNumbers: true });
          if (!preserveValueOrder && secondNum < firstNum) {
            const tempNum = secondNum;
            second = firstNum;
            first = tempNum;
          } else {
            first = firstNum;
            second = secondNum;
          }
        }
        return negateIfNotOp(operatorLC, { range: { [field]: { gte: first, lte: second } } });
      }
      return false;
    }
    case "contains":
    case "doesnotcontain":
      return negateIfNotOp(operatorLC, { regexp: { [field]: { value: `.*${value}.*` } } });
    case "beginswith":
    case "doesnotbeginwith":
      return negateIfNotOp(operatorLC, { regexp: { [field]: { value: `${value}.*` } } });
    case "endswith":
    case "doesnotendwith":
      return negateIfNotOp(operatorLC, { regexp: { [field]: { value: `.*${value}` } } });
  }
  return false;
};

// src/utils/formatQuery/defaultRuleProcessorJSONata.ts
var shouldNegate3 = (op) => op.startsWith("not") || op.startsWith("doesnot");
var quote = (v, escapeQuotes) => `"${typeof v !== "string" || !escapeQuotes ? v : v.replaceAll(`"`, `\\"`)}"`;
var negate = (clause, negate2) => negate2 ? `$not(${clause})` : `${clause}`;
var escapeStringRegex = (s) => `${s}`.replaceAll(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`).replaceAll("-", String.raw`\x2d`);
var defaultRuleProcessorJSONata = function(_ref) {
  let { field, operator, value, valueSource } = _ref;
  let { escapeQuotes, parseNumbers = true, preserveValueOrder, quoteFieldNamesWith = ["", ""], fieldIdentifierSeparator = "" } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  const valueIsField = valueSource === "field";
  const useBareValue = typeof value === "number" || typeof value === "boolean" || typeof value === "bigint" || shouldRenderAsNumber(value, parseNumbers);
  const qfn = (f) => getQuotedFieldName(f, { quoteFieldNamesWith, fieldIdentifierSeparator });
  const operatorLC = operator.toLowerCase();
  switch (operatorLC) {
    case "<":
    case "<=":
    case "=":
    case "!=":
    case ">":
    case ">=":
      return `${qfn(field)} ${operatorLC} ${valueIsField ? qfn(trimIfString(value)) : useBareValue ? trimIfString(value) : quote(value, escapeQuotes)}`;
    case "contains":
    case "doesnotcontain":
      return negate(`$contains(${qfn(field)}, ${valueIsField ? qfn(trimIfString(value)) : quote(value, escapeQuotes)})`, shouldNegate3(operatorLC));
    case "beginswith":
    case "doesnotbeginwith":
      return negate(valueIsField ? `$substring(${qfn(field)}, 0, $length(${qfn(trimIfString(value))})) = ${qfn(trimIfString(value))}` : `$contains(${qfn(field)}, /^${escapeStringRegex(value)}/)`, shouldNegate3(operatorLC));
    case "endswith":
    case "doesnotendwith":
      return negate(valueIsField ? `$substring(${qfn(field)}, $length(${qfn(field)}) - $length(${qfn(trimIfString(value))})) = ${qfn(trimIfString(value))}` : `$contains(${qfn(field)}, /${escapeStringRegex(value)}$/)`, shouldNegate3(operatorLC));
    case "null":
      return `${qfn(field)} = null`;
    case "notnull":
      return `${qfn(field)} != null`;
    case "in":
    case "notin": {
      const valueAsArray = toArray(value);
      return negate(`${qfn(field)} in [${valueAsArray.map((val) => valueIsField ? `${qfn(trimIfString(val))}` : shouldRenderAsNumber(val, parseNumbers) ? `${trimIfString(val)}` : quote(val, escapeQuotes)).join(", ")}]`, shouldNegate3(operatorLC));
    }
    case "between":
    case "notbetween": {
      const valueAsArray = toArray(value);
      if (valueAsArray.length < 2 || nullOrUndefinedOrEmpty(valueAsArray[0]) || nullOrUndefinedOrEmpty(valueAsArray[1])) {
        return "";
      }
      const [first, second] = valueAsArray;
      const firstNum = shouldRenderAsNumber(first, true) ? parseNumber(first, { parseNumbers: true }) : NaN;
      const secondNum = shouldRenderAsNumber(second, true) ? parseNumber(second, { parseNumbers: true }) : NaN;
      let firstValue = isNaN(firstNum) ? valueIsField ? `${first}` : first : firstNum;
      let secondValue = isNaN(secondNum) ? valueIsField ? `${second}` : second : secondNum;
      if (!preserveValueOrder && firstValue === firstNum && secondValue === secondNum && secondNum < firstNum) {
        const tempNum = secondNum;
        secondValue = firstNum;
        firstValue = tempNum;
      }
      const renderAsNumbers = shouldRenderAsNumber(first, parseNumbers) && shouldRenderAsNumber(second, parseNumbers);
      const expression = `${qfn(field)} >= ${valueIsField ? qfn(first) : renderAsNumbers ? firstValue : quote(firstValue, escapeQuotes)} and ${qfn(field)} <= ${valueIsField ? qfn(second) : renderAsNumbers ? secondValue : quote(secondValue, escapeQuotes)}`;
      return operatorLC === "between" ? `(${expression})` : negate(expression, true);
    }
  }
  return "";
};

// src/utils/formatQuery/defaultRuleProcessorJsonLogic.ts
var convertOperator = (op) => op.replace(/^(=)$/, "$1=").replace(/^notnull$/i, "!=").replace(/^null$/i, "==");
var negateIfNotOp2 = (op, jsonRule) => op.startsWith("not") || op.startsWith("doesnot") ? { "!": jsonRule } : jsonRule;
var defaultRuleProcessorJsonLogic = function(_ref) {
  let { field, operator, value, valueSource } = _ref;
  let { parseNumbers, preserveValueOrder } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  const valueIsField = valueSource === "field";
  const fieldObject = { var: field };
  const fieldOrNumberRenderer = (v) => valueIsField ? { var: `${v}` } : shouldRenderAsNumber(v, parseNumbers) ? parseNumber(v, { parseNumbers }) : v;
  const operatorLC = operator.toLowerCase();
  switch (operatorLC) {
    case "<":
    case "<=":
    case "=":
    case "!=":
    case ">":
    case ">=":
      return { [convertOperator(operatorLC)]: [fieldObject, fieldOrNumberRenderer(value)] };
    case "null":
    case "notnull": {
      return { [`${operatorLC === "notnull" ? "!" : "="}=`]: [fieldObject, null] };
    }
    case "in":
    case "notin": {
      const valueAsArray = toArray(value).map((v) => fieldOrNumberRenderer(v));
      return negateIfNotOp2(operatorLC, { in: [fieldObject, valueAsArray] });
    }
    case "between":
    case "notbetween": {
      const valueAsArray = toArray(value);
      if (valueAsArray.length >= 2 && isValidValue(valueAsArray[0]) && isValidValue(valueAsArray[1])) {
        let [first, second] = valueAsArray;
        if (!valueIsField && shouldRenderAsNumber(first, true) && shouldRenderAsNumber(second, true)) {
          const firstNum = parseNumber(first, { parseNumbers: true });
          const secondNum = parseNumber(second, { parseNumbers: true });
          if (!preserveValueOrder && secondNum < firstNum) {
            const tempNum = secondNum;
            second = firstNum;
            first = tempNum;
          } else {
            first = firstNum;
            second = secondNum;
          }
        } else if (valueIsField) {
          first = { var: first };
          second = { var: second };
        }
        const jsonRule = { "<=": [first, fieldObject, second] };
        return negateIfNotOp2(operatorLC, jsonRule);
      }
      return false;
    }
    case "contains":
    case "doesnotcontain": {
      const jsonRule = { in: [fieldOrNumberRenderer(value), fieldObject] };
      return negateIfNotOp2(operatorLC, jsonRule);
    }
    case "beginswith":
    case "doesnotbeginwith": {
      const jsonRule = { startsWith: [fieldObject, fieldOrNumberRenderer(value)] };
      return negateIfNotOp2(operatorLC, jsonRule);
    }
    case "endswith":
    case "doesnotendwith": {
      const jsonRule = { endsWith: [fieldObject, fieldOrNumberRenderer(value)] };
      return negateIfNotOp2(operatorLC, jsonRule);
    }
  }
  return false;
};

// src/utils/formatQuery/defaultRuleProcessorSQL.ts
var defaultOperatorProcessorSQL = (rule) => mapSQLOperator(rule.operator).toLowerCase();
var defaultRuleProcessorSQL = function(rule) {
  let opts = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  const { quoteFieldNamesWith = ["", ""], fieldIdentifierSeparator = "", quoteValuesWith = `'`, operatorProcessor = defaultOperatorProcessorSQL, valueProcessor = defaultValueProcessorByRule, concatOperator = "||" } = opts;
  const value = valueProcessor(rule, { ...opts, quoteFieldNamesWith, fieldIdentifierSeparator, quoteValuesWith, concatOperator });
  const operator = operatorProcessor(rule, opts);
  const operatorLowerCase = operator.toLowerCase();
  if ((operatorLowerCase === "in" || operatorLowerCase === "not in" || operatorLowerCase === "between" || operatorLowerCase === "not between") && !value) {
    return "";
  }
  return `${getQuotedFieldName(rule.field, { quoteFieldNamesWith, fieldIdentifierSeparator })} ${operator} ${value}`.trim();
};

// src/utils/formatQuery/defaultRuleProcessorParameterized.ts
var defaultRuleProcessorParameterized = (rule, opts, meta) => {
  const { fieldData, format, getNextNamedParam, parseNumbers, paramPrefix, paramsKeepPrefix, numberedParams, quoteFieldNamesWith = ["", ""], concatOperator, operatorProcessor = defaultOperatorProcessorSQL, valueProcessor = defaultValueProcessorByRule } = opts ?? {};
  const { processedParams = [] } = meta ?? {};
  const parameterized = format === "parameterized";
  const params = [];
  const paramsNamed = {};
  const finalize = (sql) => parameterized ? { sql, params } : { sql, params: paramsNamed };
  const value = valueProcessor(rule, { parseNumbers, quoteFieldNamesWith, concatOperator, fieldData, format });
  const sqlOperator = operatorProcessor(rule, opts);
  const sqlOperatorLowerCase = sqlOperator.toLowerCase();
  const [qPre, qPost] = quoteFieldNamesWith;
  if ((sqlOperatorLowerCase === "in" || sqlOperatorLowerCase === "not in" || sqlOperatorLowerCase === "between" || sqlOperatorLowerCase === "not between") && !value) {
    return finalize("");
  } else if (sqlOperatorLowerCase === "is null" || sqlOperatorLowerCase === "is not null") {
    return finalize(`${qPre}${rule.field}${qPost} ${sqlOperator}`);
  } else if (rule.valueSource === "field") {
    return finalize(`${qPre}${rule.field}${qPost} ${sqlOperator} ${value}`.trim());
  } else if (sqlOperatorLowerCase === "in" || sqlOperatorLowerCase === "not in") {
    const splitValue = toArray(rule.value);
    if (parameterized) {
      for (const v of splitValue) {
        params.push(shouldRenderAsNumber(v, parseNumbers) ? parseNumber(v, { parseNumbers }) : v);
      }
      return finalize(`${qPre}${rule.field}${qPost} ${sqlOperator} (${splitValue.map((_v, i) => numberedParams ? `${paramPrefix}${processedParams.length + 1 + splitValue.length - (splitValue.length - i)}` : "?").join(", ")})`);
    }
    const inParams = [];
    for (const v of splitValue) {
      const thisParamName = getNextNamedParam(rule.field);
      inParams.push(`${paramPrefix}${thisParamName}`);
      paramsNamed[`${paramsKeepPrefix ? paramPrefix : ""}${thisParamName}`] = shouldRenderAsNumber(v, parseNumbers) ? parseNumber(v, { parseNumbers }) : v;
    }
    return finalize(`${qPre}${rule.field}${qPost} ${sqlOperator} (${inParams.join(", ")})`);
  } else if (sqlOperatorLowerCase === "between" || sqlOperatorLowerCase === "not between") {
    const valueAsArray = toArray(rule.value, { retainEmptyStrings: true });
    const [first, second] = valueAsArray.slice(0, 2).map((v) => shouldRenderAsNumber(v, parseNumbers) ? parseNumber(v, { parseNumbers }) : v);
    if (parameterized) {
      params.push(first, second);
      return finalize(`${qPre}${rule.field}${qPost} ${sqlOperator} ${numberedParams ? `${paramPrefix}${processedParams.length + 1}` : "?"} and ${numberedParams ? `${paramPrefix}${processedParams.length + 2}` : "?"}`);
    }
    const firstParamName = getNextNamedParam(rule.field);
    const secondParamName = getNextNamedParam(rule.field);
    paramsNamed[`${paramsKeepPrefix ? paramPrefix : ""}${firstParamName}`] = first;
    paramsNamed[`${paramsKeepPrefix ? paramPrefix : ""}${secondParamName}`] = second;
    return finalize(`${qPre}${rule.field}${qPost} ${sqlOperator} ${paramPrefix}${firstParamName} and ${paramPrefix}${secondParamName}`);
  }
  let paramValue = rule.value;
  if (typeof rule.value === "string") {
    if (shouldRenderAsNumber(rule.value, parseNumbers)) {
      paramValue = parseNumber(rule.value, { parseNumbers });
    } else {
      paramValue = /^'.*'$/g.test(value) ? value.replaceAll(/(^'|'$)/g, "") : (
        /* istanbul ignore next */
        value
      );
    }
  }
  let paramName = "";
  if (parameterized) {
    params.push(paramValue);
  } else {
    paramName = getNextNamedParam(rule.field);
    paramsNamed[`${paramsKeepPrefix ? paramPrefix : ""}${paramName}`] = paramValue;
  }
  return finalize(`${qPre}${rule.field}${qPost} ${sqlOperator} ${parameterized ? numberedParams ? `${paramPrefix}${processedParams.length + 1}` : "?" : `${paramPrefix}${paramName}`}`.trim());
};

// src/utils/isRuleOrGroupValid.ts
var isValidationResult = (vr) => isPojo(vr) && typeof vr.valid === "boolean";
var isRuleOrGroupValid = (rg, validationResult, validator) => {
  if (typeof validationResult === "boolean") {
    return validationResult;
  }
  if (isValidationResult(validationResult)) {
    return validationResult.valid;
  }
  if (typeof validator === "function" && !isRuleGroup(rg)) {
    const vr = validator(rg);
    if (typeof vr === "boolean") {
      return vr;
    }
    if (isValidationResult(vr)) {
      return vr.valid;
    }
  }
  return true;
};

// src/utils/formatQuery/formatQuery.ts
import { produce as produce3 } from "immer";

// src/utils/formatQuery/defaultValueProcessorNL.ts
var escapeStringValueQuotes2 = (v, quoteChar, escapeQuotes) => escapeQuotes && typeof v === "string" ? v.replaceAll(`${quoteChar}`, `${quoteChar}${quoteChar}`) : (
  /* istanbul ignore next */
  v
);
var defaultValueProcessorNL = function(rule) {
  let opts = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  const { escapeQuotes, fields, parseNumbers, quoteFieldNamesWith, quoteValuesWith, fieldIdentifierSeparator } = opts;
  const valueIsField = rule.valueSource === "field";
  const operatorLowerCase = rule.operator.toLowerCase();
  const quoteChar = quoteValuesWith || /* istanbul ignore next */
  "'";
  const quoteValue = (v) => `${quoteChar}${v}${quoteChar}`;
  const escapeValue = (v) => escapeStringValueQuotes2(v, quoteChar, escapeQuotes);
  const wrapAndEscape = (v) => quoteValue(escapeValue(v));
  const wrapFieldName = (v) => getQuotedFieldName(v, { quoteFieldNamesWith, fieldIdentifierSeparator });
  switch (operatorLowerCase) {
    case "null":
    case "notnull": {
      return "";
    }
    case "between":
    case "notbetween":
      return defaultValueProcessorByRule(rule, opts);
    case "in":
    case "notin": {
      const valueAsArray = toArray(rule.value);
      if (valueAsArray.length === 0) return "";
      const valStringArray = valueAsArray.map((v) => valueIsField ? wrapFieldName(getOption(fields ?? /* istanbul ignore next */
      [], rule.value)?.label ?? v) : shouldRenderAsNumber(v, parseNumbers) ? `${trimIfString(v)}` : `${wrapAndEscape(v)}`);
      if (valStringArray.length <= 2) {
        return valStringArray.join(" or ");
      }
      return `${valStringArray.slice(0, -1).join(", ")}, or ${valStringArray.at(-1)}`;
    }
  }
  if (typeof rule.value === "boolean") {
    return rule.value ? "true" : "false";
  }
  return valueIsField ? wrapFieldName(getOption(fields ?? /* istanbul ignore next */
  [], rule.value)?.label ?? rule.value) : shouldRenderAsNumber(rule.value, parseNumbers) ? `${trimIfString(rule.value)}` : `${wrapAndEscape(rule.value)}`;
};

// src/utils/formatQuery/defaultRuleProcessorNL.ts
var nlOperatorMap = { "=": ["is the same as the value in", "is"], "!=": ["is not the same as the value in", "is not"], "<": ["is less than the value in", "is less than"], ">": ["is greater than the value in", "is greater than"], "<=": ["is less than or equal to the value in", "is less than or equal to"], ">=": ["is greater than or equal to the value in", "is greater than or equal to"], contains: ["contains the value in", "contains"], beginswith: ["starts with the value in", "starts with"], endswith: ["ends with the value in", "ends with"], doesnotcontain: ["does not contain the value in", "does not contain"], doesnotbeginwith: ["does not start with the value in", "does not start with"], doesnotendwith: ["does not end with the value in", "does not end with"], null: ["is null", "is null"], notnull: ["is not null", "is not null"], in: ["is the same as a value in", "is one of the values"], notin: ["is not the same as any value in", "is not one of the values"], between: ["is between the values in", "is between"], notbetween: ["is not between the values in", "is not between"] };
var defaultGetOperators = () => [];
var defaultOperatorProcessorNL = function(rule) {
  let opts = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  const { valueSource = (
    /* istanbul ignore next */
    "value"
  ) } = rule;
  const { getOperators = defaultGetOperators } = opts;
  const { value: operator, label } = getOption(toFullOptionList(getOperators(rule.field, { fieldData: opts.fieldData ?? { name: rule.field, value: rule.field, label: rule.field } }) ?? /* istanbul ignore next */
  []), rule.operator) ?? { name: rule.operator, value: rule.operator, label: rule.operator };
  return (nlOperatorMap[operator.toLowerCase()] ?? [label, label])[valueSource === "field" ? 0 : 1];
};
var defaultRuleProcessorNL = (rule, opts) => {
  const { fieldData, quoteFieldNamesWith = ["", ""], fieldIdentifierSeparator = "", quoteValuesWith = `'`, operatorProcessor = defaultOperatorProcessorNL, valueProcessor = defaultValueProcessorNL, concatOperator = "||" } = opts ?? /* istanbul ignore next */
  {};
  const value = valueProcessor(rule, { ...opts, quoteFieldNamesWith, fieldIdentifierSeparator, quoteValuesWith, concatOperator });
  const operatorLC = rule.operator.toLowerCase();
  if ((operatorLC === "in" || operatorLC === "notin" || operatorLC === "between" || operatorLC === "notbetween") && !value) {
    return "";
  }
  const processedField = getQuotedFieldName(fieldData?.label ?? rule.field, { quoteFieldNamesWith, fieldIdentifierSeparator });
  const processedOperator = operatorProcessor(rule, opts);
  return `${processedField} ${processedOperator} ${value}`.trim();
};

// src/utils/formatQuery/formatQuery.ts
var sqlDialectPresets = {
  ansi: {},
  // This should always be empty
  sqlite: { paramsKeepPrefix: true },
  oracle: {},
  mssql: { concatOperator: "+", quoteFieldNamesWith: ["[", "]"], fieldIdentifierSeparator: ".", paramPrefix: "@" },
  mysql: { concatOperator: "CONCAT" },
  postgresql: { quoteFieldNamesWith: '"', numberedParams: true, paramPrefix: "$" }
};
var defaultRuleProcessors = { cel: defaultRuleProcessorCEL, elasticsearch: defaultRuleProcessorElasticSearch, json_without_ids: defaultRuleProcessorSQL, json: defaultRuleProcessorSQL, jsonata: defaultRuleProcessorJSONata, jsonlogic: defaultRuleProcessorJsonLogic, mongodb_query: defaultRuleProcessorMongoDBQuery, mongodb: defaultRuleProcessorMongoDB, natural_language: defaultRuleProcessorNL, parameterized_named: defaultRuleProcessorParameterized, parameterized: defaultRuleProcessorParameterized, spel: defaultRuleProcessorSpEL, sql: defaultRuleProcessorSQL };
var defaultOperatorProcessor = (r) => r.operator;
var defaultOperatorProcessors = { cel: defaultOperatorProcessor, elasticsearch: defaultOperatorProcessor, json_without_ids: defaultOperatorProcessor, json: defaultOperatorProcessor, jsonata: defaultOperatorProcessor, jsonlogic: defaultOperatorProcessor, mongodb_query: defaultOperatorProcessor, mongodb: defaultOperatorProcessor, natural_language: defaultOperatorProcessorNL, parameterized_named: defaultOperatorProcessorSQL, parameterized: defaultOperatorProcessorSQL, spel: defaultOperatorProcessor, sql: defaultOperatorProcessorSQL };
var defaultFallbackExpressions = { cel: "1 == 1", mongodb: '"$and":[{"$expr":true}]', natural_language: "1 is 1", spel: "1 == 1", sql: "(1 = 1)" };
var mongoDbFallback = { $and: [{ $expr: true }] };
var defaultFormatQueryOptions = { format: "json", fields: [], quoteFieldNamesWith: ["", ""], fieldIdentifierSeparator: "", getOperators: () => [], paramPrefix: ":", paramsKeepPrefix: false, numberedParams: false, parseNumbers: false, preserveValueOrder: false, placeholderFieldName: defaultPlaceholderFieldName, placeholderOperatorName: defaultPlaceholderOperatorName, quoteValuesWith: "'", concatOperator: "||", preset: "ansi" };
var valueProcessorCanActAsRuleProcessor = (format) => format === "mongodb" || format === "mongodb_query" || format === "cel" || format === "spel" || format === "jsonlogic" || format === "elasticsearch" || format === "jsonata";
function formatQuery(ruleGroup) {
  let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  const optObj = { ...defaultFormatQueryOptions, ...sqlDialectPresets[options.preset ?? "ansi"] ?? null, ...typeof options === "string" ? { format: options } : options, ...typeof options !== "string" && !options.format && (Object.keys(sqlDialectPresets).includes(options.preset ?? "") ? { format: "sql" } : null) };
  const { fallbackExpression: fallbackExpression_option, getOperators: getOperators_option, operatorProcessor: operatorProcessor_option, parseNumbers, placeholderFieldName, placeholderOperatorName, quoteFieldNamesWith: quoteFieldNamesWith_option, ruleProcessor: ruleProcessor_option, validator, valueProcessor: valueProcessor_option } = optObj;
  const getParseNumberBoolean = (inputType) => !!getParseNumberMethod({ parseNumbers, inputType });
  const format = optObj.format.toLowerCase();
  const operatorProcessor = typeof operatorProcessor_option === "function" ? operatorProcessor_option : defaultOperatorProcessors[format] ?? defaultOperatorProcessor;
  const valueProcessor = typeof valueProcessor_option === "function" ? isValueProcessorLegacy(valueProcessor_option) ? (r) => valueProcessor_option(r.field, r.operator, r.value, r.valueSource) : valueProcessor_option : format === "natural_language" ? defaultValueProcessorNL : valueProcessorCanActAsRuleProcessor(format) ? ruleProcessor_option ?? defaultRuleProcessors[format] : defaultValueProcessorByRule;
  const ruleProcessor = (typeof ruleProcessor_option === "function" ? ruleProcessor_option : null) ?? (valueProcessorCanActAsRuleProcessor(format) && typeof ruleProcessor_option !== "function" && valueProcessor_option ? valueProcessor : null) ?? defaultRuleProcessors[format] ?? defaultRuleProcessorSQL;
  const quoteFieldNamesWith = getQuoteFieldNamesWithArray(quoteFieldNamesWith_option);
  const fields = toFullOptionList(optObj.fields);
  const getOperators = (f, m) => toFullOptionList(getOperators_option(f, m) ?? /* istanbul ignore next */
  []);
  const fallbackExpression = fallbackExpression_option ?? defaultFallbackExpressions[format] ?? defaultFallbackExpressions.sql;
  const finalOptions = { ...optObj, fallbackExpression, fields, format, getOperators, quoteFieldNamesWith, operatorProcessor, ruleProcessor, valueProcessor };
  if (format === "json" || format === "json_without_ids") {
    const rg = parseNumbers ? produce3(ruleGroup, (g) => numerifyValues(g, finalOptions)) : ruleGroup;
    if (format === "json_without_ids") {
      return JSON.stringify(rg, (key, value) => (
        // Remove `id` and `path` keys; leave everything else unchanged.
        key === "id" || key === "path" ? void 0 : value
      ));
    }
    return JSON.stringify(rg, null, 2);
  }
  let validationMap = {};
  if (typeof validator === "function") {
    const validationResult = validator(ruleGroup);
    if (typeof validationResult === "boolean") {
      if (validationResult === false) {
        return format === "parameterized" ? { sql: fallbackExpression, params: [] } : format === "parameterized_named" ? { sql: fallbackExpression, params: {} } : format === "mongodb" ? `{${fallbackExpression}}` : format === "mongodb_query" ? mongoDbFallback : format === "jsonlogic" ? false : format === "elasticsearch" ? {} : fallbackExpression;
      }
    } else {
      validationMap = validationResult;
    }
  }
  const validatorMap = {};
  const uniqueFields = toFlatOptionArray(fields);
  for (const f of uniqueFields) {
    if (typeof f.validator === "function") {
      validatorMap[f.value ?? /* istanbul ignore next */
      f.name] = f.validator;
    }
  }
  const validateRule = (rule) => {
    let validationResult;
    let fieldValidator;
    if (rule.id) {
      validationResult = validationMap[rule.id];
    }
    if (uniqueFields.length > 0) {
      const fieldArr = uniqueFields.filter((f) => f.name === rule.field);
      if (fieldArr.length > 0) {
        const field = fieldArr[0];
        if (typeof field.validator === "function") {
          fieldValidator = field.validator;
        }
      }
    }
    return [validationResult, fieldValidator];
  };
  if (format === "sql") {
    const processRuleGroup = (rg, outermostOrLonelyInGroup) => {
      if (!isRuleOrGroupValid(rg, validationMap[rg.id ?? /* istanbul ignore next */
      ""])) {
        return outermostOrLonelyInGroup ? fallbackExpression : (
          /* istanbul ignore next */
          ""
        );
      }
      const processedRules = rg.rules.map((rule) => {
        if (typeof rule === "string") {
          return rule;
        }
        if (isRuleGroup(rule)) {
          return processRuleGroup(rule, rg.rules.length === 1);
        }
        const [validationResult, fieldValidator] = validateRule(rule);
        if (!isRuleOrGroupValid(rule, validationResult, fieldValidator) || rule.field === placeholderFieldName || rule.operator === placeholderOperatorName) {
          return "";
        }
        const escapeQuotes = (rule.valueSource ?? "value") === "value";
        const fieldData = getOption(fields, rule.field);
        return ruleProcessor(rule, { ...finalOptions, parseNumbers: getParseNumberBoolean(fieldData?.inputType), escapeQuotes, fieldData });
      }).filter(Boolean);
      if (processedRules.length === 0) {
        return fallbackExpression;
      }
      return `${rg.not ? "NOT " : ""}(${processedRules.join(isRuleGroupType(rg) ? ` ${rg.combinator} ` : " ")})`;
    };
    return processRuleGroup(ruleGroup, true);
  }
  if (format === "parameterized" || format === "parameterized_named") {
    const parameterized = format === "parameterized";
    const params = [];
    const paramsNamed = {};
    const fieldParams = /* @__PURE__ */ new Map();
    const getNextNamedParam = (field) => {
      if (!fieldParams.has(field)) {
        fieldParams.set(field, /* @__PURE__ */ new Set());
      }
      const nextNamedParam = `${field}_${fieldParams.get(field).size + 1}`;
      fieldParams.get(field).add(nextNamedParam);
      return nextNamedParam;
    };
    const processRule = (rule) => {
      const [validationResult, fieldValidator] = validateRule(rule);
      if (!isRuleOrGroupValid(rule, validationResult, fieldValidator) || rule.field === placeholderFieldName || rule.operator === placeholderOperatorName) {
        return "";
      }
      const fieldData = getOption(fields, rule.field);
      const fieldParamNames = Object.fromEntries([...fieldParams.entries()].map((_ref) => {
        let [f, s] = _ref;
        return [f, [...s]];
      }));
      const processedRule = ruleProcessor(rule, { ...finalOptions, parseNumbers: getParseNumberBoolean(fieldData?.inputType), getNextNamedParam, fieldParamNames, fieldData }, { processedParams: params });
      if (!isPojo(processedRule)) {
        return "";
      }
      const { sql, params: customParams } = processedRule;
      if (typeof sql !== "string" || !sql) {
        return "";
      }
      if (format === "parameterized" && Array.isArray(customParams)) {
        params.push(...customParams);
      } else if (format === "parameterized_named" && isPojo(customParams)) {
        Object.assign(paramsNamed, customParams);
        for (const p of Object.keys(customParams)) fieldParams.get(rule.field)?.add(p);
      }
      return sql;
    };
    const processRuleGroup = (rg, outermostOrLonelyInGroup) => {
      if (!isRuleOrGroupValid(rg, validationMap[rg.id ?? /* istanbul ignore next */
      ""])) {
        return outermostOrLonelyInGroup ? fallbackExpression : (
          /* istanbul ignore next */
          ""
        );
      }
      const processedRules = rg.rules.map((rule) => {
        if (typeof rule === "string") {
          return rule;
        }
        if (isRuleGroup(rule)) {
          return processRuleGroup(rule, rg.rules.length === 1);
        }
        return processRule(rule);
      }).filter(Boolean);
      if (processedRules.length === 0) {
        return fallbackExpression;
      }
      return `${rg.not ? "NOT " : ""}(${processedRules.join(isRuleGroupType(rg) ? ` ${rg.combinator} ` : " ")})`;
    };
    if (parameterized) {
      return { sql: processRuleGroup(ruleGroup, true), params };
    }
    return { sql: processRuleGroup(ruleGroup, true), params: paramsNamed };
  }
  if (format === "mongodb") {
    const processRuleGroup = (rg, outermost) => {
      if (!isRuleOrGroupValid(rg, validationMap[rg.id ?? /* istanbul ignore next */
      ""])) {
        return outermost ? fallbackExpression : "";
      }
      const combinator = `"$${rg.combinator.toLowerCase()}"`;
      let hasChildRules = false;
      const expressions = rg.rules.map((rule) => {
        if (isRuleGroup(rule)) {
          const processedRuleGroup = processRuleGroup(rule);
          if (processedRuleGroup) {
            hasChildRules = true;
            return /^\{.+\}$/.test(processedRuleGroup) ? processedRuleGroup : `{${processedRuleGroup}}`;
          }
          return "";
        }
        const [validationResult, fieldValidator] = validateRule(rule);
        if (!isRuleOrGroupValid(rule, validationResult, fieldValidator) || rule.field === placeholderFieldName || rule.operator === placeholderOperatorName) {
          return "";
        }
        const fieldData = getOption(fields, rule.field);
        return ruleProcessor(rule, { ...finalOptions, parseNumbers: getParseNumberBoolean(fieldData?.inputType), fieldData });
      }).filter(Boolean);
      return expressions.length > 0 ? expressions.length === 1 && !hasChildRules ? expressions[0] : `${combinator}:[${expressions.join(",")}]` : fallbackExpression;
    };
    const rgStandard = isRuleGroupType(ruleGroup) ? ruleGroup : convertFromIC(ruleGroup);
    const processedQuery = processRuleGroup(rgStandard, true);
    return /^\{.+\}$/.test(processedQuery) ? processedQuery : `{${processedQuery}}`;
  }
  if (format === "mongodb_query") {
    const processRuleGroup = (rg, outermost) => {
      if (!isRuleOrGroupValid(rg, validationMap[rg.id ?? /* istanbul ignore next */
      ""])) {
        return outermost ? mongoDbFallback : false;
      }
      const combinator = `$${rg.combinator.toLowerCase()}`;
      let hasChildRules = false;
      const expressions = rg.rules.map((rule) => {
        if (isRuleGroup(rule)) {
          const processedRuleGroup = processRuleGroup(rule);
          if (processedRuleGroup) {
            hasChildRules = true;
            return processedRuleGroup;
          }
          return false;
        }
        const [validationResult, fieldValidator] = validateRule(rule);
        if (!isRuleOrGroupValid(rule, validationResult, fieldValidator) || rule.field === placeholderFieldName || rule.operator === placeholderOperatorName) {
          return false;
        }
        const fieldData = getOption(fields, rule.field);
        return ruleProcessor(rule, { ...finalOptions, parseNumbers: getParseNumberBoolean(fieldData?.inputType), fieldData });
      }).filter(Boolean);
      return expressions.length > 0 ? expressions.length === 1 && !hasChildRules ? expressions[0] : { [combinator]: expressions } : mongoDbFallback;
    };
    return processRuleGroup(convertFromIC(ruleGroup), true);
  }
  if (format === "cel") {
    const processRuleGroup = (rg, outermost) => {
      if (!isRuleOrGroupValid(rg, validationMap[rg.id ?? /* istanbul ignore next */
      ""])) {
        return outermost ? fallbackExpression : "";
      }
      const expression = rg.rules.map((rule) => {
        if (typeof rule === "string") {
          return celCombinatorMap[rule];
        }
        if (isRuleGroup(rule)) {
          return processRuleGroup(rule);
        }
        const [validationResult, fieldValidator] = validateRule(rule);
        if (!isRuleOrGroupValid(rule, validationResult, fieldValidator) || rule.field === placeholderFieldName || rule.operator === placeholderOperatorName) {
          return "";
        }
        const fieldData = getOption(fields, rule.field);
        return ruleProcessor(rule, { ...finalOptions, parseNumbers: getParseNumberBoolean(fieldData?.inputType), escapeQuotes: (rule.valueSource ?? "value") === "value", fieldData });
      }).filter(Boolean).join(isRuleGroupType(rg) ? ` ${celCombinatorMap[rg.combinator]} ` : " ");
      const [prefix, suffix] = rg.not || !outermost ? [`${rg.not ? "!" : ""}(`, ")"] : ["", ""];
      return expression ? `${prefix}${expression}${suffix}` : fallbackExpression;
    };
    return processRuleGroup(ruleGroup, true);
  }
  if (format === "spel") {
    const processRuleGroup = (rg, outermost) => {
      if (!isRuleOrGroupValid(rg, validationMap[rg.id ?? /* istanbul ignore next */
      ""])) {
        return outermost ? fallbackExpression : "";
      }
      const expression = rg.rules.map((rule) => {
        if (typeof rule === "string") {
          return rule;
        }
        if (isRuleGroup(rule)) {
          return processRuleGroup(rule);
        }
        const [validationResult, fieldValidator] = validateRule(rule);
        if (!isRuleOrGroupValid(rule, validationResult, fieldValidator) || rule.field === placeholderFieldName || rule.operator === placeholderOperatorName) {
          return "";
        }
        const fieldData = getOption(fields, rule.field);
        return ruleProcessor(rule, { ...finalOptions, parseNumbers: getParseNumberBoolean(fieldData?.inputType), escapeQuotes: (rule.valueSource ?? "value") === "value", fieldData });
      }).filter(Boolean).join(isRuleGroupType(rg) ? ` ${rg.combinator} ` : " ");
      const [prefix, suffix] = rg.not || !outermost ? [`${rg.not ? "!" : ""}(`, ")"] : ["", ""];
      return expression ? `${prefix}${expression}${suffix}` : fallbackExpression;
    };
    return processRuleGroup(ruleGroup, true);
  }
  if (format === "jsonata") {
    const processRuleGroup = (rg, outermost) => {
      if (!isRuleOrGroupValid(rg, validationMap[rg.id ?? /* istanbul ignore next */
      ""])) {
        return outermost ? fallbackExpression : "";
      }
      const expression = rg.rules.map((rule) => {
        if (typeof rule === "string") {
          return rule;
        }
        if (isRuleGroup(rule)) {
          return processRuleGroup(rule);
        }
        const [validationResult, fieldValidator] = validateRule(rule);
        if (!isRuleOrGroupValid(rule, validationResult, fieldValidator) || rule.field === placeholderFieldName || rule.operator === placeholderOperatorName) {
          return "";
        }
        const fieldData = getOption(fields, rule.field);
        return ruleProcessor(rule, { ...finalOptions, parseNumbers: getParseNumberBoolean(fieldData?.inputType), escapeQuotes: (rule.valueSource ?? "value") === "value", fieldData });
      }).filter(Boolean).join(isRuleGroupType(rg) ? ` ${rg.combinator} ` : " ");
      const [prefix, suffix] = rg.not || !outermost ? [`${rg.not ? "$not" : ""}(`, ")"] : ["", ""];
      return expression ? `${prefix}${expression}${suffix}` : fallbackExpression;
    };
    return processRuleGroup(ruleGroup, true);
  }
  if (format === "jsonlogic") {
    const query = isRuleGroupType(ruleGroup) ? ruleGroup : convertFromIC(ruleGroup);
    const processRuleGroup = (rg, _outermost) => {
      if (!isRuleOrGroupValid(rg, validationMap[rg.id ?? /* istanbul ignore next */
      ""])) {
        return false;
      }
      const processedRules = rg.rules.map((rule) => {
        if (isRuleGroup(rule)) {
          return processRuleGroup(rule);
        }
        const [validationResult, fieldValidator] = validateRule(rule);
        if (!isRuleOrGroupValid(rule, validationResult, fieldValidator) || rule.field === placeholderFieldName || rule.operator === placeholderOperatorName) {
          return false;
        }
        const fieldData = getOption(fields, rule.field);
        return ruleProcessor(rule, { ...finalOptions, parseNumbers: getParseNumberBoolean(fieldData?.inputType), fieldData });
      }).filter(Boolean);
      if (processedRules.length === 0) {
        return false;
      }
      const jsonRuleGroup = { [rg.combinator]: processedRules };
      return rg.not ? { "!": jsonRuleGroup } : jsonRuleGroup;
    };
    return processRuleGroup(query, true);
  }
  if (format === "elasticsearch") {
    const query = isRuleGroupType(ruleGroup) ? ruleGroup : convertFromIC(ruleGroup);
    const processRuleGroup = (rg) => {
      if (!isRuleOrGroupValid(rg, validationMap[rg.id ?? /* istanbul ignore next */
      ""])) {
        return false;
      }
      const processedRules = rg.rules.map((rule) => {
        if (isRuleGroup(rule)) {
          return processRuleGroup(rule);
        }
        const [validationResult, fieldValidator] = validateRule(rule);
        if (!isRuleOrGroupValid(rule, validationResult, fieldValidator) || rule.field === placeholderFieldName || rule.operator === placeholderOperatorName) {
          return false;
        }
        const fieldData = getOption(fields, rule.field);
        return ruleProcessor(rule, { ...finalOptions, parseNumbers: getParseNumberBoolean(fieldData?.inputType), fieldData });
      }).filter(Boolean);
      if (processedRules.length === 0) {
        return false;
      }
      return { bool: rg.not ? { must_not: /^or$/i.test(rg.combinator) ? { bool: { should: processedRules } } : processedRules } : { [/^or$/i.test(rg.combinator) ? "should" : "must"]: processedRules } };
    };
    const processedRuleGroup = processRuleGroup(query);
    return processedRuleGroup === false ? {} : processedRuleGroup;
  }
  if (format === "natural_language") {
    const processRuleGroup = (rg, outermostOrLonelyInGroup) => {
      if (!isRuleOrGroupValid(rg, validationMap[rg.id ?? /* istanbul ignore next */
      ""])) {
        return outermostOrLonelyInGroup ? fallbackExpression : (
          /* istanbul ignore next */
          ""
        );
      }
      let rg2 = rg;
      if (isRuleGroupTypeIC(rg) && rg.rules.some((r) => typeof r === "string" && r.toLowerCase() === "xor")) {
        rg2 = convertFromIC(rg);
      }
      const processedRules = rg2.rules.map((rule) => {
        if (typeof rule === "string") {
          return `, ${rule} `;
        }
        if (isRuleGroup(rule)) {
          return processRuleGroup(rule, rg2.rules.length === 1 && !(rg2.not || /^xor$/i.test(rg2.combinator ?? /* istanbul ignore next */
          "")));
        }
        const [validationResult, fieldValidator] = validateRule(rule);
        if (!isRuleOrGroupValid(rule, validationResult, fieldValidator) || rule.field === placeholderFieldName || rule.operator === placeholderOperatorName) {
          return "";
        }
        const escapeQuotes = (rule.valueSource ?? "value") === "value";
        const fieldData = getOption(fields, rule.field);
        return ruleProcessor(rule, { ...finalOptions, parseNumbers: getParseNumberBoolean(fieldData?.inputType), escapeQuotes, fieldData });
      });
      if (processedRules.length === 0) {
        return fallbackExpression;
      }
      const isXOR = /^xor$/i.test(rg2.combinator ?? "");
      const combinator = isXOR ? rg2.combinator.slice(1) : rg2.combinator;
      const mustWrap = rg2.not || !outermostOrLonelyInGroup || isXOR && processedRules.length > 1;
      const prefix = mustWrap ? `${isXOR ? rg2.not ? "either zero or more than one of " : "exactly one of " : ""}(` : "";
      const suffix = mustWrap ? `) is${rg2.not && !isXOR ? " not" : ""} true` : "";
      return `${prefix}${processedRules.filter(Boolean).join(isRuleGroupType(rg2) ? `, ${combinator} ` : "")}${suffix}`;
    };
    return processRuleGroup(ruleGroup, true);
  }
  return "";
}

// src/utils/formatQuery/index.ts
var internalValueProcessors = { default: defaultValueProcessorByRule, mongodb: defaultRuleProcessorMongoDB, cel: defaultRuleProcessorCEL, spel: defaultRuleProcessorSpEL };
var generateValueProcessor = (format) => (field, operator, value, valueSource) => internalValueProcessors[format]({ field, operator, value, valueSource }, { parseNumbers: false });
var defaultValueProcessor = generateValueProcessor("default");
var defaultMongoDBValueProcessor = generateValueProcessor("mongodb");
var defaultCELValueProcessor = generateValueProcessor("cel");
var defaultSpELValueProcessor = generateValueProcessor("spel");
var defaultValueProcessorCELByRule = defaultRuleProcessorCEL;
var defaultValueProcessorMongoDBByRule = defaultRuleProcessorMongoDB;
var defaultValueProcessorSpELByRule = defaultRuleProcessorSpEL;

// src/utils/pathUtils.ts
var findPath = (path, query) => {
  let target = query;
  let level = 0;
  while (level < path.length && target && isRuleGroup(target)) {
    const t = target.rules[path[level]];
    target = typeof t === "string" ? null : t;
    level++;
  }
  return target;
};
var getParentPath = (path) => path.slice(0, -1);
var pathsAreEqual = (path1, path2) => path1.length === path2.length && path1.every((val, idx) => val === path2[idx]);
var isAncestor = (maybeAncestor, path) => maybeAncestor.length < path.length && new RegExp(`^${maybeAncestor.join("-")}`).test(path.join("-"));
var getCommonAncestorPath = (path1, path2) => {
  const commonAncestorPath = [];
  const parentPath1 = getParentPath(path1);
  const parentPath2 = getParentPath(path2);
  let i = 0;
  while (i < parentPath1.length && i < parentPath2.length && parentPath1[i] === parentPath2[i]) {
    commonAncestorPath.push(parentPath2[i]);
    i++;
  }
  return commonAncestorPath;
};
var pathIsDisabled = (path, query) => {
  let disabled = !!query.disabled;
  let target = query;
  let level = 0;
  while (level < path.length && !disabled && isRuleGroup(target)) {
    const t = target.rules[path[level]];
    if (isPojo(t) && (isRuleGroup(t) || "field" in t && !!t.field)) {
      disabled = !!t.disabled;
      target = t;
    }
    level++;
  }
  return disabled;
};

// src/utils/generateAccessibleDescription.ts
var generateAccessibleDescription = (params) => pathsAreEqual([], params.path) ? `Query builder` : `Rule group at path ${params.path.join("-")}`;

// src/utils/generateID.ts
var cryptoModule = globalThis.crypto;
var generateID = () => "00-0-4-2-000".replaceAll(/[^-]/g, (s) => ((Math.random() + Math.trunc(s)) * 65536 >> parseInt(s)).toString(16).padStart(4, "0"));
if (cryptoModule) {
  if (typeof cryptoModule.randomUUID === "function") {
    generateID = () => cryptoModule.randomUUID();
  } else if (typeof cryptoModule.getRandomValues === "function") {
    const position19vals = "89ab";
    const container = new Uint32Array(32);
    generateID = () => {
      cryptoModule.getRandomValues(container);
      let id = (container[0] % 16).toString(16);
      for (let i = 1; i < 32; i++) {
        if (i === 12) {
          id = `${id}${"4"}`;
        } else if (i === 16) {
          id = `${id}${position19vals[container[17] % 4]}`;
        } else {
          id = `${id}${(container[i] % 16).toString(16)}`;
        }
        if (i === 7 || i === 11 || i === 15 || i === 19) {
          id = `${id}${"-"}`;
        }
      }
      return id;
    };
  }
}

// src/hooks/usePreferProp.ts
var preferPropDefaultTrue = (prop, context) => prop === false ? false : prop ? true : context === false ? false : true;
var preferPropDefaultFalse = (prop, context) => prop ? true : prop === false ? false : context ? true : false;
var preferProp = (def, prop, context) => def ? preferPropDefaultTrue(prop, context) : preferPropDefaultFalse(prop, context);
var usePreferProp = (def, prop, context) => preferProp(def, prop, context);
var usePreferAnyProp = (def, prop, context) => prop !== void 0 && prop != null ? prop : context !== void 0 && context != null ? context : def;

// src/hooks/useMergedContext.ts
import { forwardRef as forwardRef2, useCallback as useCallback6, useContext, useMemo as useMemo7 } from "react";

// src/components/Rule.tsx
import { Fragment, jsx as jsx7, jsxs as jsxs2 } from "react/jsx-runtime";
import * as React3 from "react";
import { useCallback as useCallback2, useMemo as useMemo2 } from "react";

// src/utils/getCompatContextProvider.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var getCompatContextProvider = (gccpProps) => {
  const QBContextWrapper = (props) => {
    const $ = c(3);
    const rqbContext = useMergedContext(gccpProps);
    let t0;
    if ($[0] !== props.children || $[1] !== rqbContext) {
      t0 = jsx5(QueryBuilderContext.Provider, { value: rqbContext, children: props.children });
      $[0] = props.children;
      $[1] = rqbContext;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  };
  const QBContextInner = (props) => {
    const $ = c(3);
    const rqbContext = useMergedContext(props);
    let t0;
    if ($[0] !== props.children || $[1] !== rqbContext) {
      t0 = jsx5(QueryBuilderContext.Provider, { value: rqbContext, children: props.children });
      $[0] = props.children;
      $[1] = rqbContext;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  };
  return (props) => {
    return /* @__PURE__ */ jsx5(QBContextWrapper, { children: /* @__PURE__ */ jsx5(QBContextInner, { ...props, children: props.children }) });
  };
};

// src/utils/getValidationClassNames.ts
var getValidationClassNames = (validationResult) => {
  const valid = typeof validationResult === "boolean" ? validationResult : typeof validationResult === "object" && validationResult !== null ? validationResult.valid : null;
  return typeof valid === "boolean" ? valid ? standardClassnames.valid : standardClassnames.invalid : "";
};

// src/utils/getValueSourcesUtil.ts
var defaultValueSourcesArray = ["value"];
var dummyFD = { name: "name", value: "name", valueSources: null, label: "label" };
var getValueSourcesUtil = (fieldData, operator, getValueSources) => {
  const fd = fieldData ? toFullOption(fieldData) : (
    /* istanbul ignore else */
    dummyFD
  );
  if (fd.valueSources) {
    if (typeof fd.valueSources === "function") {
      return fd.valueSources(operator);
    }
    return fd.valueSources;
  }
  if (getValueSources) {
    const vals = getValueSources(fd.value, operator, { fieldData: toFullOption(fd) });
    if (vals) return vals;
  }
  return defaultValueSourcesArray;
};

// src/utils/mergeClassnames.ts
var joinClassnamesByName = (name, args) => clsx(args.map((c2) => clsx(c2?.[name])));
var mergeClassnames = function() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }
  return { queryBuilder: joinClassnamesByName("queryBuilder", args), ruleGroup: joinClassnamesByName("ruleGroup", args), header: joinClassnamesByName("header", args), body: joinClassnamesByName("body", args), combinators: joinClassnamesByName("combinators", args), addRule: joinClassnamesByName("addRule", args), addGroup: joinClassnamesByName("addGroup", args), cloneRule: joinClassnamesByName("cloneRule", args), cloneGroup: joinClassnamesByName("cloneGroup", args), removeGroup: joinClassnamesByName("removeGroup", args), rule: joinClassnamesByName("rule", args), fields: joinClassnamesByName("fields", args), operators: joinClassnamesByName("operators", args), value: joinClassnamesByName("value", args), removeRule: joinClassnamesByName("removeRule", args), notToggle: joinClassnamesByName("notToggle", args), shiftActions: joinClassnamesByName("shiftActions", args), dragHandle: joinClassnamesByName("dragHandle", args), lockRule: joinClassnamesByName("lockRule", args), lockGroup: joinClassnamesByName("lockGroup", args), valueSource: joinClassnamesByName("valueSource", args), actionElement: joinClassnamesByName("actionElement", args), valueSelector: joinClassnamesByName("valueSelector", args), betweenRules: joinClassnamesByName("betweenRules", args), valid: joinClassnamesByName("valid", args), invalid: joinClassnamesByName("invalid", args), dndDragging: joinClassnamesByName("dndDragging", args), dndOver: joinClassnamesByName("dndOver", args), dndCopy: joinClassnamesByName("dndCopy", args), dndGroup: joinClassnamesByName("dndGroup", args), disabled: joinClassnamesByName("disabled", args), valueListItem: joinClassnamesByName("valueListItem", args), branches: joinClassnamesByName("branches", args) };
};

// src/utils/mergeTranslations.ts
import { produce as produce4 } from "immer";

// src/utils/objectUtils.ts
var objectKeys = Object.keys;
var objectEntries = Object.entries;

// src/utils/mergeTranslations.ts
var mergeTranslations = function(base) {
  for (var _len = arguments.length, otherTranslations = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    otherTranslations[_key - 1] = arguments[_key];
  }
  return produce4(base, (draft) => {
    for (const translations of otherTranslations) {
      if (translations) {
        for (const t of objectKeys(translations)) {
          if (draft[t]) {
            Object.assign(draft[t], translations[t]);
          } else {
            Object.assign(draft, { [t]: translations[t] });
          }
        }
      }
    }
  });
};
var mergeTranslation = (el, keyPropContextMap, finalize) => {
  const finalKeys = objectEntries(keyPropContextMap).map((_ref) => {
    let [key, [pT, cT]] = _ref;
    return [key, pT ?? cT ?? (finalize ? defaultTranslations : {})[el]?.[key]];
  }).filter((k) => !!k[1]);
  return finalKeys.length > 0 ? { [el]: Object.fromEntries(finalKeys) } : void 0;
};

// src/utils/prepareQueryObjects.ts
import { produce as produce5 } from "immer";
var prepareRule = function(rule) {
  let { idGenerator = generateID } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  return produce5(rule, (draft) => {
    if (!draft.id) {
      draft.id = idGenerator();
    }
  });
};
var prepareRuleGroup = function(queryObject) {
  let { idGenerator = generateID } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  return produce5(queryObject, (draft) => {
    if (!draft.id) {
      draft.id = idGenerator();
    }
    draft.rules = draft.rules.map((r) => typeof r === "string" ? r : isRuleGroup(r) ? prepareRuleGroup(r, { idGenerator }) : prepareRule(r, { idGenerator }));
  });
};
var prepareRuleOrGroup = function(rg) {
  let { idGenerator = generateID } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  return isRuleGroup(rg) ? prepareRuleGroup(rg, { idGenerator }) : prepareRule(rg, { idGenerator });
};

// src/utils/queryTools.ts
import { produce as produce6 } from "immer";

// src/utils/regenerateIDs.ts
var regenerateID = function(rule) {
  let { idGenerator = generateID } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  return structuredClone({ ...rule, id: idGenerator() });
};
var regenerateIDs = function(ruleOrGroup) {
  let { idGenerator = generateID } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  if (!isPojo(ruleOrGroup)) return ruleOrGroup;
  if (!isRuleGroup(ruleOrGroup)) {
    return structuredClone({ ...ruleOrGroup, id: idGenerator() });
  }
  if (isRuleGroupType(ruleOrGroup)) {
    const rules2 = ruleOrGroup.rules.map((r) => isRuleGroup(r) ? regenerateIDs(r, { idGenerator }) : regenerateID(r, { idGenerator }));
    return { ...ruleOrGroup, id: idGenerator(), rules: rules2 };
  }
  const rules = ruleOrGroup.rules.map((r) => typeof r === "string" ? r : isRuleGroup(r) ? regenerateIDs(r, { idGenerator }) : regenerateID(r, { idGenerator }));
  return { ...ruleOrGroup, id: idGenerator(), rules };
};

// src/utils/queryTools.ts
var add = function(query, ruleOrGroup, parentPath) {
  let { combinators = defaultCombinators, combinatorPreceding, idGenerator = generateID } = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : {};
  return produce6(query, (draft) => {
    const parent = findPath(parentPath, draft);
    if (!parent || !isRuleGroup(parent)) return;
    if (isRuleGroupTypeIC(parent) && parent.rules.length > 0) {
      const prevCombinator = parent.rules.at(-2);
      parent.rules.push(
        // @ts-expect-error This is technically a type violation until the next push
        // to the rules array, but that happens immediately and unconditionally so
        // there's no significant risk.
        combinatorPreceding ?? (typeof prevCombinator === "string" ? prevCombinator : getFirstOption(combinators))
      );
    }
    parent.rules.push(prepareRuleOrGroup(ruleOrGroup, { idGenerator }));
  });
};
var update = function(query, prop, value, path) {
  let { resetOnFieldChange = true, resetOnOperatorChange = false, getRuleDefaultOperator = () => "=", getValueSources = () => ["value"], getRuleDefaultValue = () => "" } = arguments.length > 4 && arguments[4] !== void 0 ? arguments[4] : {};
  return produce6(query, (draft) => {
    if (prop === "combinator" && !isRuleGroupType(draft)) {
      const parentRules = findPath(getParentPath(path), draft).rules;
      if (path.at(-1) % 2 === 1) {
        parentRules[path.at(-1)] = value;
      }
      return;
    }
    const ruleOrGroup = findPath(path, draft);
    if (!ruleOrGroup) return;
    const isGroup = isRuleGroup(ruleOrGroup);
    if (ruleOrGroup[prop] === value) return;
    if (prop !== "valueSource") {
      ruleOrGroup[prop] = value;
    }
    if (isGroup) return;
    let resetValueSource = false;
    let resetValue = false;
    if (resetOnFieldChange && prop === "field") {
      ruleOrGroup.operator = getRuleDefaultOperator(value);
      resetValueSource = true;
      resetValue = true;
    }
    if (resetOnOperatorChange && prop === "operator") {
      resetValueSource = true;
      resetValue = true;
    }
    const defaultValueSource = getValueSources(ruleOrGroup.field, ruleOrGroup.operator)[0];
    if (resetValueSource && ruleOrGroup.valueSource && defaultValueSource !== ruleOrGroup.valueSource || prop === "valueSource" && value !== ruleOrGroup.valueSource) {
      resetValue = !!ruleOrGroup.valueSource || !ruleOrGroup.valueSource && value !== defaultValueSource;
      ruleOrGroup.valueSource = resetValueSource ? defaultValueSource : value;
    }
    if (resetValue) {
      ruleOrGroup.value = getRuleDefaultValue(ruleOrGroup);
    }
  });
};
var remove = (query, path) => {
  if (
    // Can't remove the root group
    path.length === 0 || // Can't independently remove independent combinators
    !isRuleGroupType(query) && !findPath(path, query)
  ) {
    return query;
  }
  return produce6(query, (draft) => {
    const index = path.at(-1);
    const parent = findPath(getParentPath(path), draft);
    if (parent && isRuleGroup(parent)) {
      if (!isRuleGroupType(parent) && parent.rules.length > 1) {
        const idxStartDelete = index === 0 ? 0 : index - 1;
        parent.rules.splice(idxStartDelete, 2);
      } else {
        parent.rules.splice(index, 1);
      }
    }
  });
};
var getNextPath = (query, currentPath, newPathOrShiftDirection) => {
  if (Array.isArray(newPathOrShiftDirection)) {
    return newPathOrShiftDirection;
  }
  const ic = isRuleGroupTypeIC(query);
  if (newPathOrShiftDirection === "up") {
    if (pathsAreEqual(currentPath, [0])) {
      return currentPath;
    } else if (currentPath.at(-1) === 0) {
      const parentPath = getParentPath(currentPath);
      return [...getParentPath(parentPath), Math.max(0, parentPath.at(-1) - (ic ? 1 : 0))];
    } else {
      const evaluationPath = [...getParentPath(currentPath), Math.max(0, currentPath.at(-1) - (ic ? 2 : 1))];
      const entityAtTarget = findPath(evaluationPath, query);
      if (isRuleGroup(entityAtTarget)) {
        return [...evaluationPath, entityAtTarget.rules.length];
      } else {
        const targetPath = [...getParentPath(currentPath), Math.max(0, currentPath.at(-1) - (ic ? 3 : 1))];
        return targetPath;
      }
    }
  } else if (newPathOrShiftDirection === "down") {
    if (pathsAreEqual([query.rules.length - 1], currentPath)) {
      return currentPath;
    } else if (currentPath.at(-1) === findPath(getParentPath(currentPath), query).rules.length - 1) {
      const parentPath = getParentPath(currentPath);
      return [...getParentPath(parentPath), parentPath.at(-1) + 1];
    } else {
      const evaluationPath = [...getParentPath(currentPath), currentPath.at(-1) + (ic ? 2 : 1)];
      const entityToEvaluate = findPath(evaluationPath, query);
      if (isRuleGroup(entityToEvaluate)) {
        return [...evaluationPath, 0];
      } else {
        const targetPath = [...getParentPath(currentPath), currentPath.at(-1) + (ic ? 3 : 2)];
        return targetPath;
      }
    }
  }
  return currentPath;
};
var move = function(query, oldPath, newPath) {
  let { clone = false, combinators = defaultCombinators, idGenerator = generateID } = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : {};
  const nextPath = getNextPath(query, oldPath, newPath);
  if (oldPath.length === 0 || pathsAreEqual(oldPath, nextPath) || !findPath(getParentPath(nextPath), query)) {
    return query;
  }
  const ruleOrGroupOriginal = findPath(oldPath, query);
  if (!ruleOrGroupOriginal) {
    return query;
  }
  const ruleOrGroup = clone ? regenerateIDs(ruleOrGroupOriginal, { idGenerator }) : ruleOrGroupOriginal;
  return produce6(query, (draft) => {
    const independentCombinators = isRuleGroupTypeIC(draft);
    const parentOfRuleToRemove = findPath(getParentPath(oldPath), draft);
    const ruleToRemoveIndex = oldPath.at(-1);
    const oldPrevCombinator = independentCombinators && ruleToRemoveIndex > 0 ? parentOfRuleToRemove.rules[ruleToRemoveIndex - 1] : null;
    const oldNextCombinator = independentCombinators && ruleToRemoveIndex < parentOfRuleToRemove.rules.length - 1 ? parentOfRuleToRemove.rules[ruleToRemoveIndex + 1] : null;
    if (!clone) {
      const idxStartDelete = independentCombinators ? Math.max(0, ruleToRemoveIndex - 1) : ruleToRemoveIndex;
      const deleteLength = independentCombinators ? 2 : 1;
      parentOfRuleToRemove.rules.splice(idxStartDelete, deleteLength);
    }
    const newNewPath = [...nextPath];
    const commonAncestorPath = getCommonAncestorPath(oldPath, nextPath);
    if (!clone && oldPath.length === commonAncestorPath.length + 1 && nextPath[commonAncestorPath.length] > oldPath[commonAncestorPath.length]) {
      newNewPath[commonAncestorPath.length] -= independentCombinators ? 2 : 1;
    }
    const newNewParentPath = getParentPath(newNewPath);
    const parentToInsertInto = findPath(newNewParentPath, draft);
    const newIndex = newNewPath.at(-1);
    const insertRuleOrGroup = function() {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      return parentToInsertInto.rules.splice(newIndex, 0, ...args);
    };
    if (parentToInsertInto.rules.length === 0 || !independentCombinators) {
      insertRuleOrGroup(ruleOrGroup);
    } else {
      if (newIndex === 0) {
        if (ruleToRemoveIndex === 0 && oldNextCombinator) {
          insertRuleOrGroup(ruleOrGroup, oldNextCombinator);
        } else {
          const newNextCombinator = parentToInsertInto.rules[1] ?? oldPrevCombinator ?? getFirstOption(combinators);
          insertRuleOrGroup(ruleOrGroup, newNextCombinator);
        }
      } else {
        if (oldPrevCombinator) {
          insertRuleOrGroup(oldPrevCombinator, ruleOrGroup);
        } else {
          const newPrevCombinator = parentToInsertInto.rules[newIndex - 2] ?? oldNextCombinator ?? getFirstOption(combinators);
          insertRuleOrGroup(newPrevCombinator, ruleOrGroup);
        }
      }
    }
  });
};
var insert = function(query, ruleOrGroup, path) {
  let { combinators = defaultCombinators, combinatorPreceding, combinatorSucceeding, idGenerator = generateID, replace = false } = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : {};
  return produce6(query, (draft) => {
    const parentToInsertInto = findPath(getParentPath(path), draft);
    if (!parentToInsertInto || !isRuleGroup(parentToInsertInto)) return;
    const rorg = regenerateIDs(ruleOrGroup, { idGenerator });
    const independentCombinators = isRuleGroupTypeIC(draft);
    const newIndex = path.at(-1);
    const insertRuleOrGroup = function(idx) {
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }
      return parentToInsertInto.rules.splice(idx, replace ? args.length : 0, ...args);
    };
    if (parentToInsertInto.rules.length === 0 || !independentCombinators) {
      insertRuleOrGroup(newIndex, rorg);
    } else if (replace && independentCombinators) {
      insertRuleOrGroup(newIndex + newIndex % 2, rorg);
    } else {
      if (newIndex === 0) {
        if (rorg.path?.at(-1) === 0 && combinatorSucceeding) {
          insertRuleOrGroup(newIndex, rorg, combinatorSucceeding);
        } else {
          const newNextCombinator = parentToInsertInto.rules[1] ?? combinatorPreceding ?? getFirstOption(combinators);
          insertRuleOrGroup(newIndex, rorg, newNextCombinator);
        }
      } else {
        const normalizedNewIndex = newIndex % 2 === 0 ? newIndex - 1 : newIndex;
        if (combinatorPreceding) {
          insertRuleOrGroup(normalizedNewIndex, combinatorPreceding, rorg);
        } else {
          const newPrevCombinator = parentToInsertInto.rules[normalizedNewIndex - 2] ?? combinatorSucceeding ?? getFirstOption(combinators);
          insertRuleOrGroup(normalizedNewIndex, newPrevCombinator, rorg);
        }
      }
    }
  });
};
var group = function(query, sourcePath, targetPath) {
  let { clone = false, combinators = defaultCombinators, idGenerator = generateID } = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : {};
  const nextPath = getNextPath(query, sourcePath, targetPath);
  if (sourcePath.length === 0 || pathsAreEqual(sourcePath, nextPath) || !findPath(getParentPath(nextPath), query)) {
    return query;
  }
  const sourceRuleOrGroupOriginal = findPath(sourcePath, query);
  const targetRuleOrGroup = findPath(targetPath, query);
  if (!sourceRuleOrGroupOriginal || !targetRuleOrGroup) {
    return query;
  }
  const sourceRuleOrGroup = clone ? regenerateIDs(sourceRuleOrGroupOriginal, { idGenerator }) : sourceRuleOrGroupOriginal;
  return produce6(query, (draft) => {
    const independentCombinators = isRuleGroupTypeIC(draft);
    const parentOfRuleToRemove = findPath(getParentPath(sourcePath), draft);
    const ruleToRemoveIndex = sourcePath.at(-1);
    if (!clone) {
      const idxStartDelete = independentCombinators ? Math.max(0, ruleToRemoveIndex - 1) : ruleToRemoveIndex;
      const deleteLength = independentCombinators ? 2 : 1;
      parentOfRuleToRemove.rules.splice(idxStartDelete, deleteLength);
    }
    const newNewPath = [...nextPath];
    const commonAncestorPath = getCommonAncestorPath(sourcePath, nextPath);
    if (!clone && sourcePath.length === commonAncestorPath.length + 1 && nextPath[commonAncestorPath.length] > sourcePath[commonAncestorPath.length]) {
      newNewPath[commonAncestorPath.length] -= independentCombinators ? 2 : 1;
    }
    const newNewParentPath = getParentPath(newNewPath);
    const parentOfTargetPath = findPath(newNewParentPath, draft);
    const targetPathIndex = newNewPath.at(-1);
    parentOfTargetPath.rules.splice(targetPathIndex, 1, prepareRuleOrGroup(
      independentCombinators ? { rules: [targetRuleOrGroup, getFirstOption(combinators), sourceRuleOrGroup] } : {
        combinator: getFirstOption(combinators),
        rules: [targetRuleOrGroup, sourceRuleOrGroup]
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      },
      { idGenerator }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ));
  });
};

// src/utils/toOptions.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var toOptions = (arr) => isOptionGroupArray(arr) ? arr.map((og) => /* @__PURE__ */ jsx6("optgroup", { label: og.label, children: og.options.map((opt) => /* @__PURE__ */ jsx6("option", { value: opt.name, disabled: opt.disabled, children: opt.label }, opt.name)) }, og.label)) : Array.isArray(arr) ? arr.map((opt) => /* @__PURE__ */ jsx6("option", { value: opt.name, disabled: opt.disabled, children: opt.label }, opt.name)) : null;

// src/utils/transformQuery.ts
import { produce as produce7 } from "immer";
var remapProperties = (obj, propertyMap, deleteRemappedProperties) => produce7(obj, (draft) => {
  for (const [k, v] of Object.entries(propertyMap)) {
    if (v === false) {
      delete draft[k];
    } else if (!!v && k !== v && k in draft) {
      draft[v] = draft[k];
      if (deleteRemappedProperties) {
        delete draft[k];
      }
    }
  }
});
function transformQuery(query) {
  let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  const { ruleProcessor = (r) => r, ruleGroupProcessor = (rg) => rg, propertyMap = {}, combinatorMap = {}, operatorMap = {}, omitPath = false, deleteRemappedProperties = true } = options;
  const processGroup = (rg) => ({ ...ruleGroupProcessor(remapProperties({ ...rg, ...isRuleGroupType(rg) ? { combinator: combinatorMap[rg.combinator] ?? rg.combinator } : {} }, propertyMap, deleteRemappedProperties)), ...propertyMap["rules"] === false ? null : {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [propertyMap["rules"] ?? "rules"]: rg.rules.map((r, idx) => {
      const pathObject = omitPath ? null : { path: [...rg.path, idx] };
      if (typeof r === "string") {
        return combinatorMap[r] ?? r;
      } else if (isRuleGroup(r)) {
        return processGroup({ ...r, ...pathObject });
      }
      return ruleProcessor(remapProperties({ ...r, ...pathObject, ..."operator" in r ? { operator: operatorMap[r.operator] ?? r.operator } : {} }, propertyMap, deleteRemappedProperties));
    })
  } });
  return processGroup({ ...query, ...omitPath ? null : { path: [] } });
}

// src/components/Rule.tsx
var Rule = /* @__PURE__ */ React3.memo(function Rule2(props) {
  const $ = c(7);
  const r = useRule(props);
  const cloneRule = useStopEventPropagation(r.cloneRule);
  const toggleLockRule = useStopEventPropagation(r.toggleLockRule);
  const removeRule = useStopEventPropagation(r.removeRule);
  const shiftRuleUp = useStopEventPropagation(r.shiftRuleUp);
  const shiftRuleDown = useStopEventPropagation(r.shiftRuleDown);
  let t0;
  if ($[0] !== cloneRule || $[1] !== r || $[2] !== removeRule || $[3] !== shiftRuleDown || $[4] !== shiftRuleUp || $[5] !== toggleLockRule) {
    t0 = jsx7("div", { ref: r.dndRef, "data-testid": TestID.rule, "data-dragmonitorid": r.dragMonitorId, "data-dropmonitorid": r.dropMonitorId, className: r.outerClassName, "data-rule-id": r.id, "data-level": r.path.length, "data-path": JSON.stringify(r.path), children: jsx7(RuleComponents, { ...r, cloneRule, toggleLockRule, removeRule, shiftRuleUp, shiftRuleDown }) });
    $[0] = cloneRule;
    $[1] = r;
    $[2] = removeRule;
    $[3] = shiftRuleDown;
    $[4] = shiftRuleUp;
    $[5] = toggleLockRule;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  return t0;
});
var RuleComponents = /* @__PURE__ */ React3.memo(function RuleComponents2(r) {
  const { schema: { controls: { shiftActions: ShiftActionsControlElement, dragHandle: DragHandleControlElement, fieldSelector: FieldSelectorControlElement, operatorSelector: OperatorSelectorControlElement, valueSourceSelector: ValueSourceSelectorControlElement, valueEditor: ValueEditorControlElement, cloneRuleAction: CloneRuleActionControlElement, lockRuleAction: LockRuleActionControlElement, removeRuleAction: RemoveRuleActionControlElement } } } = r;
  return /* @__PURE__ */ jsxs2(Fragment, { children: [r.schema.showShiftActions && /* @__PURE__ */ jsx7(ShiftActionsControlElement, { testID: TestID.shiftActions, level: r.path.length, path: r.path, titles: { shiftUp: r.translations.shiftActionUp.title, shiftDown: r.translations.shiftActionDown.title }, labels: { shiftUp: r.translations.shiftActionUp.label, shiftDown: r.translations.shiftActionDown.label }, className: r.classNames.shiftActions, disabled: r.disabled, shiftUp: r.shiftRuleUp, shiftDown: r.shiftRuleDown, shiftUpDisabled: r.shiftUpDisabled, shiftDownDisabled: r.shiftDownDisabled, context: r.context, validation: r.validationResult, schema: r.schema, ruleOrGroup: r.rule }, TestID.shiftActions), r.schema.enableDragAndDrop && /* @__PURE__ */ jsx7(DragHandleControlElement, { testID: TestID.dragHandle, ref: r.dragRef, level: r.path.length, path: r.path, title: r.translations.dragHandle.title, label: r.translations.dragHandle.label, className: r.classNames.dragHandle, disabled: r.disabled, context: r.context, validation: r.validationResult, schema: r.schema, ruleOrGroup: r.rule }, TestID.dragHandle), /* @__PURE__ */ jsx7(FieldSelectorControlElement, { testID: TestID.fields, options: r.schema.fields, title: r.translations.fields.title, value: r.rule.field, operator: r.rule.operator, className: r.classNames.fields, handleOnChange: r.onChangeField, level: r.path.length, path: r.path, disabled: r.disabled, context: r.context, validation: r.validationResult, schema: r.schema, rule: r.rule }, TestID.fields), (r.schema.autoSelectField || r.rule.field !== r.translations.fields.placeholderName) && /* @__PURE__ */ jsxs2(Fragment, { children: [/* @__PURE__ */ jsx7(OperatorSelectorControlElement, { testID: TestID.operators, field: r.rule.field, fieldData: r.fieldData, title: r.translations.operators.title, options: r.operators, value: r.rule.operator, className: r.classNames.operators, handleOnChange: r.onChangeOperator, level: r.path.length, path: r.path, disabled: r.disabled, context: r.context, validation: r.validationResult, schema: r.schema, rule: r.rule }, TestID.operators), (r.schema.autoSelectOperator || r.rule.operator !== r.translations.operators.placeholderName) && !r.hideValueControls && /* @__PURE__ */ jsxs2(Fragment, { children: [!["null", "notNull"].includes(r.rule.operator) && r.valueSources.length > 1 && /* @__PURE__ */ jsx7(ValueSourceSelectorControlElement, { testID: TestID.valueSourceSelector, field: r.rule.field, fieldData: r.fieldData, title: r.translations.valueSourceSelector.title, options: r.valueSourceOptions, value: r.rule.valueSource ?? "value", className: r.classNames.valueSource, handleOnChange: r.onChangeValueSource, level: r.path.length, path: r.path, disabled: r.disabled, context: r.context, validation: r.validationResult, schema: r.schema, rule: r.rule }, TestID.valueSourceSelector), /* @__PURE__ */ jsx7(ValueEditorControlElement, { testID: TestID.valueEditor, field: r.rule.field, fieldData: r.fieldData, title: r.translations.value.title, operator: r.rule.operator, value: r.rule.value, valueSource: r.rule.valueSource ?? "value", type: r.valueEditorType, inputType: r.inputType, values: r.values, listsAsArrays: r.schema.listsAsArrays, parseNumbers: r.schema.parseNumbers, separator: r.valueEditorSeparator, className: r.classNames.value, handleOnChange: r.onChangeValue, level: r.path.length, path: r.path, disabled: r.disabled, context: r.context, validation: r.validationResult, schema: r.schema, rule: r.rule }, TestID.valueEditor)] })] }), r.schema.showCloneButtons && /* @__PURE__ */ jsx7(CloneRuleActionControlElement, { testID: TestID.cloneRule, label: r.translations.cloneRule.label, title: r.translations.cloneRule.title, className: r.classNames.cloneRule, handleOnClick: r.cloneRule, level: r.path.length, path: r.path, disabled: r.disabled, context: r.context, validation: r.validationResult, ruleOrGroup: r.rule, schema: r.schema }, TestID.cloneRule), r.schema.showLockButtons && /* @__PURE__ */ jsx7(LockRuleActionControlElement, { testID: TestID.lockRule, label: r.translations.lockRule.label, title: r.translations.lockRule.title, className: r.classNames.lockRule, handleOnClick: r.toggleLockRule, level: r.path.length, path: r.path, disabled: r.disabled, disabledTranslation: r.parentDisabled ? void 0 : r.translations.lockRuleDisabled, context: r.context, validation: r.validationResult, ruleOrGroup: r.rule, schema: r.schema }, TestID.lockRule), /* @__PURE__ */ jsx7(RemoveRuleActionControlElement, { testID: TestID.removeRule, label: r.translations.removeRule.label, title: r.translations.removeRule.title, className: r.classNames.removeRule, handleOnClick: r.removeRule, level: r.path.length, path: r.path, disabled: r.disabled, context: r.context, validation: r.validationResult, ruleOrGroup: r.rule, schema: r.schema }, TestID.removeRule)] });
});
var useRule = (props) => {
  const {
    id,
    path,
    rule: ruleProp,
    schema: { classNames: classNamesProp, fields, fieldMap, getInputType, getOperators, getValueEditorType, getValueEditorSeparator, getValueSources, getValues, validationMap, enableDragAndDrop, getRuleClassname, suppressStandardClassnames },
    actions: { moveRule, onPropChange, onRuleRemove },
    disabled: disabledProp,
    parentDisabled,
    shiftUpDisabled,
    shiftDownDisabled,
    field: fieldProp,
    operator: operatorProp,
    value: valueProp,
    valueSource: valueSourceProp,
    // Drag-and-drop
    dropEffect = "move",
    groupItems = false,
    dragMonitorId = "",
    dropMonitorId = "",
    dndRef = null,
    dragRef = null,
    isDragging = false,
    isOver = false
  } = props;
  useDeprecatedProps("rule", !ruleProp);
  useReactDndWarning(enableDragAndDrop, !!(dragMonitorId || dropMonitorId || dndRef || dragRef));
  const disabled = !!parentDisabled || !!disabledProp;
  const rule = useMemo2(() => ruleProp ?? { id, field: fieldProp ?? /* istanbul ignore next */
  "", operator: operatorProp ?? /* istanbul ignore next */
  "", value: valueProp, valueSource: valueSourceProp }, [fieldProp, id, operatorProp, ruleProp, valueProp, valueSourceProp]);
  const classNames = useMemo2(() => ({ shiftActions: clsx(suppressStandardClassnames || standardClassnames.shiftActions, classNamesProp.shiftActions), dragHandle: clsx(suppressStandardClassnames || standardClassnames.dragHandle, classNamesProp.dragHandle), fields: clsx(suppressStandardClassnames || standardClassnames.fields, classNamesProp.valueSelector, classNamesProp.fields), operators: clsx(suppressStandardClassnames || standardClassnames.operators, classNamesProp.valueSelector, classNamesProp.operators), valueSource: clsx(suppressStandardClassnames || standardClassnames.valueSource, classNamesProp.valueSelector, classNamesProp.valueSource), value: clsx(suppressStandardClassnames || standardClassnames.value, classNamesProp.value), cloneRule: clsx(suppressStandardClassnames || standardClassnames.cloneRule, classNamesProp.actionElement, classNamesProp.cloneRule), lockRule: clsx(suppressStandardClassnames || standardClassnames.lockRule, classNamesProp.actionElement, classNamesProp.lockRule), removeRule: clsx(suppressStandardClassnames || standardClassnames.removeRule, classNamesProp.actionElement, classNamesProp.removeRule), valueListItem: clsx(suppressStandardClassnames || standardClassnames.valueListItem, classNamesProp.valueListItem) }), [classNamesProp.shiftActions, classNamesProp.dragHandle, classNamesProp.valueSelector, classNamesProp.fields, classNamesProp.operators, classNamesProp.valueSource, classNamesProp.value, classNamesProp.actionElement, classNamesProp.cloneRule, classNamesProp.lockRule, classNamesProp.removeRule, classNamesProp.valueListItem, suppressStandardClassnames]);
  const getChangeHandler = useCallback2(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (prop) => (value, context) => {
      if (!disabled) {
        onPropChange(prop, value, path, context);
      }
    },
    [disabled, onPropChange, path]
  );
  const onChangeField = useMemo2(() => getChangeHandler("field"), [getChangeHandler]);
  const onChangeOperator = useMemo2(() => getChangeHandler("operator"), [getChangeHandler]);
  const onChangeValueSource = useMemo2(() => getChangeHandler("valueSource"), [getChangeHandler]);
  const onChangeValue = useMemo2(() => getChangeHandler("value"), [getChangeHandler]);
  const cloneRule = useCallback2((_event, context_0) => {
    if (!disabled) {
      const newPath = [...getParentPath(path), path.at(-1) + 1];
      moveRule(path, newPath, true, context_0);
    }
  }, [disabled, moveRule, path]);
  const toggleLockRule = useCallback2((_event_0, context_1) => onPropChange("disabled", !disabled, path, context_1), [disabled, onPropChange, path]);
  const removeRule = useCallback2((_event_1, _context) => {
    if (!disabled) {
      onRuleRemove(path);
    }
  }, [disabled, onRuleRemove, path]);
  const shiftRuleUp = useCallback2((event, context_2) => {
    if (!disabled && !shiftUpDisabled) {
      moveRule(path, "up", event?.altKey, context_2);
    }
  }, [disabled, moveRule, path, shiftUpDisabled]);
  const shiftRuleDown = useCallback2((event_0, context_3) => {
    if (!disabled && !shiftDownDisabled) {
      moveRule(path, "down", event_0?.altKey, context_3);
    }
  }, [disabled, moveRule, path, shiftDownDisabled]);
  const fieldData = useMemo2(() => fieldMap?.[rule.field] ?? { name: rule.field, value: rule.field, label: rule.field }, [fieldMap, rule.field]);
  const inputType = useMemo2(() => fieldData.inputType ?? getInputType(rule.field, rule.operator, { fieldData }), [fieldData, getInputType, rule.field, rule.operator]);
  const operators = useMemo2(() => getOperators(rule.field, { fieldData }), [fieldData, getOperators, rule.field]);
  const operatorObject = useMemo2(() => getOption(operators, rule.operator), [operators, rule.operator]);
  const arity = operatorObject?.arity;
  const hideValueControls = typeof arity === "string" && arity === "unary" || typeof arity === "number" && arity < 2;
  const valueSources = useMemo2(() => typeof fieldData.valueSources === "function" ? fieldData.valueSources(rule.operator) : fieldData.valueSources ?? getValueSources(rule.field, rule.operator, { fieldData }), [fieldData, getValueSources, rule.field, rule.operator]);
  const valueEditorType = useMemo2(() => rule.valueSource === "field" ? "select" : getValueEditorType(rule.field, rule.operator, { fieldData }), [fieldData, getValueEditorType, rule.field, rule.operator, rule.valueSource]);
  const valueEditorSeparator = useMemo2(() => getValueEditorSeparator(rule.field, rule.operator, { fieldData }), [fieldData, getValueEditorSeparator, rule.field, rule.operator]);
  const values = useMemo2(() => {
    const v = rule.valueSource === "field" ? filterFieldsByComparator(fieldData, fields, rule.operator) : fieldData.values ?? getValues(rule.field, rule.operator, { fieldData });
    return isFlexibleOptionArray(v) || isFlexibleOptionGroupArray(v) ? toFullOptionList(v) : v;
  }, [fieldData, fields, getValues, rule.field, rule.operator, rule.valueSource]);
  const valueSourceOptions = useMemo2(() => valueSources.map((vs) => ({ name: vs, value: vs, label: vs })), [valueSources]);
  const validationResult = useMemo2(() => validationMap[id ?? /* istanbul ignore next */
  ""] ?? (typeof fieldData.validator === "function" ? fieldData.validator(rule) : null), [fieldData, id, rule, validationMap]);
  const validationClassName = useMemo2(() => getValidationClassNames(validationResult), [validationResult]);
  const fieldBasedClassName = useMemo2(() => fieldData?.className ?? "", [fieldData?.className]);
  const operatorBasedClassName = useMemo2(() => operatorObject?.className ?? "", [operatorObject?.className]);
  const outerClassName = useMemo2(() => clsx(
    getRuleClassname(rule, { fieldData }),
    fieldBasedClassName,
    operatorBasedClassName,
    suppressStandardClassnames || standardClassnames.rule,
    classNamesProp.rule,
    // custom conditional classes
    disabled && classNamesProp.disabled,
    isDragging && classNamesProp.dndDragging,
    isOver && classNamesProp.dndOver,
    isOver && dropEffect === "copy" && classNamesProp.dndCopy,
    isOver && groupItems && classNamesProp.dndGroup,
    // standard conditional classes
    suppressStandardClassnames || { [standardClassnames.disabled]: disabled, [standardClassnames.dndDragging]: isDragging, [standardClassnames.dndOver]: isOver, [standardClassnames.dndCopy]: isOver && dropEffect === "copy", [standardClassnames.dndGroup]: isOver && groupItems },
    validationClassName
  ), [classNamesProp.disabled, classNamesProp.dndCopy, classNamesProp.dndDragging, classNamesProp.dndGroup, classNamesProp.dndOver, classNamesProp.rule, disabled, dropEffect, fieldBasedClassName, fieldData, getRuleClassname, groupItems, isDragging, isOver, operatorBasedClassName, rule, suppressStandardClassnames, validationClassName]);
  return { ...props, classNames, cloneRule, disabled, dndRef, dragMonitorId, dragRef, dropMonitorId, fieldData, generateOnChangeHandler: getChangeHandler, onChangeField, onChangeOperator, onChangeValueSource, onChangeValue, hideValueControls, inputType, operators, outerClassName, removeRule, rule, shiftRuleUp, shiftRuleDown, toggleLockRule, validationResult, valueEditorSeparator, valueEditorType, values, valueSourceOptions, valueSources };
};

// src/components/RuleGroup.tsx
import { Fragment as Fragment2, jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
import * as React4 from "react";
import { Fragment as Fragment22, useCallback as useCallback3, useMemo as useMemo3 } from "react";
var RuleGroup = /* @__PURE__ */ React4.memo(function RuleGroup2(props) {
  const $ = c(11);
  const rg = useRuleGroup(props);
  const { schema: t0 } = rg;
  const { controls: t1 } = t0;
  const { ruleGroupBodyElements: RuleGroupBodyElements, ruleGroupHeaderElements: RuleGroupHeaderElements } = t1;
  const addRule = useStopEventPropagation(rg.addRule);
  const addGroup = useStopEventPropagation(rg.addGroup);
  const cloneGroup = useStopEventPropagation(rg.cloneGroup);
  const toggleLockGroup = useStopEventPropagation(rg.toggleLockGroup);
  const removeGroup = useStopEventPropagation(rg.removeGroup);
  const shiftGroupUp = useStopEventPropagation(rg.shiftGroupUp);
  const shiftGroupDown = useStopEventPropagation(rg.shiftGroupDown);
  let t2;
  if ($[0] !== RuleGroupBodyElements || $[1] !== RuleGroupHeaderElements || $[2] !== addGroup || $[3] !== addRule || $[4] !== cloneGroup || $[5] !== removeGroup || $[6] !== rg || $[7] !== shiftGroupDown || $[8] !== shiftGroupUp || $[9] !== toggleLockGroup) {
    t2 = jsxs3("div", { ref: rg.previewRef, title: rg.accessibleDescription, className: rg.outerClassName, "data-testid": TestID.ruleGroup, "data-dragmonitorid": rg.dragMonitorId, "data-dropmonitorid": rg.dropMonitorId, "data-rule-group-id": rg.id, "data-level": rg.path.length, "data-path": JSON.stringify(rg.path), children: [jsx8("div", { ref: rg.dropRef, className: rg.classNames.header, children: jsx8(RuleGroupHeaderElements, { ...rg, addRule, addGroup, cloneGroup, toggleLockGroup, removeGroup, shiftGroupUp, shiftGroupDown }) }), jsx8("div", { className: rg.classNames.body, children: jsx8(RuleGroupBodyElements, { ...rg, addRule, addGroup, cloneGroup, toggleLockGroup, removeGroup, shiftGroupUp, shiftGroupDown }) })] });
    $[0] = RuleGroupBodyElements;
    $[1] = RuleGroupHeaderElements;
    $[2] = addGroup;
    $[3] = addRule;
    $[4] = cloneGroup;
    $[5] = removeGroup;
    $[6] = rg;
    $[7] = shiftGroupDown;
    $[8] = shiftGroupUp;
    $[9] = toggleLockGroup;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
var RuleGroupHeaderComponents = /* @__PURE__ */ React4.memo(function RuleGroupHeaderComponents2(rg) {
  const { schema: { controls: { shiftActions: ShiftActionsControlElement, dragHandle: DragHandleControlElement, combinatorSelector: CombinatorSelectorControlElement, notToggle: NotToggleControlElement, addRuleAction: AddRuleActionControlElement, addGroupAction: AddGroupActionControlElement, cloneGroupAction: CloneGroupActionControlElement, lockGroupAction: LockGroupActionControlElement, removeGroupAction: RemoveGroupActionControlElement } } } = rg;
  return /* @__PURE__ */ jsxs3(Fragment2, { children: [rg.schema.showShiftActions && rg.path.length > 0 && /* @__PURE__ */ jsx8(ShiftActionsControlElement, { testID: TestID.shiftActions, level: rg.path.length, path: rg.path, titles: { shiftUp: rg.translations.shiftActionUp.title, shiftDown: rg.translations.shiftActionDown.title }, labels: { shiftUp: rg.translations.shiftActionUp.label, shiftDown: rg.translations.shiftActionDown.label }, className: rg.classNames.shiftActions, disabled: rg.disabled, shiftUp: rg.shiftGroupUp, shiftDown: rg.shiftGroupDown, shiftUpDisabled: rg.shiftUpDisabled, shiftDownDisabled: rg.shiftDownDisabled, context: rg.context, validation: rg.validationResult, schema: rg.schema, ruleOrGroup: rg.ruleGroup }, TestID.shiftActions), rg.path.length > 0 && rg.schema.enableDragAndDrop && /* @__PURE__ */ jsx8(DragHandleControlElement, { testID: TestID.dragHandle, ref: rg.dragRef, level: rg.path.length, path: rg.path, title: rg.translations.dragHandle.title, label: rg.translations.dragHandle.label, className: rg.classNames.dragHandle, disabled: rg.disabled, context: rg.context, validation: rg.validationResult, schema: rg.schema, ruleOrGroup: rg.ruleGroup }, TestID.dragHandle), !rg.schema.showCombinatorsBetweenRules && !rg.schema.independentCombinators && /* @__PURE__ */ jsx8(CombinatorSelectorControlElement, { testID: TestID.combinators, options: rg.schema.combinators, value: rg.combinator, title: rg.translations.combinators.title, className: rg.classNames.combinators, handleOnChange: rg.onCombinatorChange, rules: rg.ruleGroup.rules, level: rg.path.length, path: rg.path, disabled: rg.disabled, context: rg.context, validation: rg.validationResult, schema: rg.schema }, TestID.combinators), rg.schema.showNotToggle && /* @__PURE__ */ jsx8(NotToggleControlElement, { testID: TestID.notToggle, className: rg.classNames.notToggle, title: rg.translations.notToggle.title, label: rg.translations.notToggle.label, checked: rg.ruleGroup.not, handleOnChange: rg.onNotToggleChange, level: rg.path.length, disabled: rg.disabled, path: rg.path, context: rg.context, validation: rg.validationResult, schema: rg.schema, ruleGroup: rg.ruleGroup }, TestID.notToggle), /* @__PURE__ */ jsx8(AddRuleActionControlElement, { testID: TestID.addRule, label: rg.translations.addRule.label, title: rg.translations.addRule.title, className: rg.classNames.addRule, handleOnClick: rg.addRule, rules: rg.ruleGroup.rules, level: rg.path.length, path: rg.path, disabled: rg.disabled, context: rg.context, validation: rg.validationResult, ruleOrGroup: rg.ruleGroup, schema: rg.schema }, TestID.addRule), /* @__PURE__ */ jsx8(AddGroupActionControlElement, { testID: TestID.addGroup, label: rg.translations.addGroup.label, title: rg.translations.addGroup.title, className: rg.classNames.addGroup, handleOnClick: rg.addGroup, rules: rg.ruleGroup.rules, level: rg.path.length, path: rg.path, disabled: rg.disabled, context: rg.context, validation: rg.validationResult, ruleOrGroup: rg.ruleGroup, schema: rg.schema }, TestID.addGroup), rg.schema.showCloneButtons && rg.path.length > 0 && /* @__PURE__ */ jsx8(CloneGroupActionControlElement, { testID: TestID.cloneGroup, label: rg.translations.cloneRuleGroup.label, title: rg.translations.cloneRuleGroup.title, className: rg.classNames.cloneGroup, handleOnClick: rg.cloneGroup, rules: rg.ruleGroup.rules, level: rg.path.length, path: rg.path, disabled: rg.disabled, context: rg.context, validation: rg.validationResult, ruleOrGroup: rg.ruleGroup, schema: rg.schema }, TestID.cloneGroup), rg.schema.showLockButtons && /* @__PURE__ */ jsx8(LockGroupActionControlElement, { testID: TestID.lockGroup, label: rg.translations.lockGroup.label, title: rg.translations.lockGroup.title, className: rg.classNames.lockGroup, handleOnClick: rg.toggleLockGroup, rules: rg.ruleGroup.rules, level: rg.path.length, path: rg.path, disabled: rg.disabled, disabledTranslation: rg.parentDisabled ? void 0 : rg.translations.lockGroupDisabled, context: rg.context, validation: rg.validationResult, ruleOrGroup: rg.ruleGroup, schema: rg.schema }, TestID.lockGroup), rg.path.length > 0 && /* @__PURE__ */ jsx8(RemoveGroupActionControlElement, { testID: TestID.removeGroup, label: rg.translations.removeGroup.label, title: rg.translations.removeGroup.title, className: rg.classNames.removeGroup, handleOnClick: rg.removeGroup, rules: rg.ruleGroup.rules, level: rg.path.length, path: rg.path, disabled: rg.disabled, context: rg.context, validation: rg.validationResult, ruleOrGroup: rg.ruleGroup, schema: rg.schema }, TestID.removeGroup)] });
});
var RuleGroupBodyComponents = /* @__PURE__ */ React4.memo(function RuleGroupBodyComponents2(rg) {
  const { schema: { controls: { combinatorSelector: CombinatorSelectorControlElement, inlineCombinator: InlineCombinatorControlElement, ruleGroup: RuleGroupControlElement, rule: RuleControlElement } } } = rg;
  return /* @__PURE__ */ jsx8(Fragment2, { children: rg.ruleGroup.rules.map((r, idx, _ref) => {
    let { length: ruleArrayLength } = _ref;
    const thisPathMemo = rg.pathsMemo[idx];
    const thisPath = thisPathMemo.path;
    const thisPathDisabled = thisPathMemo.disabled || typeof r !== "string" && r.disabled;
    const shiftUpDisabled = pathsAreEqual([0], thisPath);
    const shiftDownDisabled = rg.path.length === 0 && idx === ruleArrayLength - 1;
    const key = typeof r === "string" ? [...thisPath, r].join("-") : r.id;
    return /* @__PURE__ */ jsxs3(Fragment22, { children: [idx > 0 && !rg.schema.independentCombinators && rg.schema.showCombinatorsBetweenRules && /* @__PURE__ */ jsx8(InlineCombinatorControlElement, { options: rg.schema.combinators, value: rg.combinator, title: rg.translations.combinators.title, className: rg.classNames.combinators, handleOnChange: rg.onCombinatorChange, rules: rg.ruleGroup.rules, level: rg.path.length, context: rg.context, validation: rg.validationResult, component: CombinatorSelectorControlElement, path: thisPath, disabled: rg.disabled, schema: rg.schema }, TestID.inlineCombinator), typeof r === "string" ? /* @__PURE__ */ jsx8(InlineCombinatorControlElement, { options: rg.schema.combinators, value: r, title: rg.translations.combinators.title, className: rg.classNames.combinators, handleOnChange: (val) => rg.onIndependentCombinatorChange(val, idx), rules: rg.ruleGroup.rules, level: rg.path.length, context: rg.context, validation: rg.validationResult, component: CombinatorSelectorControlElement, path: thisPath, disabled: thisPathDisabled, schema: rg.schema }, `${TestID.inlineCombinator}-independent`) : isRuleGroup(r) ? /* @__PURE__ */ jsx8(RuleGroupControlElement, { id: r.id, schema: rg.schema, actions: rg.actions, path: thisPath, translations: rg.translations, ruleGroup: r, rules: r.rules, combinator: isRuleGroupType(r) ? r.combinator : void 0, not: !!r.not, disabled: thisPathDisabled, parentDisabled: rg.parentDisabled || rg.disabled, shiftUpDisabled, shiftDownDisabled, context: rg.context }, TestID.ruleGroup) : /* @__PURE__ */ jsx8(RuleControlElement, { id: r.id, rule: r, field: r.field, operator: r.operator, value: r.value, valueSource: r.valueSource, schema: rg.schema, actions: rg.actions, path: thisPath, disabled: thisPathDisabled, parentDisabled: rg.parentDisabled || rg.disabled, translations: rg.translations, shiftUpDisabled, shiftDownDisabled, context: rg.context }, TestID.rule)] }, key);
  }) });
});
var useRuleGroup = (props) => {
  const {
    id,
    path,
    ruleGroup: ruleGroupProp,
    schema: { qbId, accessibleDescriptionGenerator, classNames: classNamesProp, combinators, createRule, createRuleGroup, disabledPaths, independentCombinators, validationMap, enableDragAndDrop, getRuleGroupClassname, suppressStandardClassnames },
    actions: { onGroupAdd, onGroupRemove, onPropChange, onRuleAdd, moveRule },
    disabled: disabledProp,
    parentDisabled,
    shiftUpDisabled,
    shiftDownDisabled,
    combinator: combinatorProp,
    rules: rulesProp,
    not: notProp,
    // Drag-and-drop
    dropEffect = "move",
    groupItems = false,
    dragMonitorId = "",
    dropMonitorId = "",
    previewRef = null,
    dragRef = null,
    dropRef = null,
    isDragging = false,
    isOver = false
  } = props;
  useDeprecatedProps("ruleGroup", !ruleGroupProp);
  useReactDndWarning(enableDragAndDrop, !!(dragMonitorId || dropMonitorId || previewRef || dragRef || dropRef));
  const disabled = !!parentDisabled || !!disabledProp;
  const combinator = useMemo3(() => ruleGroupProp && isRuleGroupType(ruleGroupProp) ? ruleGroupProp.combinator : ruleGroupProp ? getFirstOption(combinators) : combinatorProp ?? getFirstOption(combinators), [combinatorProp, combinators, ruleGroupProp]);
  const ruleGroup = useMemo3(() => {
    if (ruleGroupProp) {
      if (ruleGroupProp.combinator === combinator || independentCombinators) {
        return ruleGroupProp;
      }
      const newRG = structuredClone(ruleGroupProp);
      newRG.combinator = combinator;
      return newRG;
    }
    return { rules: rulesProp, not: notProp };
  }, [combinator, independentCombinators, notProp, ruleGroupProp, rulesProp]);
  const classNames = useMemo3(() => ({ header: clsx(suppressStandardClassnames || standardClassnames.header, classNamesProp.header, isOver && dropEffect === "copy" && classNamesProp.dndCopy, suppressStandardClassnames || { [standardClassnames.dndOver]: isOver, [standardClassnames.dndCopy]: isOver && dropEffect === "copy" }), shiftActions: clsx(suppressStandardClassnames || standardClassnames.shiftActions, classNamesProp.shiftActions), dragHandle: clsx(suppressStandardClassnames || standardClassnames.dragHandle, classNamesProp.dragHandle), combinators: clsx(suppressStandardClassnames || standardClassnames.combinators, classNamesProp.valueSelector, classNamesProp.combinators), notToggle: clsx(suppressStandardClassnames || standardClassnames.notToggle, classNamesProp.notToggle), addRule: clsx(suppressStandardClassnames || standardClassnames.addRule, classNamesProp.actionElement, classNamesProp.addRule), addGroup: clsx(suppressStandardClassnames || standardClassnames.addGroup, classNamesProp.actionElement, classNamesProp.addGroup), cloneGroup: clsx(suppressStandardClassnames || standardClassnames.cloneGroup, classNamesProp.actionElement, classNamesProp.cloneGroup), lockGroup: clsx(suppressStandardClassnames || standardClassnames.lockGroup, classNamesProp.actionElement, classNamesProp.lockGroup), removeGroup: clsx(suppressStandardClassnames || standardClassnames.removeGroup, classNamesProp.actionElement, classNamesProp.removeGroup), body: clsx(suppressStandardClassnames || standardClassnames.body, classNamesProp.body) }), [classNamesProp.actionElement, classNamesProp.addGroup, classNamesProp.addRule, classNamesProp.body, classNamesProp.cloneGroup, classNamesProp.combinators, classNamesProp.dndCopy, classNamesProp.dragHandle, classNamesProp.header, classNamesProp.lockGroup, classNamesProp.notToggle, classNamesProp.removeGroup, classNamesProp.shiftActions, classNamesProp.valueSelector, dropEffect, isOver, suppressStandardClassnames]);
  const onCombinatorChange = useCallback3((value) => {
    if (!disabled) {
      onPropChange("combinator", value, path);
    }
  }, [disabled, onPropChange, path]);
  const onIndependentCombinatorChange = useCallback3(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value_0, index, _context) => {
      if (!disabled) {
        onPropChange("combinator", value_0, [...path, index]);
      }
    },
    [disabled, onPropChange, path]
  );
  const onNotToggleChange = useCallback3(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (checked, _context_0) => {
      if (!disabled) {
        onPropChange("not", checked, path);
      }
    },
    [disabled, onPropChange, path]
  );
  const addRule = useCallback3((_e, context) => {
    if (!disabled) {
      const newRule = createRule();
      onRuleAdd(newRule, path, context);
    }
  }, [createRule, disabled, onRuleAdd, path]);
  const addGroup = useCallback3((_e_0, context_0) => {
    if (!disabled) {
      const newGroup = createRuleGroup(independentCombinators);
      onGroupAdd(newGroup, path, context_0);
    }
  }, [createRuleGroup, disabled, independentCombinators, onGroupAdd, path]);
  const cloneGroup = useCallback3(() => {
    if (!disabled) {
      const newPath = [...getParentPath(path), path.at(-1) + 1];
      moveRule(path, newPath, true);
    }
  }, [disabled, moveRule, path]);
  const shiftGroupUp = useCallback3(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event, _context_1) => {
      if (!disabled && !shiftUpDisabled) {
        moveRule(path, "up", event?.altKey);
      }
    },
    [disabled, moveRule, path, shiftUpDisabled]
  );
  const shiftGroupDown = useCallback3(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event_0, _context_2) => {
      if (!disabled && !shiftDownDisabled) {
        moveRule(path, "down", event_0?.altKey);
      }
    },
    [disabled, moveRule, path, shiftDownDisabled]
  );
  const toggleLockGroup = useCallback3(() => {
    onPropChange("disabled", !disabled, path);
  }, [disabled, onPropChange, path]);
  const removeGroup = useCallback3(() => {
    if (!disabled) {
      onGroupRemove(path);
    }
  }, [disabled, onGroupRemove, path]);
  const validationResult = useMemo3(() => validationMap[id ?? /* istanbul ignore next */
  ""], [id, validationMap]);
  const validationClassName = useMemo3(() => getValidationClassNames(validationResult), [validationResult]);
  const combinatorBasedClassName = useMemo3(() => independentCombinators ? null : getOption(combinators, combinator)?.className ?? "", [combinator, combinators, independentCombinators]);
  const ruleGroupClassname = useMemo3(() => getRuleGroupClassname(ruleGroup), [getRuleGroupClassname, ruleGroup]);
  const outerClassName = useMemo3(() => clsx(ruleGroupClassname, combinatorBasedClassName, suppressStandardClassnames || standardClassnames.ruleGroup, classNamesProp.ruleGroup, disabled && classNamesProp.disabled, isDragging && classNamesProp.dndDragging, isOver && groupItems && classNamesProp.dndGroup, suppressStandardClassnames || { [standardClassnames.disabled]: disabled, [standardClassnames.dndDragging]: isDragging, [standardClassnames.dndGroup]: isOver && groupItems }, validationClassName), [classNamesProp.disabled, classNamesProp.dndDragging, classNamesProp.dndGroup, classNamesProp.ruleGroup, combinatorBasedClassName, disabled, groupItems, isDragging, isOver, ruleGroupClassname, suppressStandardClassnames, validationClassName]);
  const pathsMemo = useMemo3(() => {
    const paths = [];
    for (let i = 0; i < ruleGroup.rules.length; i++) {
      const thisPath = [...path, i];
      paths[i] = { path: thisPath, disabled: disabled || disabledPaths.some((p) => pathsAreEqual(thisPath, p)) };
    }
    return paths;
  }, [disabled, path, ruleGroup.rules.length, disabledPaths]);
  const accessibleDescription = useMemo3(() => accessibleDescriptionGenerator({ path, qbId }), [accessibleDescriptionGenerator, path, qbId]);
  return { ...props, addGroup, addRule, accessibleDescription, classNames, cloneGroup, combinator, disabled, dragMonitorId, dragRef, dropMonitorId, dropRef, isDragging, isOver, onCombinatorChange, onGroupAdd, onIndependentCombinatorChange, onNotToggleChange, outerClassName, parentDisabled, pathsMemo, previewRef, removeGroup, ruleGroup, shiftGroupUp, shiftGroupDown, toggleLockGroup, validationClassName, validationResult };
};

// src/components/ShiftActions.tsx
import { jsx as jsx9, jsxs as jsxs4 } from "react/jsx-runtime";
var ShiftActions = (props) => /* @__PURE__ */ jsxs4("div", { "data-testid": props.testID, className: props.className, children: [/* @__PURE__ */ jsx9("button", { disabled: props.disabled || props.shiftUpDisabled, onClick: props.shiftUp, title: props.titles?.shiftUp, children: props.labels?.shiftUp }), /* @__PURE__ */ jsx9("button", { disabled: props.disabled || props.shiftDownDisabled, onClick: props.shiftDown, title: props.titles?.shiftDown, children: props.labels?.shiftDown })] });

// src/components/ValueEditor.tsx
import { jsx as jsx10, jsxs as jsxs5 } from "react/jsx-runtime";
import { produce as produce8 } from "immer";
import { useCallback as useCallback4, useEffect, useMemo as useMemo4 } from "react";
var ValueEditor = (allProps) => {
  const {
    operator,
    value,
    handleOnChange,
    title,
    className,
    type = "text",
    values = [],
    listsAsArrays,
    fieldData,
    disabled,
    separator = null,
    testID,
    selectorComponent: SelectorComponent = allProps.schema.controls.valueSelector,
    // Some value selectors spread all extra props to the rendered component, so
    // we cherry pick these out of `propsForValueSelector` to keep them from being
    // assigned to DOM elements. (The props with mixed case are the only ones that
    // really matter. Props in all lowercase don't emit warnings.)
    inputType: _inputType,
    parseNumbers: _parseNumbers,
    skipHook: _skipHook,
    valueSource: _valueSource,
    ...propsForValueSelector
  } = allProps;
  const { valueAsArray, multiValueHandler, parseNumberMethod, valueListItemClassName, inputTypeCoerced } = useValueEditor(allProps);
  if (operator === "null" || operator === "notNull") {
    return null;
  }
  const placeHolderText = fieldData?.placeholder ?? "";
  if ((operator === "between" || operator === "notBetween") && (type === "select" || type === "text")) {
    const editors = ["from", "to"].map((key, i) => {
      if (type === "text") {
        return /* @__PURE__ */ jsx10("input", { type: inputTypeCoerced, placeholder: placeHolderText, value: valueAsArray[i] ?? "", className: valueListItemClassName, disabled, onChange: (e) => multiValueHandler(e.target.value, i) }, key);
      }
      return /* @__PURE__ */ jsx10(SelectorComponent, { ...propsForValueSelector, schema: allProps.schema, className: valueListItemClassName, handleOnChange: (v) => multiValueHandler(v, i), disabled, value: valueAsArray[i] ?? getFirstOption(values), options: values, listsAsArrays }, key);
    });
    return /* @__PURE__ */ jsxs5("span", { "data-testid": testID, className, title, children: [editors[0], separator, editors[1]] });
  }
  switch (type) {
    case "select":
    case "multiselect":
      return /* @__PURE__ */ jsx10(SelectorComponent, { ...propsForValueSelector, schema: allProps.schema, testID, className, title, handleOnChange, disabled, value, options: values, multiple: type === "multiselect", listsAsArrays });
    case "textarea":
      return /* @__PURE__ */ jsx10("textarea", { "data-testid": testID, placeholder: placeHolderText, value, title, className, disabled, onChange: (e_2) => handleOnChange(e_2.target.value) });
    case "switch":
    case "checkbox":
      return /* @__PURE__ */ jsx10("input", { "data-testid": testID, type: "checkbox", className, title, onChange: (e_1) => handleOnChange(e_1.target.checked), checked: !!value, disabled });
    case "radio":
      return /* @__PURE__ */ jsx10("span", { "data-testid": testID, className, title, children: values.map((v_0) => /* @__PURE__ */ jsxs5("label", { children: [/* @__PURE__ */ jsx10("input", { type: "radio", value: v_0.name, disabled, checked: value === v_0.name, onChange: (e_0) => handleOnChange(e_0.target.value) }), v_0.label] }, v_0.name)) });
  }
  return /* @__PURE__ */ jsx10("input", { "data-testid": testID, type: inputTypeCoerced, placeholder: placeHolderText, value, title, className, disabled, onChange: (e_3) => handleOnChange(parseNumber(e_3.target.value, { parseNumbers: parseNumberMethod })) });
};
var useValueEditor = (props) => {
  const $ = c(29);
  const { handleOnChange, inputType, operator, value, listsAsArrays, parseNumbers, values, type, skipHook, schema: t0 } = props;
  const { classNames: classNamesProp, suppressStandardClassnames } = t0;
  let t1;
  let t2;
  if ($[0] !== handleOnChange || $[1] !== inputType || $[2] !== operator || $[3] !== skipHook || $[4] !== type || $[5] !== value) {
    t1 = () => {
      if (!skipHook && type !== "multiselect" && !["between", "notBetween", "in", "notIn"].includes(operator) && (Array.isArray(value) || inputType === "number" && typeof value === "string" && value.includes(","))) {
        handleOnChange(toArray(value, { retainEmptyStrings: true })[0] ?? "");
      }
    };
    t2 = [handleOnChange, inputType, operator, skipHook, type, value];
    $[0] = handleOnChange;
    $[1] = inputType;
    $[2] = operator;
    $[3] = skipHook;
    $[4] = type;
    $[5] = value;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[8] !== value) {
    t4 = toArray(value, { retainEmptyStrings: true });
    $[8] = value;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  t3 = t4;
  const valueAsArray = t3;
  let t5;
  let t6;
  if ($[10] !== inputType || $[11] !== parseNumbers) {
    t6 = getParseNumberMethod({ parseNumbers, inputType });
    $[10] = inputType;
    $[11] = parseNumbers;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  t5 = t6;
  const parseNumberMethod = t5;
  let t7;
  if ($[13] !== handleOnChange || $[14] !== listsAsArrays || $[15] !== operator || $[16] !== parseNumberMethod || $[17] !== valueAsArray || $[18] !== values) {
    t7 = (val, idx) => {
      const v = produce8(valueAsArray, (va) => {
        va[idx] = parseNumber(val, { parseNumbers: parseNumberMethod });
        if (idx === 0 && (operator === "between" || operator === "notBetween") && (va.length < 2 || va[1] === void 0)) {
          va[1] = getFirstOption(values);
        }
      });
      handleOnChange(listsAsArrays ? v : joinWith(v, ","));
    };
    $[13] = handleOnChange;
    $[14] = listsAsArrays;
    $[15] = operator;
    $[16] = parseNumberMethod;
    $[17] = valueAsArray;
    $[18] = values;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const multiValueHandler = t7;
  const t8 = suppressStandardClassnames || standardClassnames.valueListItem;
  const t9 = classNamesProp?.valueListItem;
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = clsx_default(t8, t9);
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const valueListItemClassName = t10;
  const inputTypeCoerced = operator === "in" || operator === "notIn" ? "text" : inputType || "text";
  let t11;
  if ($[23] !== inputTypeCoerced || $[24] !== multiValueHandler || $[25] !== parseNumberMethod || $[26] !== valueAsArray || $[27] !== valueListItemClassName) {
    t11 = { valueAsArray, multiValueHandler, parseNumberMethod, valueListItemClassName, inputTypeCoerced };
    $[23] = inputTypeCoerced;
    $[24] = multiValueHandler;
    $[25] = parseNumberMethod;
    $[26] = valueAsArray;
    $[27] = valueListItemClassName;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
};

// src/components/ValueSelector.tsx
import { jsx as jsx11 } from "react/jsx-runtime";
import { useCallback as useCallback5, useMemo as useMemo6 } from "react";

// src/hooks/useSelectElementChangeHandler.ts
import { useMemo as useMemo5 } from "react";
var useSelectElementChangeHandler = (params) => {
  const $ = c(3);
  const { multiple, onChange } = params;
  let t0;
  let t1;
  if ($[0] !== multiple || $[1] !== onChange) {
    t1 = multiple ? (e) => onChange(Array.from(e.target.selectedOptions).map(_temp)) : (e_0) => onChange(e_0.target.value);
    $[0] = multiple;
    $[1] = onChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  t0 = t1;
  const selectElementChangeHandler = t0;
  return selectElementChangeHandler;
};
function _temp(o) {
  return o.value;
}

// src/components/ValueSelector.tsx
var ValueSelector = (props) => {
  const $ = c(12);
  const { onChange, val } = useValueSelector(props);
  let t0;
  if ($[0] !== onChange || $[1] !== props.multiple) {
    t0 = { multiple: props.multiple, onChange };
    $[0] = onChange;
    $[1] = props.multiple;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const selectElementChangeHandler = useSelectElementChangeHandler(t0);
  const t1 = !!props.multiple;
  let t2;
  if ($[3] !== props.className || $[4] !== props.disabled || $[5] !== props.options || $[6] !== props.testID || $[7] !== props.title || $[8] !== selectElementChangeHandler || $[9] !== t1 || $[10] !== val) {
    t2 = jsx11("select", { "data-testid": props.testID, className: props.className, value: val, title: props.title, disabled: props.disabled, multiple: t1, onChange: selectElementChangeHandler, children: toOptions(props.options) });
    $[3] = props.className;
    $[4] = props.disabled;
    $[5] = props.options;
    $[6] = props.testID;
    $[7] = props.title;
    $[8] = selectElementChangeHandler;
    $[9] = t1;
    $[10] = val;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
var useValueSelector = (props) => {
  const $ = c(10);
  const { handleOnChange, listsAsArrays: t0, multiple: t1, value } = props;
  const listsAsArrays = t0 === void 0 ? false : t0;
  const multiple = t1 === void 0 ? false : t1;
  let t2;
  if ($[0] !== handleOnChange || $[1] !== listsAsArrays || $[2] !== multiple) {
    t2 = (v) => {
      if (multiple) {
        const valueAsArray = toArray(v);
        handleOnChange(listsAsArrays ? valueAsArray : joinWith(valueAsArray, ","));
      } else {
        handleOnChange(v);
      }
    };
    $[0] = handleOnChange;
    $[1] = listsAsArrays;
    $[2] = multiple;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const onChange = t2;
  let t3;
  let t4;
  if ($[4] !== multiple || $[5] !== value) {
    t4 = multiple ? toArray(value) : value;
    $[4] = multiple;
    $[5] = value;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  t3 = t4;
  const val = t3;
  let t5;
  if ($[7] !== onChange || $[8] !== val) {
    t5 = { onChange, val };
    $[7] = onChange;
    $[8] = val;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};

// src/defaultControlElements.ts
var defaultControlElements = { actionElement: ActionElement, addGroupAction: ActionElement, addRuleAction: ActionElement, cloneGroupAction: ActionElement, cloneRuleAction: ActionElement, combinatorSelector: ValueSelector, dragHandle: DragHandle, fieldSelector: ValueSelector, inlineCombinator: InlineCombinator, lockGroupAction: ActionElement, lockRuleAction: ActionElement, notToggle: NotToggle, operatorSelector: ValueSelector, removeGroupAction: ActionElement, removeRuleAction: ActionElement, rule: Rule, ruleGroup: RuleGroup, ruleGroupBodyElements: RuleGroupBodyComponents, ruleGroupHeaderElements: RuleGroupHeaderComponents, shiftActions: ShiftActions, valueEditor: ValueEditor, valueSelector: ValueSelector, valueSourceSelector: ValueSelector };

// src/hooks/useMergedContext.ts
var nullComp = () => null;
var nullFwdComp = /* @__PURE__ */ forwardRef2(nullComp);
var emptyObject = {};
var useMergedContext = (t0) => {
  const $ = c(185);
  let finalize;
  let props;
  if ($[0] !== t0) {
    ({ finalize, ...props } = t0);
    $[0] = t0;
    $[1] = finalize;
    $[2] = props;
  } else {
    finalize = $[1];
    props = $[2];
  }
  const rqbContext = useContext(QueryBuilderContext);
  const debugModePreferred = usePreferProp(false, props.debugMode, rqbContext.debugMode);
  const debugMode = finalize ? debugModePreferred : props.debugMode ?? rqbContext.debugMode;
  const enableMountQueryChangePreferred = usePreferProp(true, props.enableMountQueryChange, rqbContext.enableMountQueryChange);
  const enableMountQueryChange = finalize ? enableMountQueryChangePreferred : props.enableMountQueryChange ?? rqbContext.enableMountQueryChange;
  const enableDragAndDropPreferred = usePreferProp(false, props.enableDragAndDrop, rqbContext.enableDragAndDrop) && rqbContext.enableDragAndDrop !== false;
  const enableDragAndDrop = finalize ? enableDragAndDropPreferred : props.enableDragAndDrop ?? rqbContext.enableDragAndDrop;
  let t1;
  let t2;
  if ($[3] !== finalize || $[4] !== props.controlClassnames || $[5] !== rqbContext.controlClassnames) {
    t2 = mergeClassnames(finalize ? Object.assign({}, defaultControlClassnames) : emptyObject, rqbContext.controlClassnames, props.controlClassnames);
    $[3] = finalize;
    $[4] = props.controlClassnames;
    $[5] = rqbContext.controlClassnames;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  t1 = t2;
  const cc = t1;
  let t3;
  let t4;
  if ($[7] !== cc.actionElement || $[8] !== cc.addGroup || $[9] !== cc.addRule || $[10] !== cc.betweenRules || $[11] !== cc.body || $[12] !== cc.branches || $[13] !== cc.cloneGroup || $[14] !== cc.cloneRule || $[15] !== cc.combinators || $[16] !== cc.disabled || $[17] !== cc.dndCopy || $[18] !== cc.dndDragging || $[19] !== cc.dndGroup || $[20] !== cc.dndOver || $[21] !== cc.dragHandle || $[22] !== cc.fields || $[23] !== cc.header || $[24] !== cc.invalid || $[25] !== cc.lockGroup || $[26] !== cc.lockRule || $[27] !== cc.notToggle || $[28] !== cc.operators || $[29] !== cc.queryBuilder || $[30] !== cc.removeGroup || $[31] !== cc.removeRule || $[32] !== cc.rule || $[33] !== cc.ruleGroup || $[34] !== cc.shiftActions || $[35] !== cc.valid || $[36] !== cc.value || $[37] !== cc.valueListItem || $[38] !== cc.valueSelector || $[39] !== cc.valueSource) {
    t4 = { actionElement: cc.actionElement, addGroup: cc.addGroup, addRule: cc.addRule, body: cc.body, cloneGroup: cc.cloneGroup, cloneRule: cc.cloneRule, combinators: cc.combinators, dragHandle: cc.dragHandle, fields: cc.fields, header: cc.header, lockGroup: cc.lockGroup, lockRule: cc.lockRule, notToggle: cc.notToggle, operators: cc.operators, queryBuilder: cc.queryBuilder, removeGroup: cc.removeGroup, removeRule: cc.removeRule, rule: cc.rule, ruleGroup: cc.ruleGroup, shiftActions: cc.shiftActions, value: cc.value, valueSelector: cc.valueSelector, valueSource: cc.valueSource, betweenRules: cc.betweenRules, valid: cc.valid, invalid: cc.invalid, dndDragging: cc.dndDragging, dndOver: cc.dndOver, dndCopy: cc.dndCopy, dndGroup: cc.dndGroup, disabled: cc.disabled, valueListItem: cc.valueListItem, branches: cc.branches };
    $[7] = cc.actionElement;
    $[8] = cc.addGroup;
    $[9] = cc.addRule;
    $[10] = cc.betweenRules;
    $[11] = cc.body;
    $[12] = cc.branches;
    $[13] = cc.cloneGroup;
    $[14] = cc.cloneRule;
    $[15] = cc.combinators;
    $[16] = cc.disabled;
    $[17] = cc.dndCopy;
    $[18] = cc.dndDragging;
    $[19] = cc.dndGroup;
    $[20] = cc.dndOver;
    $[21] = cc.dragHandle;
    $[22] = cc.fields;
    $[23] = cc.header;
    $[24] = cc.invalid;
    $[25] = cc.lockGroup;
    $[26] = cc.lockRule;
    $[27] = cc.notToggle;
    $[28] = cc.operators;
    $[29] = cc.queryBuilder;
    $[30] = cc.removeGroup;
    $[31] = cc.removeRule;
    $[32] = cc.rule;
    $[33] = cc.ruleGroup;
    $[34] = cc.shiftActions;
    $[35] = cc.valid;
    $[36] = cc.value;
    $[37] = cc.valueListItem;
    $[38] = cc.valueSelector;
    $[39] = cc.valueSource;
    $[40] = t4;
  } else {
    t4 = $[40];
  }
  t3 = t4;
  const controlClassnames = t3;
  const contextCE = rqbContext.controlElements ?? emptyObject;
  const propsCE = props.controlElements ?? emptyObject;
  let t5;
  if ($[41] !== contextCE.actionElement || $[42] !== contextCE.valueSelector || $[43] !== finalize || $[44] !== propsCE.actionElement || $[45] !== propsCE.valueSelector) {
    t5 = (name, propComp, contextComp) => {
      const nc = name === "dragHandle" ? nullFwdComp : nullComp;
      const propBulkOverride = (name.endsWith("Action") && propsCE.actionElement ? propsCE.actionElement : void 0) ?? (name.endsWith("Selector") && propsCE.valueSelector ? propsCE.valueSelector : void 0);
      const contextBulkOverride = (name.endsWith("Action") && contextCE.actionElement ? contextCE.actionElement : void 0) ?? (name.endsWith("Selector") && contextCE.valueSelector ? contextCE.valueSelector : void 0);
      const comp = propComp === null ? nc : propComp ?? (finalize ? propBulkOverride : void 0) ?? (contextComp === null ? nc : contextComp ?? (finalize ? contextBulkOverride : void 0));
      return comp ? { [name]: comp } : finalize ? { [name]: defaultControlElements[name] } : emptyObject;
    };
    $[41] = contextCE.actionElement;
    $[42] = contextCE.valueSelector;
    $[43] = finalize;
    $[44] = propsCE.actionElement;
    $[45] = propsCE.valueSelector;
    $[46] = t5;
  } else {
    t5 = $[46];
  }
  const mergeControlElement = t5;
  let t6;
  let t7;
  if ($[47] !== contextCE.actionElement || $[48] !== contextCE.addGroupAction || $[49] !== contextCE.addRuleAction || $[50] !== contextCE.cloneGroupAction || $[51] !== contextCE.cloneRuleAction || $[52] !== contextCE.combinatorSelector || $[53] !== contextCE.dragHandle || $[54] !== contextCE.fieldSelector || $[55] !== contextCE.inlineCombinator || $[56] !== contextCE.lockGroupAction || $[57] !== contextCE.lockRuleAction || $[58] !== contextCE.notToggle || $[59] !== contextCE.operatorSelector || $[60] !== contextCE.removeGroupAction || $[61] !== contextCE.removeRuleAction || $[62] !== contextCE.rule || $[63] !== contextCE.ruleGroup || $[64] !== contextCE.ruleGroupBodyElements || $[65] !== contextCE.ruleGroupHeaderElements || $[66] !== contextCE.shiftActions || $[67] !== contextCE.valueEditor || $[68] !== contextCE.valueSelector || $[69] !== contextCE.valueSourceSelector || $[70] !== finalize || $[71] !== mergeControlElement || $[72] !== propsCE.actionElement || $[73] !== propsCE.addGroupAction || $[74] !== propsCE.addRuleAction || $[75] !== propsCE.cloneGroupAction || $[76] !== propsCE.cloneRuleAction || $[77] !== propsCE.combinatorSelector || $[78] !== propsCE.dragHandle || $[79] !== propsCE.fieldSelector || $[80] !== propsCE.inlineCombinator || $[81] !== propsCE.lockGroupAction || $[82] !== propsCE.lockRuleAction || $[83] !== propsCE.notToggle || $[84] !== propsCE.operatorSelector || $[85] !== propsCE.removeGroupAction || $[86] !== propsCE.removeRuleAction || $[87] !== propsCE.rule || $[88] !== propsCE.ruleGroup || $[89] !== propsCE.ruleGroupBodyElements || $[90] !== propsCE.ruleGroupHeaderElements || $[91] !== propsCE.shiftActions || $[92] !== propsCE.valueEditor || $[93] !== propsCE.valueSelector || $[94] !== propsCE.valueSourceSelector) {
    t7 = Object.assign({}, mergeControlElement("addGroupAction", propsCE.addGroupAction, contextCE.addGroupAction), mergeControlElement("addRuleAction", propsCE.addRuleAction, contextCE.addRuleAction), mergeControlElement("cloneGroupAction", propsCE.cloneGroupAction, contextCE.cloneGroupAction), mergeControlElement("cloneRuleAction", propsCE.cloneRuleAction, contextCE.cloneRuleAction), mergeControlElement("combinatorSelector", propsCE.combinatorSelector, contextCE.combinatorSelector), mergeControlElement("dragHandle", propsCE.dragHandle, contextCE.dragHandle), mergeControlElement("fieldSelector", propsCE.fieldSelector, contextCE.fieldSelector), mergeControlElement("inlineCombinator", propsCE.inlineCombinator, contextCE.inlineCombinator), mergeControlElement("lockGroupAction", propsCE.lockGroupAction, contextCE.lockGroupAction), mergeControlElement("lockRuleAction", propsCE.lockRuleAction, contextCE.lockRuleAction), mergeControlElement("notToggle", propsCE.notToggle, contextCE.notToggle), mergeControlElement("operatorSelector", propsCE.operatorSelector, contextCE.operatorSelector), mergeControlElement("removeGroupAction", propsCE.removeGroupAction, contextCE.removeGroupAction), mergeControlElement("removeRuleAction", propsCE.removeRuleAction, contextCE.removeRuleAction), mergeControlElement("shiftActions", propsCE.shiftActions, contextCE.shiftActions), { valueEditor: propsCE.valueEditor === null ? nullComp : propsCE.valueEditor ?? (contextCE.valueEditor === null ? nullComp : contextCE.valueEditor) ?? defaultControlElements.valueEditor }, mergeControlElement("valueSourceSelector", propsCE.valueSourceSelector, contextCE.valueSourceSelector), mergeControlElement("rule", propsCE.rule, contextCE.rule), mergeControlElement("ruleGroup", propsCE.ruleGroup, contextCE.ruleGroup), mergeControlElement("ruleGroupBodyElements", propsCE.ruleGroupBodyElements, contextCE.ruleGroupBodyElements), mergeControlElement("ruleGroupHeaderElements", propsCE.ruleGroupHeaderElements, contextCE.ruleGroupHeaderElements), { actionElement: propsCE.actionElement ?? contextCE.actionElement ?? (finalize ? defaultControlElements.actionElement : void 0) }, { valueSelector: propsCE.valueSelector ?? contextCE.valueSelector ?? (finalize ? defaultControlElements.valueSelector : void 0) });
    $[47] = contextCE.actionElement;
    $[48] = contextCE.addGroupAction;
    $[49] = contextCE.addRuleAction;
    $[50] = contextCE.cloneGroupAction;
    $[51] = contextCE.cloneRuleAction;
    $[52] = contextCE.combinatorSelector;
    $[53] = contextCE.dragHandle;
    $[54] = contextCE.fieldSelector;
    $[55] = contextCE.inlineCombinator;
    $[56] = contextCE.lockGroupAction;
    $[57] = contextCE.lockRuleAction;
    $[58] = contextCE.notToggle;
    $[59] = contextCE.operatorSelector;
    $[60] = contextCE.removeGroupAction;
    $[61] = contextCE.removeRuleAction;
    $[62] = contextCE.rule;
    $[63] = contextCE.ruleGroup;
    $[64] = contextCE.ruleGroupBodyElements;
    $[65] = contextCE.ruleGroupHeaderElements;
    $[66] = contextCE.shiftActions;
    $[67] = contextCE.valueEditor;
    $[68] = contextCE.valueSelector;
    $[69] = contextCE.valueSourceSelector;
    $[70] = finalize;
    $[71] = mergeControlElement;
    $[72] = propsCE.actionElement;
    $[73] = propsCE.addGroupAction;
    $[74] = propsCE.addRuleAction;
    $[75] = propsCE.cloneGroupAction;
    $[76] = propsCE.cloneRuleAction;
    $[77] = propsCE.combinatorSelector;
    $[78] = propsCE.dragHandle;
    $[79] = propsCE.fieldSelector;
    $[80] = propsCE.inlineCombinator;
    $[81] = propsCE.lockGroupAction;
    $[82] = propsCE.lockRuleAction;
    $[83] = propsCE.notToggle;
    $[84] = propsCE.operatorSelector;
    $[85] = propsCE.removeGroupAction;
    $[86] = propsCE.removeRuleAction;
    $[87] = propsCE.rule;
    $[88] = propsCE.ruleGroup;
    $[89] = propsCE.ruleGroupBodyElements;
    $[90] = propsCE.ruleGroupHeaderElements;
    $[91] = propsCE.shiftActions;
    $[92] = propsCE.valueEditor;
    $[93] = propsCE.valueSelector;
    $[94] = propsCE.valueSourceSelector;
    $[95] = t7;
  } else {
    t7 = $[95];
  }
  t6 = t7;
  const controlElements = t6;
  const propsT = props.translations ?? emptyObject;
  const contextT = rqbContext.translations ?? emptyObject;
  propsT.addGroup?.label;
  propsT.addGroup?.title;
  propsT.addRule?.label;
  propsT.addRule?.title;
  propsT.cloneRule?.label;
  propsT.cloneRule?.title;
  propsT.cloneRuleGroup?.label;
  propsT.cloneRuleGroup?.title;
  propsT.combinators?.title;
  propsT.dragHandle?.label;
  propsT.dragHandle?.title;
  propsT.fields?.placeholderGroupLabel;
  propsT.fields?.placeholderLabel;
  propsT.fields?.placeholderName;
  propsT.fields?.title;
  propsT.lockGroup?.label;
  propsT.lockGroup?.title;
  propsT.lockGroupDisabled?.label;
  propsT.lockGroupDisabled?.title;
  propsT.lockRule?.label;
  propsT.lockRule?.title;
  propsT.lockRuleDisabled?.label;
  propsT.lockRuleDisabled?.title;
  propsT.notToggle?.label;
  propsT.notToggle?.title;
  propsT.operators?.placeholderGroupLabel;
  propsT.operators?.placeholderLabel;
  propsT.operators?.placeholderName;
  propsT.operators?.title;
  propsT.removeGroup?.label;
  propsT.removeGroup?.title;
  propsT.removeRule?.label;
  propsT.removeRule?.title;
  propsT.shiftActionDown?.label;
  propsT.shiftActionDown?.title;
  propsT.shiftActionUp?.label;
  propsT.shiftActionUp?.title;
  propsT.value?.title;
  propsT.valueSourceSelector?.title;
  contextT.addGroup?.label;
  contextT.addGroup?.title;
  contextT.addRule?.label;
  contextT.addRule?.title;
  contextT.cloneRule?.label;
  contextT.cloneRule?.title;
  contextT.cloneRuleGroup?.label;
  contextT.cloneRuleGroup?.title;
  contextT.combinators?.title;
  contextT.dragHandle?.label;
  contextT.dragHandle?.title;
  contextT.fields?.placeholderGroupLabel;
  contextT.fields?.placeholderLabel;
  contextT.fields?.placeholderName;
  contextT.fields?.title;
  contextT.lockGroup?.label;
  contextT.lockGroup?.title;
  contextT.lockGroupDisabled?.label;
  contextT.lockGroupDisabled?.title;
  contextT.lockRule?.label;
  contextT.lockRule?.title;
  contextT.lockRuleDisabled?.label;
  contextT.lockRuleDisabled?.title;
  contextT.notToggle?.label;
  contextT.notToggle?.title;
  contextT.operators?.placeholderGroupLabel;
  contextT.operators?.placeholderLabel;
  contextT.operators?.placeholderName;
  contextT.operators?.title;
  contextT.removeGroup?.label;
  contextT.removeGroup?.title;
  contextT.removeRule?.label;
  contextT.removeRule?.title;
  contextT.shiftActionDown?.label;
  contextT.shiftActionDown?.title;
  contextT.shiftActionUp?.label;
  contextT.shiftActionUp?.title;
  contextT.value?.title;
  contextT.valueSourceSelector?.title;
  let t8;
  let t9;
  if ($[96] !== contextT.addGroup?.label || $[97] !== contextT.addGroup?.title || $[98] !== contextT.addRule?.label || $[99] !== contextT.addRule?.title || $[100] !== contextT.cloneRule?.label || $[101] !== contextT.cloneRule?.title || $[102] !== contextT.cloneRuleGroup?.label || $[103] !== contextT.cloneRuleGroup?.title || $[104] !== contextT.combinators?.title || $[105] !== contextT.dragHandle?.label || $[106] !== contextT.dragHandle?.title || $[107] !== contextT.fields?.placeholderGroupLabel || $[108] !== contextT.fields?.placeholderLabel || $[109] !== contextT.fields?.placeholderName || $[110] !== contextT.fields?.title || $[111] !== contextT.lockGroup?.label || $[112] !== contextT.lockGroup?.title || $[113] !== contextT.lockGroupDisabled?.label || $[114] !== contextT.lockGroupDisabled?.title || $[115] !== contextT.lockRule?.label || $[116] !== contextT.lockRule?.title || $[117] !== contextT.lockRuleDisabled?.label || $[118] !== contextT.lockRuleDisabled?.title || $[119] !== contextT.notToggle?.label || $[120] !== contextT.notToggle?.title || $[121] !== contextT.operators?.placeholderGroupLabel || $[122] !== contextT.operators?.placeholderLabel || $[123] !== contextT.operators?.placeholderName || $[124] !== contextT.operators?.title || $[125] !== contextT.removeGroup?.label || $[126] !== contextT.removeGroup?.title || $[127] !== contextT.removeRule?.label || $[128] !== contextT.removeRule?.title || $[129] !== contextT.shiftActionDown?.label || $[130] !== contextT.shiftActionDown?.title || $[131] !== contextT.shiftActionUp?.label || $[132] !== contextT.shiftActionUp?.title || $[133] !== contextT.value?.title || $[134] !== contextT.valueSourceSelector?.title || $[135] !== finalize || $[136] !== propsT.addGroup?.label || $[137] !== propsT.addGroup?.title || $[138] !== propsT.addRule?.label || $[139] !== propsT.addRule?.title || $[140] !== propsT.cloneRule?.label || $[141] !== propsT.cloneRule?.title || $[142] !== propsT.cloneRuleGroup?.label || $[143] !== propsT.cloneRuleGroup?.title || $[144] !== propsT.combinators?.title || $[145] !== propsT.dragHandle?.label || $[146] !== propsT.dragHandle?.title || $[147] !== propsT.fields?.placeholderGroupLabel || $[148] !== propsT.fields?.placeholderLabel || $[149] !== propsT.fields?.placeholderName || $[150] !== propsT.fields?.title || $[151] !== propsT.lockGroup?.label || $[152] !== propsT.lockGroup?.title || $[153] !== propsT.lockGroupDisabled?.label || $[154] !== propsT.lockGroupDisabled?.title || $[155] !== propsT.lockRule?.label || $[156] !== propsT.lockRule?.title || $[157] !== propsT.lockRuleDisabled?.label || $[158] !== propsT.lockRuleDisabled?.title || $[159] !== propsT.notToggle?.label || $[160] !== propsT.notToggle?.title || $[161] !== propsT.operators?.placeholderGroupLabel || $[162] !== propsT.operators?.placeholderLabel || $[163] !== propsT.operators?.placeholderName || $[164] !== propsT.operators?.title || $[165] !== propsT.removeGroup?.label || $[166] !== propsT.removeGroup?.title || $[167] !== propsT.removeRule?.label || $[168] !== propsT.removeRule?.title || $[169] !== propsT.shiftActionDown?.label || $[170] !== propsT.shiftActionDown?.title || $[171] !== propsT.shiftActionUp?.label || $[172] !== propsT.shiftActionUp?.title || $[173] !== propsT.value?.title || $[174] !== propsT.valueSourceSelector?.title) {
    t9 = Object.assign(finalize ? Object.assign({}, defaultTranslations) : {}, mergeTranslation("addGroup", { label: [propsT.addGroup?.label, contextT.addGroup?.label], title: [propsT.addGroup?.title, contextT.addGroup?.title] }, finalize), mergeTranslation("addRule", { label: [propsT.addRule?.label, contextT.addRule?.label], title: [propsT.addRule?.title, contextT.addRule?.title] }, finalize), mergeTranslation("cloneRule", { label: [propsT.cloneRule?.label, contextT.cloneRule?.label], title: [propsT.cloneRule?.title, contextT.cloneRule?.title] }, finalize), mergeTranslation("cloneRuleGroup", { label: [propsT.cloneRuleGroup?.label, contextT.cloneRuleGroup?.label], title: [propsT.cloneRuleGroup?.title, contextT.cloneRuleGroup?.title] }, finalize), mergeTranslation("combinators", { title: [propsT.combinators?.title, contextT.combinators?.title] }, finalize), mergeTranslation("dragHandle", { label: [propsT.dragHandle?.label, contextT.dragHandle?.label], title: [propsT.dragHandle?.title, contextT.dragHandle?.title] }, finalize), mergeTranslation("fields", { placeholderGroupLabel: [propsT.fields?.placeholderGroupLabel, contextT.fields?.placeholderGroupLabel], placeholderLabel: [propsT.fields?.placeholderLabel, contextT.fields?.placeholderLabel], placeholderName: [propsT.fields?.placeholderName, contextT.fields?.placeholderName], title: [propsT.fields?.title, contextT.fields?.title] }, finalize), mergeTranslation("lockGroup", { label: [propsT.lockGroup?.label, contextT.lockGroup?.label], title: [propsT.lockGroup?.title, contextT.lockGroup?.title] }, finalize), mergeTranslation("lockGroupDisabled", { label: [propsT.lockGroupDisabled?.label, contextT.lockGroupDisabled?.label], title: [propsT.lockGroupDisabled?.title, contextT.lockGroupDisabled?.title] }, finalize), mergeTranslation("lockRule", { label: [propsT.lockRule?.label, contextT.lockRule?.label], title: [propsT.lockRule?.title, contextT.lockRule?.title] }, finalize), mergeTranslation("lockRuleDisabled", { label: [propsT.lockRuleDisabled?.label, contextT.lockRuleDisabled?.label], title: [propsT.lockRuleDisabled?.title, contextT.lockRuleDisabled?.title] }, finalize), mergeTranslation("notToggle", { label: [propsT.notToggle?.label, contextT.notToggle?.label], title: [propsT.notToggle?.title, contextT.notToggle?.title] }, finalize), mergeTranslation("operators", { placeholderGroupLabel: [propsT.operators?.placeholderGroupLabel, contextT.operators?.placeholderGroupLabel], placeholderLabel: [propsT.operators?.placeholderLabel, contextT.operators?.placeholderLabel], placeholderName: [propsT.operators?.placeholderName, contextT.operators?.placeholderName], title: [propsT.operators?.title, contextT.operators?.title] }, finalize), mergeTranslation("removeGroup", { label: [propsT.removeGroup?.label, contextT.removeGroup?.label], title: [propsT.removeGroup?.title, contextT.removeGroup?.title] }, finalize), mergeTranslation("removeRule", { label: [propsT.removeRule?.label, contextT.removeRule?.label], title: [propsT.removeRule?.title, contextT.removeRule?.title] }, finalize), mergeTranslation("shiftActionDown", { label: [propsT.shiftActionDown?.label, contextT.shiftActionDown?.label], title: [propsT.shiftActionDown?.title, contextT.shiftActionDown?.title] }, finalize), mergeTranslation("shiftActionUp", { label: [propsT.shiftActionUp?.label, contextT.shiftActionUp?.label], title: [propsT.shiftActionUp?.title, contextT.shiftActionUp?.title] }, finalize), mergeTranslation("value", { title: [propsT.value?.title, contextT.value?.title] }, finalize), mergeTranslation("valueSourceSelector", { title: [propsT.valueSourceSelector?.title, contextT.valueSourceSelector?.title] }, finalize));
    $[96] = contextT.addGroup?.label;
    $[97] = contextT.addGroup?.title;
    $[98] = contextT.addRule?.label;
    $[99] = contextT.addRule?.title;
    $[100] = contextT.cloneRule?.label;
    $[101] = contextT.cloneRule?.title;
    $[102] = contextT.cloneRuleGroup?.label;
    $[103] = contextT.cloneRuleGroup?.title;
    $[104] = contextT.combinators?.title;
    $[105] = contextT.dragHandle?.label;
    $[106] = contextT.dragHandle?.title;
    $[107] = contextT.fields?.placeholderGroupLabel;
    $[108] = contextT.fields?.placeholderLabel;
    $[109] = contextT.fields?.placeholderName;
    $[110] = contextT.fields?.title;
    $[111] = contextT.lockGroup?.label;
    $[112] = contextT.lockGroup?.title;
    $[113] = contextT.lockGroupDisabled?.label;
    $[114] = contextT.lockGroupDisabled?.title;
    $[115] = contextT.lockRule?.label;
    $[116] = contextT.lockRule?.title;
    $[117] = contextT.lockRuleDisabled?.label;
    $[118] = contextT.lockRuleDisabled?.title;
    $[119] = contextT.notToggle?.label;
    $[120] = contextT.notToggle?.title;
    $[121] = contextT.operators?.placeholderGroupLabel;
    $[122] = contextT.operators?.placeholderLabel;
    $[123] = contextT.operators?.placeholderName;
    $[124] = contextT.operators?.title;
    $[125] = contextT.removeGroup?.label;
    $[126] = contextT.removeGroup?.title;
    $[127] = contextT.removeRule?.label;
    $[128] = contextT.removeRule?.title;
    $[129] = contextT.shiftActionDown?.label;
    $[130] = contextT.shiftActionDown?.title;
    $[131] = contextT.shiftActionUp?.label;
    $[132] = contextT.shiftActionUp?.title;
    $[133] = contextT.value?.title;
    $[134] = contextT.valueSourceSelector?.title;
    $[135] = finalize;
    $[136] = propsT.addGroup?.label;
    $[137] = propsT.addGroup?.title;
    $[138] = propsT.addRule?.label;
    $[139] = propsT.addRule?.title;
    $[140] = propsT.cloneRule?.label;
    $[141] = propsT.cloneRule?.title;
    $[142] = propsT.cloneRuleGroup?.label;
    $[143] = propsT.cloneRuleGroup?.title;
    $[144] = propsT.combinators?.title;
    $[145] = propsT.dragHandle?.label;
    $[146] = propsT.dragHandle?.title;
    $[147] = propsT.fields?.placeholderGroupLabel;
    $[148] = propsT.fields?.placeholderLabel;
    $[149] = propsT.fields?.placeholderName;
    $[150] = propsT.fields?.title;
    $[151] = propsT.lockGroup?.label;
    $[152] = propsT.lockGroup?.title;
    $[153] = propsT.lockGroupDisabled?.label;
    $[154] = propsT.lockGroupDisabled?.title;
    $[155] = propsT.lockRule?.label;
    $[156] = propsT.lockRule?.title;
    $[157] = propsT.lockRuleDisabled?.label;
    $[158] = propsT.lockRuleDisabled?.title;
    $[159] = propsT.notToggle?.label;
    $[160] = propsT.notToggle?.title;
    $[161] = propsT.operators?.placeholderGroupLabel;
    $[162] = propsT.operators?.placeholderLabel;
    $[163] = propsT.operators?.placeholderName;
    $[164] = propsT.operators?.title;
    $[165] = propsT.removeGroup?.label;
    $[166] = propsT.removeGroup?.title;
    $[167] = propsT.removeRule?.label;
    $[168] = propsT.removeRule?.title;
    $[169] = propsT.shiftActionDown?.label;
    $[170] = propsT.shiftActionDown?.title;
    $[171] = propsT.shiftActionUp?.label;
    $[172] = propsT.shiftActionUp?.title;
    $[173] = propsT.value?.title;
    $[174] = propsT.valueSourceSelector?.title;
    $[175] = t9;
  } else {
    t9 = $[175];
  }
  t8 = t9;
  const translations = t8;
  let t10;
  if ($[176] !== controlClassnames || $[177] !== controlElements || $[178] !== debugMode || $[179] !== enableDragAndDrop || $[180] !== enableMountQueryChange || $[181] !== props.initialQuery || $[182] !== props.qbId || $[183] !== translations) {
    t10 = { controlClassnames, controlElements, debugMode, enableDragAndDrop, enableMountQueryChange, translations, initialQuery: props.initialQuery, qbId: props.qbId };
    $[176] = controlClassnames;
    $[177] = controlElements;
    $[178] = debugMode;
    $[179] = enableDragAndDrop;
    $[180] = enableMountQueryChange;
    $[181] = props.initialQuery;
    $[182] = props.qbId;
    $[183] = translations;
    $[184] = t10;
  } else {
    t10 = $[184];
  }
  return t10;
};

// src/hooks/usePrevious.ts
import { useRef } from "react";
var usePrevious = (value) => {
  const ref = useRef({ value, prev: null });
  const current = ref.current.value;
  if (value !== current) {
    ref.current = { value, prev: current };
  }
  return ref.current.prev;
};

// src/hooks/useControlledOrUncontrolled.ts
var useControlledOrUncontrolled = (params) => {
  const dispatch = useRQB_INTERNAL_QueryBuilderDispatch();
  const { defaultQuery, queryProp } = params;
  const prevQueryPresent = usePrevious(!!queryProp);
  if (process.env.NODE_ENV !== "production") {
    if (!!queryProp && !!defaultQuery) {
      dispatch(rqbWarn(messages.errorBothQueryDefaultQuery));
    } else {
      if (prevQueryPresent === true && !queryProp && !!defaultQuery) {
        dispatch(rqbWarn(messages.errorControlledToUncontrolled));
      } else {
        if (prevQueryPresent === false && !!queryProp && !defaultQuery) {
          dispatch(rqbWarn(messages.errorUncontrolledToControlled));
        }
      }
    }
  }
};

// src/redux/selectors.ts
var getQuerySelectorById = (qbId) => (state) => queriesSlice.selectors.getQuerySelectorById({ queries: state.queries }, qbId);

// src/redux/hooks.ts
import * as React5 from "react";
import { createSelectorHook } from "react-redux";
var useRQB_INTERNAL_QueryBuilderSelector = createSelectorHook(QueryBuilderStateContext);
var useQueryBuilderSelector = (selector, other) => {
  const rqbContext = React5.useContext(QueryBuilderContext);
  const result = useRQB_INTERNAL_QueryBuilderSelector(selector, other);
  return result ?? rqbContext?.initialQuery;
};
var useQueryBuilderQuery = (props) => {
  const rqbContext = React5.useContext(QueryBuilderContext);
  return useRQB_INTERNAL_QueryBuilderSelector(getQuerySelectorById(props?.schema.qbId ?? rqbContext.qbId ?? "")) ?? rqbContext?.initialQuery;
};

// src/components/QueryBuilder.useQueryBuilderSchema.ts
import { useCallback as useCallback7, useEffect as useEffect2, useMemo as useMemo8, useRef as useRef2, useState } from "react";
var defaultValidationResult = {};
var defaultValidationMap = {};
var defaultDisabledPaths = [];
var icCombinatorPropObject = {};
var defaultGetValueEditorSeparator = () => null;
var defaultGetRuleOrGroupClassname = () => "";
var defaultOnAddMoveRemove = () => true;
var defaultOnLog = function() {
  console.log(...arguments);
};
function useQueryBuilderSchema(props, setup) {
  const { query: queryProp, defaultQuery: defaultQueryProp, getValueEditorSeparator = defaultGetValueEditorSeparator, getRuleClassname = defaultGetRuleOrGroupClassname, getRuleGroupClassname = defaultGetRuleOrGroupClassname, onAddRule = defaultOnAddMoveRemove, onAddGroup = defaultOnAddMoveRemove, onMoveRule = defaultOnAddMoveRemove, onMoveGroup = defaultOnAddMoveRemove, onGroupRule = defaultOnAddMoveRemove, onGroupGroup = defaultOnAddMoveRemove, onRemove = defaultOnAddMoveRemove, onQueryChange, showCombinatorsBetweenRules: showCombinatorsBetweenRulesProp = false, showNotToggle: showNotToggleProp = false, showShiftActions: showShiftActionsProp = false, showCloneButtons: showCloneButtonsProp = false, showLockButtons: showLockButtonsProp = false, suppressStandardClassnames: suppressStandardClassnamesProp = false, resetOnFieldChange: resetOnFieldChangeProp = true, resetOnOperatorChange: resetOnOperatorChangeProp = false, autoSelectField: autoSelectFieldProp = true, autoSelectOperator: autoSelectOperatorProp = true, addRuleToNewGroups: addRuleToNewGroupsProp = false, listsAsArrays: listsAsArraysProp = false, parseNumbers = false, disabled = false, validator, onLog = defaultOnLog, idGenerator, accessibleDescriptionGenerator = generateAccessibleDescription } = props;
  const { qbId, rqbContext: incomingRqbContext, fields, fieldMap, combinators, getOperatorsMain, getRuleDefaultOperator, getValueEditorTypeMain, getValueSourcesMain, getValuesMain, getRuleDefaultValue, getInputTypeMain, createRule, createRuleGroup } = setup;
  const { controlClassnames, controlElements: controls, debugMode, enableDragAndDrop, enableMountQueryChange, translations } = incomingRqbContext;
  const showCombinatorsBetweenRules = !!showCombinatorsBetweenRulesProp;
  const showNotToggle = !!showNotToggleProp;
  const showShiftActions = !!showShiftActionsProp;
  const showCloneButtons = !!showCloneButtonsProp;
  const showLockButtons = !!showLockButtonsProp;
  const resetOnFieldChange = !!resetOnFieldChangeProp;
  const resetOnOperatorChange = !!resetOnOperatorChangeProp;
  const autoSelectField = !!autoSelectFieldProp;
  const autoSelectOperator = !!autoSelectOperatorProp;
  const addRuleToNewGroups = !!addRuleToNewGroupsProp;
  const listsAsArrays = !!listsAsArraysProp;
  const suppressStandardClassnames = !!suppressStandardClassnamesProp;
  const log = useCallback7(function() {
    if (debugMode) {
      onLog(...arguments);
    }
  }, [debugMode, onLog]);
  useControlledOrUncontrolled({ defaultQuery: defaultQueryProp, queryProp });
  const queryBuilderStore = useRQB_INTERNAL_QueryBuilderStore();
  const queryBuilderDispatch = useRQB_INTERNAL_QueryBuilderDispatch();
  const querySelector = useMemo8(() => getQuerySelectorById(qbId), [qbId]);
  const storeQuery = useQueryBuilderSelector(querySelector);
  const getQuery = useCallback7(() => querySelector(queryBuilderStore.getState()), [queryBuilderStore, querySelector]);
  const fallbackQuery = useMemo8(() => createRuleGroup(), [createRuleGroup]);
  const candidateQuery = queryProp ?? storeQuery ?? defaultQueryProp ?? fallbackQuery;
  const rootGroup = candidateQuery.id ? candidateQuery : prepareRuleGroup(candidateQuery, { idGenerator });
  const [initialQuery] = useState(rootGroup);
  const rqbContext = useMemo8(() => ({ ...incomingRqbContext, initialQuery }), [incomingRqbContext, initialQuery]);
  useEffect2(() => {
    if (!!queryProp && !Object.is(queryProp, storeQuery)) {
      queryBuilderDispatch(_RQB_INTERNAL_dispatchThunk({ payload: { qbId, query: queryProp }, onQueryChange: void 0 }));
    }
  }, [queryProp, qbId, storeQuery, queryBuilderDispatch]);
  const independentCombinators = useMemo8(() => isRuleGroupTypeIC(rootGroup), [rootGroup]);
  const invalidIC = !!props.independentCombinators && !independentCombinators;
  useDeprecatedProps("independentCombinators", invalidIC || !invalidIC && (props.independentCombinators ?? "not present") !== "not present", invalidIC ? "invalid" : "unnecessary");
  const hasRunMountQueryChange = useRef2(false);
  useEffect2(() => {
    if (hasRunMountQueryChange.current) return;
    hasRunMountQueryChange.current = true;
    queryBuilderDispatch(_RQB_INTERNAL_dispatchThunk({ payload: { qbId, query: rootGroup }, onQueryChange: (
      // Leave `onQueryChange` undefined if `enableMountQueryChange` is disabled
      enableMountQueryChange && typeof onQueryChange === "function" ? onQueryChange : void 0
    ) }));
  }, [enableMountQueryChange, onQueryChange, qbId, queryBuilderDispatch, rootGroup]);
  const dispatchQuery = useCallback7((newQuery) => {
    queryBuilderDispatch(_RQB_INTERNAL_dispatchThunk({ payload: { qbId, query: newQuery }, onQueryChange }));
  }, [onQueryChange, qbId, queryBuilderDispatch]);
  const disabledPaths = useMemo8(() => Array.isArray(disabled) && disabled || defaultDisabledPaths, [disabled]);
  const queryDisabled = disabled === true;
  const rootGroupDisabled = useMemo8(() => rootGroup.disabled || disabledPaths.some((p) => p.length === 0), [disabledPaths, rootGroup.disabled]);
  const onRuleAdd = useCallback7(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (rule, parentPath, context) => {
      const queryLocal = getQuerySelectorById(qbId)(queryBuilderStore.getState());
      if (!queryLocal) return;
      if (pathIsDisabled(parentPath, queryLocal) || queryDisabled) {
        log({ qbId, type: LogType.parentPathDisabled, rule, parentPath, query: queryLocal });
        return;
      }
      const nextRule = onAddRule(rule, parentPath, queryLocal, context);
      if (!nextRule) {
        log({ qbId, type: LogType.onAddRuleFalse, rule, parentPath, query: queryLocal });
        return;
      }
      const newRule = nextRule === true ? rule : nextRule;
      const newQuery_0 = add(queryLocal, newRule, parentPath, { combinators, combinatorPreceding: newRule.combinatorPreceding ?? void 0, idGenerator });
      log({ qbId, type: LogType.add, query: queryLocal, newQuery: newQuery_0, newRule, parentPath });
      dispatchQuery(newQuery_0);
    },
    [qbId, queryBuilderStore, queryDisabled, onAddRule, combinators, idGenerator, log, dispatchQuery]
  );
  const onGroupAdd = useCallback7(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (ruleGroup, parentPath_0, context_0) => {
      const queryLocal_0 = getQuerySelectorById(qbId)(queryBuilderStore.getState());
      if (!queryLocal_0) return;
      if (pathIsDisabled(parentPath_0, queryLocal_0) || queryDisabled) {
        log({ qbId, type: LogType.parentPathDisabled, ruleGroup, parentPath: parentPath_0, query: queryLocal_0 });
        return;
      }
      const nextGroup = onAddGroup(ruleGroup, parentPath_0, queryLocal_0, context_0);
      if (!nextGroup) {
        log({ qbId, type: LogType.onAddGroupFalse, ruleGroup, parentPath: parentPath_0, query: queryLocal_0 });
        return;
      }
      const newGroup = nextGroup === true ? ruleGroup : nextGroup;
      const newQuery_1 = add(queryLocal_0, newGroup, parentPath_0, { combinators, combinatorPreceding: newGroup.combinatorPreceding ?? void 0, idGenerator });
      log({ qbId, type: LogType.add, query: queryLocal_0, newQuery: newQuery_1, newGroup, parentPath: parentPath_0 });
      dispatchQuery(newQuery_1);
    },
    [qbId, queryBuilderStore, queryDisabled, onAddGroup, combinators, idGenerator, log, dispatchQuery]
  );
  const onPropChange = useCallback7(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (prop, value, path) => {
      const queryLocal_1 = getQuerySelectorById(qbId)(queryBuilderStore.getState());
      if (!queryLocal_1) return;
      if (pathIsDisabled(path, queryLocal_1) && prop !== "disabled" || queryDisabled) {
        log({ qbId, type: LogType.pathDisabled, path, prop, value, query: queryLocal_1 });
        return;
      }
      const newQuery_2 = update(queryLocal_1, prop, value, path, { resetOnFieldChange, resetOnOperatorChange, getRuleDefaultOperator, getValueSources: getValueSourcesMain, getRuleDefaultValue });
      log({ qbId, type: LogType.update, query: queryLocal_1, newQuery: newQuery_2, prop, value, path });
      dispatchQuery(newQuery_2);
    },
    [qbId, queryBuilderStore, queryDisabled, resetOnFieldChange, resetOnOperatorChange, getRuleDefaultOperator, getValueSourcesMain, getRuleDefaultValue, log, dispatchQuery]
  );
  const onRuleOrGroupRemove = useCallback7(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (path_0, context_1) => {
      const queryLocal_2 = getQuerySelectorById(qbId)(queryBuilderStore.getState());
      if (!queryLocal_2) return;
      if (pathIsDisabled(path_0, queryLocal_2) || queryDisabled) {
        log({ qbId, type: LogType.pathDisabled, path: path_0, query: queryLocal_2 });
        return;
      }
      const ruleOrGroup = findPath(path_0, queryLocal_2);
      if (ruleOrGroup) {
        if (onRemove(ruleOrGroup, path_0, queryLocal_2, context_1)) {
          const newQuery_3 = remove(queryLocal_2, path_0);
          log({ qbId, type: LogType.remove, query: queryLocal_2, newQuery: newQuery_3, path: path_0, ruleOrGroup });
          dispatchQuery(newQuery_3);
        } else {
          log({ qbId, type: LogType.onRemoveFalse, ruleOrGroup, path: path_0, query: queryLocal_2 });
        }
      }
    },
    [qbId, queryBuilderStore, queryDisabled, log, onRemove, dispatchQuery]
  );
  const moveRule = useCallback7(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (oldPath, newPath, clone, context_2) => {
      const queryLocal_3 = getQuerySelectorById(qbId)(queryBuilderStore.getState());
      if (!queryLocal_3) return;
      if (pathIsDisabled(oldPath, queryLocal_3) || queryDisabled) {
        log({ qbId, type: LogType.pathDisabled, oldPath, newPath, query: queryLocal_3 });
        return;
      }
      const nextQuery = move(queryLocal_3, oldPath, newPath, { clone, combinators, idGenerator });
      const ruleOrGroup_0 = findPath(oldPath, queryLocal_3);
      const isGroup = isRuleGroup(ruleOrGroup_0);
      const callbackResult = (isGroup ? onMoveGroup : onMoveRule)(ruleOrGroup_0, oldPath, newPath, queryLocal_3, nextQuery, { clone, combinators }, context_2);
      if (!callbackResult) {
        log({ qbId, type: isGroup ? LogType.onMoveGroupFalse : LogType.onMoveRuleFalse, ruleOrGroup: ruleOrGroup_0, oldPath, newPath, clone, query: queryLocal_3, nextQuery });
        return;
      }
      const newQuery_4 = isRuleGroup(callbackResult) ? callbackResult : nextQuery;
      log({ qbId, type: LogType.move, query: queryLocal_3, newQuery: newQuery_4, oldPath, newPath, clone });
      dispatchQuery(newQuery_4);
    },
    [qbId, queryBuilderStore, queryDisabled, combinators, idGenerator, onMoveGroup, onMoveRule, log, dispatchQuery]
  );
  const groupRule = useCallback7(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (sourcePath, targetPath, clone_0, context_3) => {
      const queryLocal_4 = getQuerySelectorById(qbId)(queryBuilderStore.getState());
      if (!queryLocal_4) return;
      if (pathIsDisabled(sourcePath, queryLocal_4) || queryDisabled) {
        log({ qbId, type: LogType.pathDisabled, sourcePath, targetPath, query: queryLocal_4 });
        return;
      }
      const nextQuery_0 = group(queryLocal_4, sourcePath, targetPath, { clone: clone_0, combinators, idGenerator });
      const ruleOrGroup_1 = findPath(sourcePath, queryLocal_4);
      const isGroup_0 = isRuleGroup(ruleOrGroup_1);
      const callbackResult_0 = (isGroup_0 ? onGroupGroup : onGroupRule)(ruleOrGroup_1, sourcePath, targetPath, queryLocal_4, nextQuery_0, { clone: clone_0, combinators }, context_3);
      if (!callbackResult_0) {
        log({ qbId, type: isGroup_0 ? LogType.onGroupGroupFalse : LogType.onGroupRuleFalse, ruleOrGroup: ruleOrGroup_1, sourcePath, targetPath, clone: clone_0, query: queryLocal_4, nextQuery: nextQuery_0 });
        return;
      }
      const newQuery_5 = isRuleGroup(callbackResult_0) ? callbackResult_0 : nextQuery_0;
      log({ qbId, type: LogType.group, query: queryLocal_4, newQuery: newQuery_5, sourcePath, targetPath, clone: clone_0 });
      dispatchQuery(newQuery_5);
    },
    [qbId, queryBuilderStore, queryDisabled, combinators, idGenerator, onGroupGroup, onGroupRule, log, dispatchQuery]
  );
  const { validationResult, validationMap } = useMemo8(() => {
    const validationResult2 = typeof validator === "function" && rootGroup ? validator(rootGroup) : defaultValidationResult;
    const validationMap2 = typeof validationResult2 === "boolean" ? defaultValidationMap : validationResult2;
    return { validationResult: validationResult2, validationMap: validationMap2 };
  }, [rootGroup, validator]);
  const dndEnabledAttr = useMemo8(() => enableDragAndDrop ? "enabled" : "disabled", [enableDragAndDrop]);
  const inlineCombinatorsAttr = useMemo8(() => independentCombinators || showCombinatorsBetweenRules ? "enabled" : "disabled", [independentCombinators, showCombinatorsBetweenRules]);
  const combinatorPropObject = useMemo8(() => typeof rootGroup.combinator === "string" ? { combinator: rootGroup.combinator } : icCombinatorPropObject, [rootGroup.combinator]);
  const wrapperClassName = useMemo8(() => clsx(
    suppressStandardClassnames || standardClassnames.queryBuilder,
    clsx(controlClassnames.queryBuilder),
    // custom conditional classes
    queryDisabled && controlClassnames.disabled,
    typeof validationResult === "boolean" && validationResult && controlClassnames.valid,
    typeof validationResult === "boolean" && !validationResult && controlClassnames.invalid,
    // standard conditional classes
    suppressStandardClassnames || { [standardClassnames.disabled]: queryDisabled, [standardClassnames.valid]: typeof validationResult === "boolean" && validationResult, [standardClassnames.invalid]: typeof validationResult === "boolean" && !validationResult }
  ), [controlClassnames.disabled, controlClassnames.invalid, controlClassnames.queryBuilder, controlClassnames.valid, queryDisabled, suppressStandardClassnames, validationResult]);
  const schema = useMemo8(() => ({ addRuleToNewGroups, accessibleDescriptionGenerator, autoSelectField, autoSelectOperator, classNames: controlClassnames, combinators, controls, createRule, createRuleGroup, disabledPaths, enableDragAndDrop, fieldMap, fields, dispatchQuery, getQuery, getInputType: getInputTypeMain, getOperators: getOperatorsMain, getRuleClassname, getRuleGroupClassname, getValueEditorSeparator, getValueEditorType: getValueEditorTypeMain, getValues: getValuesMain, getValueSources: getValueSourcesMain, independentCombinators, listsAsArrays, parseNumbers, qbId, showCloneButtons, showCombinatorsBetweenRules, showLockButtons, showNotToggle, showShiftActions, suppressStandardClassnames, validationMap }), [accessibleDescriptionGenerator, addRuleToNewGroups, autoSelectField, autoSelectOperator, combinators, controlClassnames, controls, createRule, createRuleGroup, disabledPaths, dispatchQuery, enableDragAndDrop, fieldMap, fields, getInputTypeMain, getOperatorsMain, getQuery, getRuleClassname, getRuleGroupClassname, getValueEditorSeparator, getValueEditorTypeMain, getValuesMain, getValueSourcesMain, independentCombinators, listsAsArrays, parseNumbers, qbId, showCloneButtons, showCombinatorsBetweenRules, showLockButtons, showNotToggle, showShiftActions, suppressStandardClassnames, validationMap]);
  const actions = useMemo8(() => ({ moveRule, onGroupAdd, onGroupRemove: onRuleOrGroupRemove, onPropChange, onRuleAdd, onRuleRemove: onRuleOrGroupRemove, groupRule }), [groupRule, moveRule, onGroupAdd, onPropChange, onRuleAdd, onRuleOrGroupRemove]);
  return { actions, rootGroup, rootGroupDisabled, queryDisabled, rqbContext, schema, translations, wrapperClassName, dndEnabledAttr, inlineCombinatorsAttr, combinatorPropObject };
}

// src/components/QueryBuilder.useQueryBuilderSetup.ts
import { useCallback as useCallback8, useMemo as useMemo9, useState as useState2 } from "react";
var getFirstOptionsFrom = (opts, r, listsAsArrays) => {
  const firstOption = getFirstOption(opts);
  if (r.operator === "between" || r.operator === "notBetween") {
    const valueAsArray = [firstOption, firstOption];
    return listsAsArrays ? valueAsArray : joinWith(valueAsArray.map((v) => v ?? /* istanbul ignore next */
    ""), ",");
  }
  return firstOption;
};
var useQueryBuilderSetup = (props) => {
  const [qbId] = useState2(generateID);
  const { fields: fieldsPropOriginal, baseField, operators: operatorsProp, baseOperator, combinators: combinatorsProp = defaultCombinators, baseCombinator, translations: translationsProp, enableMountQueryChange: enableMountQueryChangeProp = true, controlClassnames: controlClassnamesProp, controlElements: controlElementsProp, getDefaultField, getDefaultOperator, getDefaultValue, getOperators, getValueEditorType, getValueSources, getInputType, getValues, autoSelectField = true, autoSelectOperator = true, addRuleToNewGroups = false, enableDragAndDrop: enableDragAndDropProp, listsAsArrays = false, debugMode: debugModeProp = false, idGenerator = generateID } = props;
  const operators = operatorsProp ?? defaultOperators;
  const [initialQueryProp] = useState2(props.query ?? props.defaultQuery);
  const rqbContext = useMergedContext({ controlClassnames: controlClassnamesProp, controlElements: controlElementsProp, debugMode: debugModeProp, enableDragAndDrop: enableDragAndDropProp, enableMountQueryChange: enableMountQueryChangeProp, translations: translationsProp, initialQuery: initialQueryProp, qbId, finalize: true });
  const { translations } = rqbContext;
  const defaultField = useMemo9(() => ({ id: translations.fields.placeholderName, name: translations.fields.placeholderName, value: translations.fields.placeholderName, label: translations.fields.placeholderLabel }), [translations.fields.placeholderLabel, translations.fields.placeholderName]);
  const fieldsProp = useMemo9(() => fieldsPropOriginal ?? [defaultField], [defaultField, fieldsPropOriginal]);
  const fields = useMemo9(() => {
    const flds = Array.isArray(fieldsProp) ? toFullOptionList(fieldsProp, baseField) : objectKeys(toFullOptionMap(fieldsProp, baseField)).map((fld) => ({ ...fieldsProp[fld], name: fld, value: fld })).sort((a, b) => a.label.localeCompare(b.label));
    if (isFlexibleOptionGroupArray(flds)) {
      return autoSelectField ? uniqOptGroups(flds) : uniqOptGroups([{ label: translations.fields.placeholderGroupLabel, options: [defaultField] }, ...flds]);
    } else {
      return autoSelectField ? uniqByIdentifier(flds) : uniqByIdentifier([defaultField, ...flds]);
    }
  }, [autoSelectField, baseField, defaultField, fieldsProp, translations.fields.placeholderGroupLabel]);
  const fieldMap = useMemo9(() => {
    if (!Array.isArray(fieldsProp)) {
      const fp = toFullOptionMap(fieldsProp, baseField);
      return autoSelectField ? fp : { ...fp, [translations.fields.placeholderName]: defaultField };
    }
    const fm = {};
    if (isFlexibleOptionGroupArray(fields)) {
      for (const f of fields) {
        for (const opt of f.options) {
          fm[opt.value ?? /* istanbul ignore next */
          opt.name] = toFullOption(opt, baseField);
        }
      }
    } else {
      for (const f_0 of fields) {
        fm[f_0.value ?? /* istanbul ignore next */
        f_0.name] = toFullOption(f_0, baseField);
      }
    }
    return fm;
  }, [autoSelectField, baseField, defaultField, fields, fieldsProp, translations.fields.placeholderName]);
  const combinators = useMemo9(() => toFullOptionList(combinatorsProp, baseCombinator), [baseCombinator, combinatorsProp]);
  const defaultOperator = useMemo9(() => ({ id: translations.operators.placeholderName, name: translations.operators.placeholderName, value: translations.operators.placeholderName, label: translations.operators.placeholderLabel }), [translations.operators.placeholderLabel, translations.operators.placeholderName]);
  const getOperatorsMain = useCallback8((field, _ref) => {
    let { fieldData } = _ref;
    let opsFinal = toFullOptionList(operators, baseOperator);
    if (fieldData?.operators) {
      opsFinal = toFullOptionList(fieldData.operators, baseOperator);
    } else if (getOperators) {
      const ops = getOperators(field, { fieldData });
      if (ops) {
        opsFinal = toFullOptionList(ops, baseOperator);
      }
    }
    if (!autoSelectOperator) {
      opsFinal = isFlexibleOptionGroupArray(opsFinal) ? [{ label: translations.operators.placeholderGroupLabel, options: [defaultOperator] }, ...opsFinal] : [defaultOperator, ...opsFinal];
    }
    return uniqOptList(opsFinal);
  }, [autoSelectOperator, baseOperator, defaultOperator, getOperators, operators, translations.operators.placeholderGroupLabel]);
  const getRuleDefaultOperator = useCallback8((field_0) => {
    const fieldData_0 = fieldMap[field_0];
    if (fieldData_0?.defaultOperator) {
      return fieldData_0.defaultOperator;
    }
    if (getDefaultOperator) {
      return typeof getDefaultOperator === "function" ? getDefaultOperator(field_0, { fieldData: fieldData_0 }) : getDefaultOperator;
    }
    const ops_0 = getOperatorsMain(field_0, { fieldData: fieldData_0 }) ?? /* istanbul ignore next */
    [];
    return getFirstOption(ops_0) ?? /* istanbul ignore next */
    "";
  }, [fieldMap, getDefaultOperator, getOperatorsMain]);
  const getValueEditorTypeMain = useCallback8((field_1, operator, _ref2) => {
    let { fieldData: fieldData_1 } = _ref2;
    if (fieldData_1.valueEditorType) {
      if (typeof fieldData_1.valueEditorType === "function") {
        return fieldData_1.valueEditorType(operator);
      }
      return fieldData_1.valueEditorType;
    }
    return getValueEditorType?.(field_1, operator, { fieldData: fieldData_1 }) ?? "text";
  }, [getValueEditorType]);
  const getValueSourcesMain = useCallback8((field_2, operator_0) => getValueSourcesUtil(fieldMap[field_2], operator_0, getValueSources), [fieldMap, getValueSources]);
  const getValuesMain = useCallback8((field_3, operator_1, _ref3) => {
    let { fieldData: fieldData_2 } = _ref3;
    if (fieldData_2?.values) {
      return toFullOptionList(fieldData_2.values);
    }
    if (getValues) {
      return toFullOptionList(getValues(field_3, operator_1, { fieldData: fieldData_2 }));
    }
    return [];
  }, [getValues]);
  const getRuleDefaultValue = useCallback8((r) => {
    const fieldData_3 = fieldMap[r.field] ?? {};
    if (fieldData_3?.defaultValue !== void 0 && fieldData_3.defaultValue !== null) {
      return fieldData_3.defaultValue;
    } else if (getDefaultValue) {
      return getDefaultValue(r, { fieldData: fieldData_3 });
    }
    let value = "";
    const values = getValuesMain(r.field, r.operator, { fieldData: fieldData_3 });
    if (r.valueSource === "field") {
      const filteredFields = filterFieldsByComparator(fieldData_3, fields, r.operator);
      value = filteredFields.length > 0 ? getFirstOptionsFrom(filteredFields, r, listsAsArrays) : "";
    } else if (values.length > 0) {
      const editorType = getValueEditorTypeMain(r.field, r.operator, { fieldData: fieldData_3 });
      if (editorType === "multiselect") {
        value = listsAsArrays ? [] : "";
      } else if (editorType === "select" || editorType === "radio") {
        value = getFirstOptionsFrom(values, r, listsAsArrays);
      }
    } else {
      const editorType_0 = getValueEditorTypeMain(r.field, r.operator, { fieldData: fieldData_3 });
      if (editorType_0 === "checkbox") {
        value = false;
      }
    }
    return value;
  }, [fieldMap, fields, getDefaultValue, getValueEditorTypeMain, getValuesMain, listsAsArrays]);
  const getInputTypeMain = useCallback8((field_4, operator_2, _ref4) => {
    let { fieldData: fieldData_4 } = _ref4;
    if (getInputType) {
      const inputType = getInputType(field_4, operator_2, { fieldData: fieldData_4 });
      if (inputType) return inputType;
    }
    return "text";
  }, [getInputType]);
  const createRule = useCallback8(() => {
    let field_5 = "";
    const flds_0 = fields;
    if (flds_0?.length > 0 && flds_0[0]) {
      const fo = getFirstOption(flds_0);
      if (fo) field_5 = fo;
    }
    if (getDefaultField) {
      if (typeof getDefaultField === "function") {
        const df = getDefaultField(flds_0);
        if (df) field_5 = df;
      } else {
        field_5 = getDefaultField;
      }
    }
    const operator_3 = getRuleDefaultOperator(field_5);
    const valueSource = getValueSourcesMain(field_5, operator_3)[0] ?? "value";
    const newRule = { id: idGenerator(), field: field_5, operator: operator_3, valueSource, value: "" };
    const value_0 = getRuleDefaultValue(newRule);
    return { ...newRule, value: value_0 };
  }, [fields, getDefaultField, getRuleDefaultOperator, getRuleDefaultValue, getValueSourcesMain, idGenerator]);
  const createRuleGroup = useCallback8((independentCombinators) => {
    if (independentCombinators) {
      return { id: idGenerator(), rules: addRuleToNewGroups ? [createRule()] : [], not: false };
    }
    return { id: idGenerator(), rules: addRuleToNewGroups ? [createRule()] : [], combinator: getFirstOption(combinators) ?? /* istanbul ignore next */
    "", not: false };
  }, [addRuleToNewGroups, combinators, createRule, idGenerator]);
  return {
    qbId,
    rqbContext,
    // TODO: Why is a cast necessary here?
    fields,
    fieldMap,
    combinators,
    getOperatorsMain,
    getRuleDefaultOperator,
    getValueEditorTypeMain,
    getValueSourcesMain,
    getValuesMain,
    getRuleDefaultValue,
    getInputTypeMain,
    createRule,
    createRuleGroup
  };
};

// src/components/QueryBuilder.useQueryBuilder.ts
var useQueryBuilder = (props) => {
  return useQueryBuilderSchema(props, useQueryBuilderSetup(props));
};

// src/components/QueryBuilderInternal.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
var QueryBuilderInternal = (t0) => {
  const $ = c(15);
  const { props } = t0;
  const qb = useQueryBuilder(props);
  const RuleGroupControlElement = qb.schema.controls.ruleGroup;
  const t1 = !!qb.rootGroup.not;
  let t2;
  if ($[0] !== RuleGroupControlElement || $[1] !== props.context || $[2] !== qb.actions || $[3] !== qb.combinatorPropObject || $[4] !== qb.dndEnabledAttr || $[5] !== qb.inlineCombinatorsAttr || $[6] !== qb.queryDisabled || $[7] !== qb.rootGroup || $[8] !== qb.rootGroupDisabled || $[9] !== qb.rqbContext || $[10] !== qb.schema || $[11] !== qb.translations || $[12] !== qb.wrapperClassName || $[13] !== t1) {
    t2 = jsx12(QueryBuilderContext.Provider, { value: qb.rqbContext, children: jsx12("div", { role: "form", className: qb.wrapperClassName, "data-dnd": qb.dndEnabledAttr, "data-inlinecombinators": qb.inlineCombinatorsAttr, children: jsx12(RuleGroupControlElement, { translations: qb.translations, ruleGroup: qb.rootGroup, rules: qb.rootGroup.rules, ...qb.combinatorPropObject, not: t1, schema: qb.schema, actions: qb.actions, id: qb.rootGroup.id, path: rootPath, disabled: qb.rootGroupDisabled, shiftUpDisabled: true, shiftDownDisabled: true, parentDisabled: qb.queryDisabled, context: props.context }) }) }, qb.dndEnabledAttr);
    $[0] = RuleGroupControlElement;
    $[1] = props.context;
    $[2] = qb.actions;
    $[3] = qb.combinatorPropObject;
    $[4] = qb.dndEnabledAttr;
    $[5] = qb.inlineCombinatorsAttr;
    $[6] = qb.queryDisabled;
    $[7] = qb.rootGroup;
    $[8] = qb.rootGroupDisabled;
    $[9] = qb.rqbContext;
    $[10] = qb.schema;
    $[11] = qb.translations;
    $[12] = qb.wrapperClassName;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
};

export {
  QueryBuilderContext,
  ActionElement,
  DragHandle,
  defaultPlaceholderName,
  defaultPlaceholderLabel,
  defaultPlaceholderFieldName,
  defaultPlaceholderFieldLabel,
  defaultPlaceholderFieldGroupLabel,
  defaultPlaceholderOperatorName,
  defaultPlaceholderOperatorLabel,
  defaultPlaceholderOperatorGroupLabel,
  defaultJoinChar,
  defaultTranslations,
  defaultOperators,
  defaultOperatorNegationMap,
  defaultCombinators,
  defaultCombinatorsExtended,
  standardClassnames,
  defaultControlClassnames,
  groupInvalidReasons,
  TestID,
  LogType,
  rootPath,
  InlineCombinator,
  NotToggle,
  messages,
  QueryBuilderStateContext,
  storeCommon,
  useDeprecatedProps,
  useReactDndWarning,
  useStopEventPropagation,
  splitBy,
  joinWith,
  trimIfString,
  toArray,
  nullFreeArray,
  numericRegex,
  isPojo,
  nullOrUndefinedOrEmpty,
  isRuleGroup,
  isRuleGroupType,
  isRuleGroupTypeIC,
  convertFromIC,
  convertToIC,
  convertQuery,
  defaultValidator,
  toFullOption,
  toFullOptionList,
  toFullOptionMap,
  uniqByName,
  uniqByIdentifier,
  isOptionGroupArray,
  isFlexibleOptionArray,
  isFullOptionArray,
  isFlexibleOptionGroupArray,
  isFullOptionGroupArray,
  getOption,
  getFirstOption,
  toFlatOptionArray,
  uniqOptGroups,
  uniqOptList,
  filterFieldsByComparator,
  parseNumber,
  getParseNumberMethod,
  mapSQLOperator,
  mongoOperators,
  celCombinatorMap,
  jsonLogicAdditionalOperators,
  numerifyValues,
  isValidValue,
  shouldRenderAsNumber,
  isValueProcessorLegacy,
  getQuoteFieldNamesWithArray,
  getQuotedFieldName,
  defaultRuleProcessorCEL,
  defaultRuleProcessorMongoDBQuery,
  defaultRuleProcessorMongoDB,
  defaultRuleProcessorSpEL,
  defaultValueProcessorByRule,
  defaultRuleProcessorElasticSearch,
  defaultRuleProcessorJSONata,
  defaultRuleProcessorJsonLogic,
  defaultRuleProcessorSQL,
  defaultRuleProcessorParameterized,
  isValidationResult,
  isRuleOrGroupValid,
  sqlDialectPresets,
  formatQuery,
  defaultValueProcessor,
  defaultMongoDBValueProcessor,
  defaultCELValueProcessor,
  defaultSpELValueProcessor,
  defaultValueProcessorCELByRule,
  defaultValueProcessorMongoDBByRule,
  defaultValueProcessorSpELByRule,
  findPath,
  getParentPath,
  pathsAreEqual,
  isAncestor,
  getCommonAncestorPath,
  pathIsDisabled,
  generateAccessibleDescription,
  generateID,
  preferProp,
  usePreferProp,
  usePreferAnyProp,
  useMergedContext,
  getCompatContextProvider,
  getValidationClassNames,
  getValueSourcesUtil,
  mergeClassnames,
  objectKeys,
  objectEntries,
  mergeTranslations,
  mergeTranslation,
  prepareRule,
  prepareRuleGroup,
  prepareRuleOrGroup,
  regenerateID,
  regenerateIDs,
  add,
  update,
  remove,
  move,
  insert,
  group,
  toOptions,
  transformQuery,
  Rule,
  RuleComponents,
  useRule,
  RuleGroup,
  RuleGroupHeaderComponents,
  RuleGroupBodyComponents,
  useRuleGroup,
  ShiftActions,
  ValueEditor,
  useValueEditor,
  useSelectElementChangeHandler,
  ValueSelector,
  useValueSelector,
  defaultControlElements,
  usePrevious,
  useControlledOrUncontrolled,
  getQuerySelectorById,
  useQueryBuilderSelector,
  useQueryBuilderQuery,
  useQueryBuilderSchema,
  useQueryBuilderSetup,
  useQueryBuilder,
  QueryBuilderInternal
};
//# sourceMappingURL=chunk-DOC6LQGN.mjs.map